import { useState, useEffect, useRef, useCallback } from "react";

// ══════════════════════════════════════════════════════════════
//  INLINE L4U LOGO COMPONENT — matches uploaded brand design
// ══════════════════════════════════════════════════════════════
const L4ULogo = ({ size=72, noTagline=false, dark=false, animated=false }) => {
  const uid = `lg_${size}${dark?"d":""}`;
  const textFill = dark
    ? `url(#${uid}_dk)`
    : `url(#${uid}_lv)`;
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 400 460"
      width={size} height={noTagline ? size : size*1.18}
      fill="none" aria-label="LikeIt4U" role="img"
      style={animated?{filter:`drop-shadow(0 0 12px rgba(123,104,238,0.5))`,animation:"splashGlow 2.5s ease-in-out infinite"}:{}}>
      <defs>
        <linearGradient id={`${uid}_lv`} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%"   stopColor="#9B8FEE"/>
          <stop offset="50%"  stopColor="#7B68EE"/>
          <stop offset="100%" stopColor="#B0A8FF"/>
        </linearGradient>
        <linearGradient id={`${uid}_dk`} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%"   stopColor="#D3D3FF"/>
          <stop offset="50%"  stopColor="#FFFFFF"/>
          <stop offset="100%" stopColor="#D3D3FF"/>
        </linearGradient>
        <linearGradient id={`${uid}_arc`} x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%"   stopColor="#D3D3FF" stopOpacity="0.15"/>
          <stop offset="50%"  stopColor="#9B8FEE" stopOpacity={dark?"0.9":"0.6"}/>
          <stop offset="100%" stopColor="#D3D3FF" stopOpacity="0.15"/>
        </linearGradient>
        <linearGradient id={`${uid}_bag`} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%"   stopColor="#B0A8FF"/>
          <stop offset="100%" stopColor="#7B68EE"/>
        </linearGradient>
      </defs>
      {/* Top arc */}
      <path d="M72 200 A132 132 0 1 1 328 200"
        stroke={`url(#${uid}_arc)`} strokeWidth="2.5" strokeLinecap="round"/>
      {/* Bottom arc */}
      <path d="M120 315 Q200 350 280 315"
        stroke={`url(#${uid}_arc)`} strokeWidth="2" strokeLinecap="round"/>
      {/* L */}
      <text x="48"  y="272" fontFamily="Georgia,'Times New Roman',serif"
        fontSize="198" fontStyle="italic" fill={textFill}>L</text>
      {/* 4 */}
      <text x="148" y="272" fontFamily="Georgia,'Times New Roman',serif"
        fontSize="198" fill={textFill}>4</text>
      {/* U */}
      <text x="252" y="272" fontFamily="Georgia,'Times New Roman',serif"
        fontSize="198" fill={textFill}>U</text>
      {/* Shopping bag */}
      <rect x="102" y="313" width="37" height="33" rx="4" fill={`url(#${uid}_bag)`}/>
      <path d="M112 313 Q112 297 120.5 297 Q129 297 129 313"
        stroke={`url(#${uid}_bag)`} strokeWidth="3.5" strokeLinecap="round"/>
      {/* Main sparkle */}
      <g transform="translate(308,88)">
        <path d="M0-27 Q3-9 0 0 Q-3-9 0-27" fill={dark?"#FFFFFF":"#D3D3FF"}/>
        <path d="M0 27 Q3 9 0 0 Q-3 9 0 27"  fill={dark?"#FFFFFF":"#D3D3FF"}/>
        <path d="M-27 0 Q-9 3 0 0 Q-9-3-27 0" fill={dark?"#FFFFFF":"#D3D3FF"}/>
        <path d="M 27 0 Q 9 3 0 0 Q 9-3 27 0" fill={dark?"#FFFFFF":"#D3D3FF"}/>
      </g>
      {/* Small sparkle */}
      <g transform="translate(337,67)" opacity="0.65">
        <path d="M0-11 Q1.5-3 0 0 Q-1.5-3 0-11" fill={dark?"#FFFFFF":"#D3D3FF"}/>
        <path d="M0 11 Q1.5 3 0 0 Q-1.5 3 0 11"  fill={dark?"#FFFFFF":"#D3D3FF"}/>
        <path d="M-11 0 Q-3 1.5 0 0 Q-3-1.5-11 0" fill={dark?"#FFFFFF":"#D3D3FF"}/>
        <path d="M 11 0 Q 3 1.5 0 0 Q 3-1.5 11 0" fill={dark?"#FFFFFF":"#D3D3FF"}/>
      </g>
      {/* Tagline */}
      {!noTagline && (
        <text x="200" y="403"
          fontFamily="'DM Sans','Helvetica Neue',Arial,sans-serif"
          fontSize="17" fontWeight="400"
          fill={dark ? "rgba(211,211,255,0.8)" : "#9B8FEE"}
          textAnchor="middle" letterSpacing="4.5">
          LIKEIT4U · SOUTH INDIA FASHION
        </text>
      )}
    </svg>
  );
};

// ══════════════════════════════════════════════════════════════
//  DESIGN TOKENS — LAVENDER + SOFT WHITE
// ══════════════════════════════════════════════════════════════
const T = {
  lav:      "#7B68EE",   lavLight: "#D3D3FF",  lavDark:  "#5A4FCF",
  lavPale:  "#F3F2FF",   lavMid:   "#9B8FEE",  lavGlow:  "#B0A8FF",
  softWhite:"#FAFAFE",   offWhite: "#F3F2FF",  parchment:"#EEEEFF",
  ink:      "#1A1A2E",   charcoal: "#2C2C4A",
  muted:    "#7A7A9A",   border:   "#E2E0F5",  borderSoft:"#F0EFF9",
  white:    "#FFFFFF",
  ok:       "#2E7D32",   err:      "#C62828",   warn:     "#E65100",
  teal:     "#5B6FBE",   blue:     "#1565C0",
};

// ══════════════════════════════════════════════════════════════
//  PERSISTENT STORE (simulates backend — survives re-renders)
// ══════════════════════════════════════════════════════════════
const DB = {
  users: [
    { id:"u1", type:"customer", name:"Priya Sharma",   mobile:"9876543210", email:"priya@email.com",   password:"priya123",  city:"Nalgonda", joined:"Apr 2025" },
    { id:"u2", type:"customer", name:"Ravi Kumar",     mobile:"9123456789", email:"ravi@email.com",    password:"ravi123",   city:"Hyderabad", joined:"Mar 2025" },
  ],
  stores: [
    { id:"s1", type:"store", name:"Swarna Kanchi Mall", owner:"Kanchi Reddy", mobile:"9100903659", email:"swarna@store.com", password:"store123",
      city:"Nalgonda", area:"Nagarjuna Colony", state:"Telangana", stateCode:"TG",
      phone:"+91 91009 03659", hours:"10 AM – 10 PM · Daily", since:"1998",
      img:"https://images.unsplash.com/photo-1594938298603-c8148c4b4157?w=500&q=80",
      verified:true, rating:4.7, reviews:1477, gst:"36ABCDE1234F1Z5",
      tags:["Sarees","Kurtas","Bridal","Ethnic"], snippet:"Beautiful kurta sets & sarees." },
    { id:"s2", type:"store", name:"KALKI Hyderabad",    owner:"Kavitha Menon", mobile:"9884520977", email:"kalki@store.com", password:"kalki123",
      city:"Hyderabad", area:"Banjara Hills", state:"Telangana", stateCode:"TG",
      phone:"+91 40 4520 9977", hours:"10:30 AM – 9 PM · Daily", since:"2008",
      img:"https://images.unsplash.com/photo-1583391733956-6c78276477e2?w=500&q=80",
      verified:true, rating:4.9, reviews:2923, gst:"36BCDEF5678G2Z6",
      tags:["Bridal","Designer","Lehenga","Premium"], snippet:"Top-tier bridal boutique." },
  ],
  inventory: [
    { id:"p1", storeId:"s1", category:"Women", sub:"Sarees", name:"Banarasi Silk Saree",
      mrp:8500, price:6800, discount:20, stock:4, fabric:"Pure Banarasi Silk",
      sizes:["Free Size"], colors:["Royal Blue","Deep Red","Emerald"],
      occasions:["Bridal","Wedding","Festive"], customizable:true,
      img:"https://images.unsplash.com/photo-1610030469983-98e550d6193c?w=500&q=80",
      rating:4.8, reviews:32, isBestseller:true, isNew:false, active:true,
      description:"Handwoven pure Banarasi silk saree with intricate zari work border.", addedOn:"Apr 2025" },
    { id:"p2", storeId:"s1", category:"Women", sub:"Kurtis", name:"Cotton Anarkali Kurti",
      mrp:1299, price:899, discount:31, stock:18, fabric:"100% Cotton",
      sizes:["XS","S","M","L","XL","XXL"], colors:["Peach","Teal","Lavender"],
      occasions:["Casual","Office"], customizable:true,
      img:"https://images.unsplash.com/photo-1614897823648-6b27b9ab19b1?w=500&q=80",
      rating:4.5, reviews:87, isBestseller:false, isNew:true, active:true,
      description:"Breathable summer cotton kurti in beautiful floral print.", addedOn:"Apr 2025" },
    { id:"p3", storeId:"s1", category:"Men", sub:"Shirts", name:"Premium Linen Shirt",
      mrp:1499, price:999, discount:33, stock:23, fabric:"60% Linen 40% Cotton",
      sizes:["S(38)","M(40)","L(42)","XL(44)","XXL(46)"], colors:["White","Sky Blue","Beige"],
      occasions:["Office","Formal"], customizable:false,
      img:"https://images.unsplash.com/photo-1598300042247-d088f8ab3a91?w=500&q=80",
      rating:4.6, reviews:104, isBestseller:true, isNew:false, active:true,
      description:"Premium linen formal shirt — cool, anti-microbial, machine washable.", addedOn:"Mar 2025" },
    { id:"p4", storeId:"s2", category:"Women", sub:"Lehenga", name:"Embroidered Bridal Lehenga",
      mrp:22000, price:16500, discount:25, stock:2, fabric:"Georgette with Net",
      sizes:["S","M","L","XL","Custom"], colors:["Bridal Red","Blush Pink"],
      occasions:["Bridal","Wedding","Engagement"], customizable:true,
      img:"https://images.unsplash.com/photo-1576566588028-4147f3842f27?w=500&q=80",
      rating:4.9, reviews:14, isBestseller:false, isNew:false, active:true,
      description:"Heavy zari and mirror work 3-piece bridal lehenga set.", addedOn:"Apr 2025" },
  ],
  otpStore: {},
};

const rk = n => n >= 1000 ? `${(n/1000).toFixed(1)}k` : String(n);
const genOtp = () => String(Math.floor(100000 + Math.random() * 900000));
const genId  = prefix => `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2,7)}`;

// ══════════════════════════════════════════════════════════════
//  GLOBAL STYLES
// ══════════════════════════════════════════════════════════════
const Styles = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,600;1,400&family=DM+Sans:wght@300;400;500;600&display=swap');
    *,*::before,*::after{box-sizing:border-box;margin:0;padding:0;}
    html{scroll-behavior:smooth;}
    body{background:#FAFAFE;color:#1A1A2E;font-family:'DM Sans',sans-serif;overflow-x:hidden;}
    /* Only hide cursor on interactive non-input elements */
    button,a,[role="button"]{cursor:none;}
    input,textarea,select{cursor:text !important;}
    ::-webkit-scrollbar{width:3px;} ::-webkit-scrollbar-thumb{background:#7B68EE;border-radius:2px;}
    .serif{font-family:'Cormorant Garamond',serif;}

    #cursor-dot{width:8px;height:8px;background:#7B68EE;border-radius:50%;position:fixed;pointer-events:none;z-index:99999;transform:translate(-50%,-50%);transition:width .2s,height .2s;}
    #cursor-ring{width:30px;height:30px;border:1.5px solid #D3D3FF;border-radius:50%;position:fixed;pointer-events:none;z-index:99998;transform:translate(-50%,-50%);}
    #scroll-bar{position:fixed;top:0;left:0;height:2px;background:linear-gradient(90deg,#7B68EE,#D3D3FF);z-index:10000;transition:width .1s;}

    @keyframes fadeUp{from{opacity:0;transform:translateY(20px)}to{opacity:1;transform:translateY(0)}}
    @keyframes fadeIn{from{opacity:0}to{opacity:1}}
    @keyframes slideIn{from{opacity:0;transform:translateX(-20px)}to{opacity:1;transform:translateX(0)}}
    @keyframes pulse{0%,100%{opacity:1}50%{opacity:.35}}
    @keyframes ping{0%{transform:scale(1);opacity:.7}75%,100%{transform:scale(2.2);opacity:0}}
    @keyframes float{0%,100%{transform:translateY(0)}50%{transform:translateY(-8px)}}
    @keyframes shimmer{0%{background-position:-200% 0}100%{background-position:200% 0}}
    @keyframes lavFlow{0%{background-position:0% 50%}100%{background-position:200% 50%}}
    @keyframes morphBlob{0%,100%{border-radius:60% 40% 30% 70%/60% 30% 70% 40%}50%{border-radius:30% 60% 70% 40%/50% 60% 30% 60%}}
    @keyframes glowPulse{0%,100%{box-shadow:0 0 15px #D3D3FF40}50%{box-shadow:0 0 35px #7B68EE50}}
    @keyframes spin{from{transform:rotate(0)}to{transform:rotate(360deg)}}
    @keyframes popIn{0%{opacity:0;transform:scale(.85) translateY(10px)}70%{transform:scale(1.03)}100%{opacity:1;transform:scale(1) translateY(0)}}
    @keyframes particleFly{0%{transform:translate(0,0) scale(1);opacity:1}100%{transform:translate(var(--tx),var(--ty)) scale(0);opacity:0}}
    @keyframes shake{0%,100%{transform:translateX(0)}20%,60%{transform:translateX(-6px)}40%,80%{transform:translateX(6px)}}
    @keyframes otpPop{0%{transform:scale(.5);opacity:0}60%{transform:scale(1.1)}100%{transform:scale(1);opacity:1}}

    .fade-up{animation:fadeUp .45s ease both;}
    .fade-in{animation:fadeIn .35s ease both;}
    .pop-in{animation:popIn .4s cubic-bezier(.34,1.56,.64,1) both;}
    .slide-in{animation:slideIn .4s ease both;}

    .lav-text{
      background:linear-gradient(90deg,#5A4FCF,#7B68EE,#D3D3FF,#9B8FEE,#5A4FCF);
      background-size:200% auto;-webkit-background-clip:text;-webkit-text-fill-color:transparent;
      animation:lavFlow 3s linear infinite;
    }

    .input-field{
      width:100%;padding:12px 16px;border:1.5px solid #E2E0F5;border-radius:10px;
      font-family:'DM Sans',sans-serif;font-size:14px;background:#FFFFFF;color:#1A1A2E;
      transition:all .2s;outline:none;
    }
    .input-field:focus{border-color:#7B68EE;box-shadow:0 0 0 3px #D3D3FF40;}
    .input-field.err{border-color:#C62828;box-shadow:0 0 0 3px #C6282820;}
    .input-field.ok{border-color:#2E7D32;box-shadow:0 0 0 3px #2E7D3220;}

    .btn-primary{
      background:linear-gradient(135deg,#7B68EE,#5A4FCF);color:#FFFFFF;
      border:none;border-radius:10px;padding:13px 24px;font-family:'DM Sans',sans-serif;
      font-weight:600;font-size:14px;letter-spacing:.04em;cursor:none;
      transition:all .25s;position:relative;overflow:hidden;
    }
    .btn-primary:hover{transform:translateY(-2px);box-shadow:0 8px 24px #7B68EE50;}
    .btn-primary:active{transform:translateY(0);}
    .btn-primary:disabled{opacity:.55;cursor:not-allowed;transform:none;}

    .btn-outline{
      background:#FFFFFF;color:#7B68EE;border:1.5px solid #7B68EE;
      border-radius:10px;padding:12px 24px;font-family:'DM Sans',sans-serif;
      font-weight:600;font-size:14px;letter-spacing:.04em;cursor:none;
      transition:all .25s;
    }
    .btn-outline:hover{background:#F3F2FF;transform:translateY(-1px);}

    .btn-ghost{background:none;border:none;color:#7B68EE;font-family:'DM Sans',sans-serif;
      font-size:13px;cursor:none;transition:opacity .2s;font-weight:500;}
    .btn-ghost:hover{opacity:.7;}

    .card{background:#FFFFFF;border-radius:16px;border:1px solid #E2E0F5;
      transition:transform .3s,box-shadow .3s;}
    .card:hover{transform:translateY(-3px);box-shadow:0 12px 40px #D3D3FF30;}

    .otp-box{
      width:52px;height:58px;text-align:center;font-size:22px;font-weight:700;
      border:2px solid #E2E0F5;border-radius:12px;background:#FAFAFE;
      font-family:'DM Sans',sans-serif;color:#1A1A2E;outline:none;
      transition:all .2s;
    }
    .otp-box:focus{border-color:#7B68EE;background:#F3F2FF;box-shadow:0 0 0 3px #D3D3FF50;transform:scale(1.06);}
    .otp-box.filled{border-color:#7B68EE;background:#F3F2FF;animation:otpPop .3s ease;}

    .tab{padding:10px 18px;border:none;background:none;cursor:pointer;font-family:'DM Sans',sans-serif;
      font-size:13px;font-weight:500;border-bottom:2.5px solid transparent;color:#7A7A9A;transition:all .2s;}
    .tab.on{color:#7B68EE;border-bottom-color:#7B68EE;}

    .tag{display:inline-block;padding:3px 10px;border-radius:20px;font-size:10.5px;font-weight:600;
      letter-spacing:.04em;text-transform:uppercase;background:#F3F2FF;color:#7B68EE;border:1px solid #E2E0F5;}

    .blob{position:absolute;border-radius:60% 40% 30% 70%/60% 30% 70% 40%;animation:morphBlob 9s ease-in-out infinite;filter:blur(45px);opacity:.1;pointer-events:none;}

    .reveal{opacity:0;transform:translateY(20px);transition:opacity .6s ease,transform .6s ease;}
    .reveal.on{opacity:1;transform:translateY(0);}

    .shake{animation:shake .4s ease;}

    .nav-link{color:#2C2C4A;font-size:13px;font-weight:500;letter-spacing:.05em;text-transform:uppercase;
      cursor:pointer;transition:color .2s;position:relative;}
    .nav-link::after{content:'';position:absolute;bottom:-2px;left:0;width:0;height:1.5px;background:#7B68EE;transition:width .3s;}
    .nav-link:hover::after,.nav-link.active::after{width:100%;}
    .nav-link:hover,.nav-link.active{color:#7B68EE;}

    .switch-track{width:44px;height:24px;border-radius:12px;background:#E2E0F5;position:relative;cursor:pointer;transition:background .25s;}
    .switch-track.on{background:#7B68EE;}
    .switch-thumb{width:18px;height:18px;border-radius:50%;background:#FFFFFF;position:absolute;top:3px;left:3px;transition:transform .25s;box-shadow:0 1px 4px rgba(0,0,0,.15);}
    .switch-track.on .switch-thumb{transform:translateX(20px);}

    @keyframes splashGlow{0%,100%{filter:drop-shadow(0 0 10px rgba(123,104,238,0.4))}50%{filter:drop-shadow(0 0 24px rgba(211,211,255,0.8))}}
    /* ── 3D & Profile Animations ── */
    @keyframes rotate3d{0%{transform:perspective(600px) rotateY(0deg)}100%{transform:perspective(600px) rotateY(360deg)}}
    @keyframes tiltIn{from{opacity:0;transform:perspective(800px) rotateX(20deg) translateY(30px)}to{opacity:1;transform:perspective(800px) rotateX(0) translateY(0)}}
    @keyframes slideUp{from{opacity:0;transform:translateY(40px)}to{opacity:1;transform:translateY(0)}}
    @keyframes scaleIn{from{opacity:0;transform:scale(.7)}to{opacity:1;transform:scale(1)}}
    @keyframes ripple{0%{transform:scale(0);opacity:.6}100%{transform:scale(4);opacity:0}}
    @keyframes countUp{from{opacity:0;transform:scale(.5)}to{opacity:1;transform:scale(1)}}
    @keyframes gradShift{0%{background-position:0% 50%}50%{background-position:100% 50%}100%{background-position:0% 50%}}
    @keyframes avatarFloat{0%,100%{transform:translateY(0) rotate(-1deg)}50%{transform:translateY(-10px) rotate(1deg)}}
    @keyframes badgePop{0%{transform:scale(0) rotate(-20deg)}60%{transform:scale(1.15) rotate(4deg)}100%{transform:scale(1) rotate(0)}}
    @keyframes progressFill{from{width:0}to{width:var(--pct,100%)}}
    @keyframes cardFlip{0%{transform:perspective(600px) rotateY(90deg);opacity:0}100%{transform:perspective(600px) rotateY(0);opacity:1}}
    @keyframes neonPulse{0%,100%{text-shadow:0 0 4px #7B68EE60}50%{text-shadow:0 0 16px #7B68EE,0 0 32px #D3D3FF60}}
    @keyframes walkIn{from{opacity:0;transform:translateX(-50px)}to{opacity:1;transform:translateX(0)}}
    @keyframes notifySlide{from{opacity:0;transform:translateX(60px)}to{opacity:1;transform:translateX(0)}}
    @keyframes heartBeat{0%,100%{transform:scale(1)}14%{transform:scale(1.3)}28%{transform:scale(1)}42%{transform:scale(1.2)}70%{transform:scale(1)}}
    @keyframes borderGlow{0%,100%{border-color:#D3D3FF}50%{border-color:#7B68EE;box-shadow:0 0 20px #7B68EE40}}
    @keyframes rotateRing{0%{transform:rotate(0)}100%{transform:rotate(360deg)}}

    .tilt-in{animation:tiltIn .6s cubic-bezier(.22,1,.36,1) both;}
    .card-flip{animation:cardFlip .5s ease both;}
    .walk-in{animation:walkIn .45s ease both;}
    .notify-slide{animation:notifySlide .4s ease both;}
    .badge-pop{animation:badgePop .5s cubic-bezier(.34,1.56,.64,1) both;}
    .heart-beat{animation:heartBeat 1.2s ease infinite;}
    .neon-text{animation:neonPulse 2.5s ease-in-out infinite;}

    .card3d{transform-style:preserve-3d;transition:transform .4s ease,box-shadow .4s ease;}
    .card3d:hover{transform:perspective(800px) rotateX(-3deg) rotateY(3deg) translateZ(10px);box-shadow:6px 14px 36px #7B68EE20;}

    .skeleton{background:linear-gradient(90deg,#F3F2FF 25%,#EEEEFF 50%,#F3F2FF 75%);background-size:200% 100%;animation:shimmer 1.5s infinite;border-radius:8px;}

    .grad-bg{background:linear-gradient(270deg,#1A1A3E,#2D2060,#3A2070,#2D2060);background-size:400% 400%;animation:gradShift 8s ease infinite;}

    .progress-anim{height:6px;border-radius:3px;overflow:hidden;background:#E2E0F5;}
    .progress-anim-fill{height:100%;border-radius:3px;background:linear-gradient(90deg,#7B68EE,#D3D3FF);animation:progressFill .9s ease forwards;}

    .avatar-ring{position:relative;display:inline-block;}
    .avatar-ring::before{content:'';position:absolute;inset:-4px;border-radius:50%;background:conic-gradient(#7B68EE,#D3D3FF,#5A4FCF,#D3D3FF,#7B68EE);animation:rotateRing 3s linear infinite;z-index:0;}
    .avatar-ring-inner{position:relative;z-index:1;}

    @media(max-width:768px){
      .hide-mob{display:none!important;}
      #cursor-dot,#cursor-ring{display:none;}
      .card3d:hover{transform:none;}
    }
  `}</style>
);

// ══════════════════════════════════════════════════════════════
//  MICRO COMPONENTS
// ══════════════════════════════════════════════════════════════
const CustomCursor = () => {
  const dot=useRef(null),ring=useRef(null),pos=useRef({x:0,y:0}),rp=useRef({x:0,y:0});
  useEffect(()=>{
    const mv=e=>{pos.current={x:e.clientX,y:e.clientY};if(dot.current){dot.current.style.left=e.clientX+"px";dot.current.style.top=e.clientY+"px";}};
    let raf;const lerp=(a,b,t)=>a+(b-a)*t;
    const loop=()=>{rp.current.x=lerp(rp.current.x,pos.current.x,.13);rp.current.y=lerp(rp.current.y,pos.current.y,.13);if(ring.current){ring.current.style.left=rp.current.x+"px";ring.current.style.top=rp.current.y+"px";}raf=requestAnimationFrame(loop);};
    loop();document.addEventListener("mousemove",mv);
    return()=>{document.removeEventListener("mousemove",mv);cancelAnimationFrame(raf);};
  },[]);
  return <><div id="cursor-dot" ref={dot}/><div id="cursor-ring" ref={ring}/></>;
};

const ScrollBar = () => {
  const [p,setP]=useState(0);
  useEffect(()=>{const h=()=>{const s=document.documentElement;setP((s.scrollTop/(s.scrollHeight-s.clientHeight))*100||0);};window.addEventListener("scroll",h);return()=>window.removeEventListener("scroll",h);},[]);
  return <div id="scroll-bar" style={{ width:p+"%" }}/>;
};

const Stars = ({ rating, count, size=13 }) => (
  <div style={{ display:"flex",alignItems:"center",gap:3 }}>
    {[1,2,3,4,5].map(s=><span key={s} style={{ fontSize:size,color:s<=Math.round(rating)?T.lav:"#DDD" }}>★</span>)}
    {count!=null&&<span style={{ fontSize:11,color:T.muted,marginLeft:3 }}>{rating} ({rk(count)})</span>}
  </div>
);

const Spinner = () => <div style={{ width:18,height:18,border:`2.5px solid #D3D3FF`,borderTopColor:T.lav,borderRadius:"50%",animation:"spin .7s linear infinite",display:"inline-block" }}/>;

const Alert = ({ type, msg, onClose }) => {
  const colors = { ok:["#E8F5E9","#2E7D32"], err:["#FFEBEE","#C62828"], info:["#EDE7F6","#7B68EE"], warn:["#FFF3E0","#E65100"] };
  const [bg,clr] = colors[type]||colors.info;
  return (
    <div className="pop-in" style={{ background:bg,border:`1px solid ${clr}30`,borderRadius:10,padding:"11px 14px",display:"flex",gap:9,alignItems:"flex-start",marginBottom:12 }}>
      <span style={{ fontSize:15 }}>{type==="ok"?"✅":type==="err"?"❌":type==="warn"?"⚠️":"ℹ️"}</span>
      <p style={{ fontSize:13,color:clr,flex:1,lineHeight:1.45 }}>{msg}</p>
      {onClose&&<button onClick={onClose} style={{ background:"none",border:"none",cursor:"none",color:clr,fontSize:16,lineHeight:1 }}>×</button>}
    </div>
  );
};

// Toggle Switch
const Toggle = ({ on, onChange, label }) => (
  <label style={{ display:"flex",alignItems:"center",gap:10,cursor:"pointer" }} onClick={()=>onChange(!on)}>
    <div className={`switch-track ${on?"on":""}`}><div className="switch-thumb"/></div>
    {label&&<span style={{ fontSize:13,color:T.muted }}>{label}</span>}
  </label>
);

// ══════════════════════════════════════════════════════════════
//  OTP INPUT COMPONENT
// ══════════════════════════════════════════════════════════════
const OtpInput = ({ value, onChange, length=6 }) => {
  const refs = useRef([]);
  const vals = value.split("").concat(Array(length).fill("")).slice(0,length);

  const handleChange = (i, e) => {
    const raw = e.target.value.replace(/\D/g,"");
    if(!raw) return;
    const digit = raw[raw.length-1];
    const next = [...vals];
    next[i] = digit;
    onChange(next.join(""));
    if(i < length-1) setTimeout(()=>refs.current[i+1]?.focus(), 0);
  };

  const handleKey = (i, e) => {
    if(e.key==="Backspace") {
      e.preventDefault();
      const next = [...vals];
      if(next[i]) {
        next[i] = "";
        onChange(next.join(""));
      } else if(i > 0) {
        next[i-1] = "";
        onChange(next.join(""));
        refs.current[i-1]?.focus();
      }
    } else if(e.key==="ArrowLeft" && i>0) {
      refs.current[i-1]?.focus();
    } else if(e.key==="ArrowRight" && i<length-1) {
      refs.current[i+1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").replace(/\D/g,"").slice(0,length);
    if(pasted) {
      onChange(pasted.padEnd(length,"").slice(0,length).replace(/ /g,""));
      const focusIdx = Math.min(pasted.length, length-1);
      setTimeout(()=>refs.current[focusIdx]?.focus(), 0);
    }
  };

  return (
    <div style={{ display:"flex",gap:8,justifyContent:"center" }}>
      {Array(length).fill(0).map((_,i)=>(
        <input
          key={i}
          ref={el=>refs.current[i]=el}
          className={`otp-box ${vals[i]?"filled":""}`}
          type="tel"
          inputMode="numeric"
          pattern="[0-9]*"
          autoComplete="one-time-code"
          maxLength={1}
          value={vals[i]||""}
          onChange={e=>handleChange(i,e)}
          onKeyDown={e=>handleKey(i,e)}
          onPaste={handlePaste}
          onFocus={e=>e.target.select()}
          style={{ caretColor:T.lav }}
        />
      ))}
    </div>
  );
};

// ══════════════════════════════════════════════════════════════
//  AUTH PAGE  (Login + Signup — Customer & Store)
// ══════════════════════════════════════════════════════════════
const AuthPage = ({ onAuth }) => {
  const [mode,setMode]   = useState("login");   // login | signup
  const [role,setRole]   = useState("customer"); // customer | store
  const [method,setMethod] = useState("mobile"); // mobile | userid
  const [step,setStep]   = useState(1);          // 1=form 2=otp 3=done

  // Form fields
  const [mobile,setMobile]   = useState("");
  const [userId,setUserId]   = useState("");
  const [password,setPassword] = useState("");
  const [name,setName]       = useState("");
  const [email,setEmail]     = useState("");
  const [storeName,setStoreName] = useState("");
  const [city,setCity]       = useState("");
  const [otp,setOtp]         = useState("");

  const [loading,setLoading] = useState(false);
  const [err,setErr]         = useState("");
  const [info,setInfo]       = useState("");
  const [showPass,setShowPass] = useState(false);
  const [sentOtp,setSentOtp] = useState("");

  const clear = () => { setErr(""); setInfo(""); };

  // Send OTP
  const handleSendOtp = () => {
    clear();
    if(!/^\d{10}$/.test(mobile)){ setErr("Enter a valid 10-digit mobile number."); return; }
    if(mode==="login") {
      const allUsers = [...DB.users, ...DB.stores];
      const found = allUsers.find(u=>u.mobile===mobile);
      if(!found){ setErr("No account found with this mobile number."); return; }
      if(role==="store"&&found.type!=="store"){ setErr("This number is not registered as a store. Try customer login."); return; }
      if(role==="customer"&&found.type==="store"){ setErr("This is a store account. Use store login."); return; }
    }
    setLoading(true);
    setTimeout(()=>{
      const code = genOtp();
      setSentOtp(code);
      DB.otpStore[mobile] = code;
      setInfo(`OTP sent to +91 ${mobile}. Demo OTP: ${code}`);
      setLoading(false);
      setStep(2);
    },1200);
  };

  // Verify OTP
  const handleVerifyOtp = () => {
    clear();
    if(otp.length!==6){ setErr("Enter all 6 digits."); return; }
    if(otp !== DB.otpStore[mobile]){ setErr("Incorrect OTP. Please try again."); return; }
    setLoading(true);
    setTimeout(()=>{
      if(mode==="login") {
        const allUsers=[...DB.users,...DB.stores];
        const user = allUsers.find(u=>u.mobile===mobile);
        setLoading(false);
        onAuth(user);
      } else {
        // Signup via OTP — create account
        if(!name.trim()){ setErr("Name is required."); setLoading(false); return; }
        const newUser = role==="store"
          ? { id:genId("s"), type:"store", name:storeName||name, owner:name, mobile, email, password:"",
              city, area:"", state:"", stateCode:"", phone:`+91 ${mobile}`,
              hours:"10 AM – 9 PM · Daily", since:new Date().getFullYear().toString(),
              img:"https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=500&q=80",
              verified:false, rating:0, reviews:0, gst:"", tags:[], snippet:"New store on L4U.",
              joined: new Date().toLocaleDateString("en-IN",{month:"short",year:"numeric"}) }
          : { id:genId("u"), type:"customer", name, mobile, email, password:"", city,
              joined: new Date().toLocaleDateString("en-IN",{month:"short",year:"numeric"}) };
        if(role==="store") DB.stores.push(newUser); else DB.users.push(newUser);
        setLoading(false);
        onAuth(newUser);
      }
    },1000);
  };

  // Login with UserID + Password
  const handlePasswordLogin = () => {
    clear();
    if(!userId.trim()){ setErr("Enter your User ID or email."); return; }
    if(!password){ setErr("Enter your password."); return; }
    setLoading(true);
    setTimeout(()=>{
      const pool = role==="store" ? DB.stores : DB.users;
      const user = pool.find(u=>(u.email===userId||u.mobile===userId)&&u.password===password);
      setLoading(false);
      if(!user){ setErr("Invalid credentials. Please check and try again."); return; }
      onAuth(user);
    },900);
  };

  // Signup with password
  const handleSignupPassword = () => {
    clear();
    if(!name.trim()){ setErr("Full name is required."); return; }
    if(!userId.trim()){ setErr("Email or mobile is required."); return; }
    if(password.length<6){ setErr("Password must be at least 6 characters."); return; }
    const isMob = /^\d{10}$/.test(userId);
    const isEmail = userId.includes("@");
    if(!isMob&&!isEmail){ setErr("Enter a valid email or 10-digit mobile."); return; }
    const pool=[...DB.users,...DB.stores];
    if(pool.find(u=>u.email===userId||u.mobile===userId)){ setErr("Account already exists. Please login."); return; }
    setLoading(true);
    setTimeout(()=>{
      const newUser = role==="store"
        ? { id:genId("s"), type:"store", name:storeName||name, owner:name,
            mobile:isMob?userId:"", email:isEmail?userId:"", password,
            city, area:"", state:"", stateCode:"", phone:isMob?`+91 ${userId}`:"",
            hours:"10 AM – 9 PM · Daily", since:new Date().getFullYear().toString(),
            img:"https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=500&q=80",
            verified:false, rating:0, reviews:0, gst:"", tags:[], snippet:"New store on L4U.",
            joined: new Date().toLocaleDateString("en-IN",{month:"short",year:"numeric"}) }
        : { id:genId("u"), type:"customer", name, mobile:isMob?userId:"", email:isEmail?userId:"", password, city,
            joined: new Date().toLocaleDateString("en-IN",{month:"short",year:"numeric"}) };
      if(role==="store") DB.stores.push(newUser); else DB.users.push(newUser);
      setLoading(false);
      onAuth(newUser);
    },1000);
  };

  const isMobileMethod = method==="mobile";

  return (
    <div style={{ minHeight:"100vh",background:"#FAFAFE",display:"flex",alignItems:"stretch" }}>
      {/* Left panel — decorative */}
      <div className="hide-mob" style={{ width:"45%",background:`linear-gradient(150deg,#1A1A3E 0%,#2D2060 55%,#3A2070 100%)`,position:"relative",overflow:"hidden",display:"flex",flexDirection:"column",justifyContent:"center",padding:"56px 48px" }}>
        <div className="blob" style={{ width:380,height:380,background:"#7B68EE",top:-100,right:-80 }}/>
        <div className="blob" style={{ width:260,height:260,background:"#D3D3FF",bottom:-60,left:40,animationDelay:"4s" }}/>
        {/* Particle dots */}
        {Array(20).fill(0).map((_,i)=>(
          <div key={i} style={{ position:"absolute",width:3+Math.random()*3,height:3+Math.random()*3,borderRadius:"50%",background:`rgba(211,211,255,${.15+Math.random()*.3})`,left:`${Math.random()*100}%`,top:`${Math.random()*100}%`,animation:`float ${3+Math.random()*3}s ease-in-out ${Math.random()*2}s infinite` }}/>
        ))}
        <div style={{ position:"relative",zIndex:1 }}>
          <div className="serif" style={{ fontSize:44,color:"#FFFFFF",fontWeight:400,lineHeight:1.1,marginBottom:16 }}>
            Shop Local.<br/><em className="lav-text">Go Digital.</em>
          </div>
          <p style={{ color:"#B0B0D8",fontSize:15,lineHeight:1.7,marginBottom:32 }}>
            L4U brings every local fashion shop across South India online. Browse real inventories, chat with owners, and support your neighbourhood stores.
          </p>
          {/* State pills */}
          <div style={{ display:"flex",gap:8,flexWrap:"wrap" }}>
            {[["🌶️","Telangana"],["🦚","Andhra Pradesh"],["🏛️","Tamil Nadu"],["🦁","Karnataka"],["🌴","Kerala"]].map(([e,s])=>(
              <div key={s} style={{ display:"flex",alignItems:"center",gap:6,background:"rgba(211,211,255,.12)",border:"1px solid rgba(211,211,255,.2)",borderRadius:20,padding:"5px 12px" }}>
                <span style={{ fontSize:14 }}>{e}</span>
                <span style={{ fontSize:11.5,color:"#D3D3FF",fontWeight:500 }}>{s}</span>
              </div>
            ))}
          </div>
          {/* Testimonials */}
          <div style={{ marginTop:36,background:"rgba(255,255,255,.06)",backdropFilter:"blur(10px)",borderRadius:14,padding:"16px 18px",border:"1px solid rgba(255,255,255,.1)" }}>
            <p style={{ fontSize:13.5,color:"#D3D3FF",lineHeight:1.6,fontStyle:"italic",marginBottom:10 }}>"Found the most beautiful Banarasi saree for my wedding. Shop owner helped via chat!"</p>
            <div style={{ display:"flex",gap:8,alignItems:"center" }}>
              <div style={{ width:28,height:28,borderRadius:"50%",background:"linear-gradient(135deg,#7B68EE,#5A4FCF)",display:"flex",alignItems:"center",justifyContent:"center",color:"#FFF",fontSize:11,fontWeight:600 }}>A</div>
              <div><div style={{ fontSize:12,fontWeight:600,color:"#FFFFFF" }}>Ananya K.</div><div style={{ fontSize:10,color:"#9090C0" }}>Nalgonda, Telangana</div></div>
            </div>
          </div>
        </div>
      </div>

      {/* Right panel — auth form */}
      <div style={{ flex:1,display:"flex",flexDirection:"column",justifyContent:"center",alignItems:"center",padding:"40px 24px",overflowY:"auto" }}>
        <div style={{ width:"100%",maxWidth:420 }}>
          {/* Logo */}
          <div style={{ textAlign:"center",marginBottom:28 }}>
            <L4ULogo size={96} />
          </div>

          {/* Role selector */}
          <div style={{ display:"flex",gap:8,marginBottom:20,background:T.offWhite,borderRadius:12,padding:4 }}>
            {[["customer","🛍️ Customer"],["store","🏪 Store Owner"]].map(([r,l])=>(
              <button key={r} onClick={()=>{setRole(r);setErr("");setStep(1);setOtp("");}} style={{ flex:1,padding:"9px 12px",borderRadius:9,border:"none",fontFamily:"'DM Sans',sans-serif",fontSize:13,fontWeight:600,cursor:"none",transition:"all .2s",background:role===r?"#FFFFFF":T.offWhite,color:role===r?T.lav:T.muted,boxShadow:role===r?"0 2px 10px #D3D3FF30":"none" }}>{l}</button>
            ))}
          </div>

          {/* Mode tabs */}
          <div style={{ display:"flex",borderBottom:`1px solid ${T.border}`,marginBottom:22 }}>
            {[["login","Sign In"],["signup","Create Account"]].map(([m,l])=>(
              <button key={m} className={`tab ${mode===m?"on":""}`} onClick={()=>{setMode(m);setErr("");setStep(1);setOtp("");}}>
                {l}
              </button>
            ))}
          </div>

          <div className="fade-up">
            {/* ALERTS */}
            {err&&<Alert type="err" msg={err} onClose={()=>setErr("")}/>}
            {info&&<Alert type="info" msg={info} onClose={()=>setInfo("")}/>}

            {/* ── STEP 1: Form ── */}
            {step===1 && (
              <div style={{ display:"flex",flexDirection:"column",gap:14 }}>
                {/* Signup extra fields */}
                {mode==="signup" && (
                  <>
                    <div>
                      <label style={{ fontSize:12,fontWeight:600,color:T.muted,display:"block",marginBottom:5,letterSpacing:".04em",textTransform:"uppercase" }}>
                        {role==="store"?"Owner Name":"Your Name"} *
                      </label>
                      <input className="input-field" placeholder="Full name" value={name} onChange={e=>setName(e.target.value)}/>
                    </div>
                    {role==="store"&&(
                      <div>
                        <label style={{ fontSize:12,fontWeight:600,color:T.muted,display:"block",marginBottom:5,letterSpacing:".04em",textTransform:"uppercase" }}>Store Name *</label>
                        <input className="input-field" placeholder="Your shop name" value={storeName} onChange={e=>setStoreName(e.target.value)}/>
                      </div>
                    )}
                    <div>
                      <label style={{ fontSize:12,fontWeight:600,color:T.muted,display:"block",marginBottom:5,letterSpacing:".04em",textTransform:"uppercase" }}>City *</label>
                      <input className="input-field" placeholder="Your city" value={city} onChange={e=>setCity(e.target.value)}/>
                    </div>
                  </>
                )}

                {/* Login method toggle */}
                <div style={{ display:"flex",gap:8,background:T.offWhite,borderRadius:10,padding:3 }}>
                  {[["mobile","📱 Mobile OTP"],["userid","🔑 Email / Password"]].map(([m,l])=>(
                    <button key={m} onClick={()=>{setMethod(m);setErr("");}} style={{ flex:1,padding:"7px",borderRadius:8,border:"none",fontFamily:"'DM Sans',sans-serif",fontSize:12.5,fontWeight:500,cursor:"none",transition:"all .2s",background:method===m?"#FFFFFF":T.offWhite,color:method===m?T.lav:T.muted,boxShadow:method===m?"0 1px 6px #D3D3FF30":"none" }}>{l}</button>
                  ))}
                </div>

                {/* Mobile OTP flow */}
                {isMobileMethod ? (
                  <>
                    <div>
                      <label style={{ fontSize:12,fontWeight:600,color:T.muted,display:"block",marginBottom:5,letterSpacing:".04em",textTransform:"uppercase" }}>Mobile Number *</label>
                      <div style={{ display:"flex",gap:8 }}>
                        <div style={{ background:T.offWhite,border:`1.5px solid ${T.border}`,borderRadius:10,padding:"12px 14px",fontSize:14,color:T.muted,flexShrink:0 }}>+91</div>
                        <input className="input-field" placeholder="10-digit mobile" maxLength={10} value={mobile} onChange={e=>setMobile(e.target.value.replace(/\D/g,"").slice(0,10))} style={{ flex:1 }}/>
                      </div>
                    </div>
                    {mode==="signup"&&<div>
                      <label style={{ fontSize:12,fontWeight:600,color:T.muted,display:"block",marginBottom:5,letterSpacing:".04em",textTransform:"uppercase" }}>Email (optional)</label>
                      <input className="input-field" placeholder="yourname@email.com" value={email} onChange={e=>setEmail(e.target.value)}/>
                    </div>}
                    <button className="btn-primary" onClick={handleSendOtp} disabled={loading||mobile.length!==10} style={{ width:"100%" }}>
                      {loading?<Spinner/>:"Send OTP →"}
                    </button>
                  </>
                ) : (
                  <>
                    <div>
                      <label style={{ fontSize:12,fontWeight:600,color:T.muted,display:"block",marginBottom:5,letterSpacing:".04em",textTransform:"uppercase" }}>Email / Mobile</label>
                      <input className="input-field" placeholder="email@example.com or 9876543210" value={userId} onChange={e=>setUserId(e.target.value)}/>
                    </div>
                    <div>
                      <div style={{ display:"flex",justifyContent:"space-between",marginBottom:5 }}>
                        <label style={{ fontSize:12,fontWeight:600,color:T.muted,letterSpacing:".04em",textTransform:"uppercase" }}>Password</label>
                        {mode==="login"&&<button className="btn-ghost" style={{ fontSize:12 }} onClick={()=>setInfo("Password reset via OTP — switch to Mobile OTP tab.")}>Forgot?</button>}
                      </div>
                      <div style={{ position:"relative" }}>
                        <input className="input-field" type={showPass?"text":"password"} placeholder="Enter password" value={password} onChange={e=>setPassword(e.target.value)} style={{ paddingRight:44 }} onKeyDown={e=>e.key==="Enter"&&(mode==="login"?handlePasswordLogin():handleSignupPassword())}/>
                        <button onClick={()=>setShowPass(!showPass)} style={{ position:"absolute",right:12,top:"50%",transform:"translateY(-50%)",background:"none",border:"none",cursor:"none",fontSize:16,color:T.muted }}>
                          {showPass?"🙈":"👁️"}
                        </button>
                      </div>
                    </div>
                    <button className="btn-primary" onClick={mode==="login"?handlePasswordLogin:handleSignupPassword} disabled={loading} style={{ width:"100%" }}>
                      {loading?<Spinner/>:mode==="login"?"Sign In →":"Create Account →"}
                    </button>
                  </>
                )}

                <p style={{ textAlign:"center",fontSize:12.5,color:T.muted }}>
                  {mode==="login"?"Don't have an account?":"Already have an account?"}
                  {" "}
                  <button className="btn-ghost" onClick={()=>{setMode(mode==="login"?"signup":"login");setErr("");setStep(1);}}>
                    {mode==="login"?"Sign Up Free":"Sign In"}
                  </button>
                </p>

                {/* Demo credentials */}
                <div style={{ background:T.lavPale,border:`1px solid ${T.border}`,borderRadius:10,padding:"12px 14px" }}>
                  <p style={{ fontSize:11.5,fontWeight:600,color:T.lav,marginBottom:6 }}>🔑 Demo Credentials</p>
                  <p style={{ fontSize:11.5,color:T.muted,lineHeight:1.6 }}>
                    <strong>Customer:</strong> 9876543210 (OTP) or priya@email.com / priya123<br/>
                    <strong>Store:</strong> 9100903659 (OTP) or swarna@store.com / store123
                  </p>
                </div>
              </div>
            )}

            {/* ── STEP 2: OTP Verification ── */}
            {step===2 && (
              <div className="fade-in" style={{ display:"flex",flexDirection:"column",gap:18,alignItems:"center" }}>
                <div style={{ textAlign:"center" }}>
                  <div style={{ fontSize:48,marginBottom:10,animation:"float 2s ease-in-out infinite" }}>📱</div>
                  <h2 className="serif" style={{ fontSize:26,fontWeight:400,marginBottom:6 }}>Verify Your <em style={{ color:T.lav }}>Number</em></h2>
                  <p style={{ fontSize:13.5,color:T.muted }}>OTP sent to <strong style={{ color:T.ink }}>+91 {mobile}</strong></p>
                </div>
                <OtpInput value={otp} onChange={setOtp} length={6}/>
                <button className="btn-primary" onClick={handleVerifyOtp} disabled={loading||otp.length!==6} style={{ width:"100%" }}>
                  {loading?<Spinner/>:"Verify & Continue →"}
                </button>
                <div style={{ display:"flex",gap:16,alignItems:"center" }}>
                  <button className="btn-ghost" onClick={()=>{setStep(1);setOtp("");clear();}}>← Change Number</button>
                  <button className="btn-ghost" onClick={handleSendOtp}>Resend OTP</button>
                </div>
                {sentOtp&&<div style={{ background:T.lavPale,border:`1px solid ${T.border}`,borderRadius:8,padding:"8px 14px",width:"100%",textAlign:"center" }}>
                  <span style={{ fontSize:12,color:T.lav }}>🔑 Demo OTP: <strong>{sentOtp}</strong></span>
                </div>}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ══════════════════════════════════════════════════════════════
//  TOP BAR
// ══════════════════════════════════════════════════════════════
const TopBar = ({ user, page, setPage, onLogout, userLoc, locStatus, onRequestLocation }) => {
  const [sc,setSc]=useState(false);
  const [menu,setMenu]=useState(false);
  useEffect(()=>{const h=()=>setSc(window.scrollY>10);window.addEventListener("scroll",h);return()=>window.removeEventListener("scroll",h);},[]);
  const isStore = user?.type==="store";
  return (
    <header style={{ position:"sticky",top:0,zIndex:200,background:sc?"rgba(250,250,254,.97)":"#FAFAFE",backdropFilter:"blur(16px)",borderBottom:`1px solid ${T.border}`,padding:"0 24px",transition:"all .3s",boxShadow:sc?"0 2px 20px #D3D3FF25":"none" }}>
      <div style={{ maxWidth:1240,margin:"0 auto",display:"flex",alignItems:"center",height:60,gap:24 }}>
        <div onClick={()=>setPage("home")} style={{ cursor:"pointer",flexShrink:0,display:"flex",alignItems:"center" }}>
          <L4ULogo size={48} noTagline dark={false}/>
        </div>
        <nav className="hide-mob" style={{ display:"flex",gap:20,flex:1,justifyContent:"center" }}>
          {isStore ? (
            [["store-home","Dashboard"],["store-inventory","My Inventory"],["store-add","+ Add Product"],["store-orders","Orders"]].map(([id,l])=>(
              <span key={id} className={`nav-link ${page===id?"active":""}`} onClick={()=>setPage(id)}>{l}</span>
            ))
          ) : (
            [["home","Home"],["explore","Explore States"],["discover","Discover Shops"],["chat","Chat"]].map(([id,l])=>(
              <span key={id} className={`nav-link ${page===id?"active":""}`} onClick={()=>setPage(id)}>{l}</span>
            ))
          )}
        </nav>
        <div style={{ marginLeft:"auto",display:"flex",gap:10,alignItems:"center",flexShrink:0 }}>
          {/* Location pill — customer only */}
          {!isStore && (
            <div className="hide-mob">
              {locStatus==="granted" && userLoc ? (
                <div style={{ display:"flex",alignItems:"center",gap:6,background:"#E8F5E9",border:"1px solid #A5D6A7",borderRadius:20,padding:"4px 12px" }}>
                  <span style={{ width:7,height:7,borderRadius:"50%",background:T.ok,animation:"pulse 1.5s infinite",display:"inline-block",flexShrink:0 }}/>
                  <span style={{ fontSize:11.5,fontWeight:500,color:T.ok }}>📍 Location On</span>
                </div>
              ) : locStatus==="asking" ? (
                <div style={{ display:"flex",alignItems:"center",gap:6,background:T.lavPale,border:`1px solid ${T.border}`,borderRadius:20,padding:"4px 12px" }}>
                  <Spinner/>
                  <span style={{ fontSize:11.5,color:T.lav }}>Detecting…</span>
                </div>
              ) : (
                <button onClick={onRequestLocation} style={{ display:"flex",alignItems:"center",gap:6,background:T.lavPale,border:`1px solid ${T.border}`,borderRadius:20,padding:"5px 12px",fontSize:11.5,color:T.lav,fontWeight:500 }}>
                  📍 Enable Location
                </button>
              )}
            </div>
          )}
          {/* User menu */}
          <div style={{ position:"relative" }}>
            <div onClick={()=>setMenu(!menu)} style={{ display:"flex",alignItems:"center",gap:8,background:T.offWhite,border:`1px solid ${T.border}`,borderRadius:20,padding:"5px 12px 5px 5px",cursor:"pointer",transition:"all .2s" }} onMouseEnter={e=>e.currentTarget.style.borderColor=T.lav} onMouseLeave={e=>e.currentTarget.style.borderColor=T.border}>
              <div style={{ width:26,height:26,borderRadius:"50%",background:`linear-gradient(135deg,${T.lav},${T.lavDark})`,display:"flex",alignItems:"center",justifyContent:"center",color:T.white,fontSize:11,fontWeight:700 }}>
                {user?.name?.[0]?.toUpperCase()||"U"}
              </div>
              <span style={{ fontSize:12.5,fontWeight:500,color:T.ink,maxWidth:100,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap" }}>{user?.name?.split(" ")[0]}</span>
              <span style={{ fontSize:10,color:T.muted }}>▾</span>
            </div>
            {menu&&(
              <div className="pop-in" style={{ position:"absolute",top:"calc(100% + 8px)",right:0,background:T.white,border:`1px solid ${T.border}`,borderRadius:12,padding:"8px",minWidth:180,boxShadow:`0 8px 32px ${T.lavLight}30`,zIndex:100 }}>
                <div style={{ padding:"8px 12px",borderBottom:`1px solid ${T.borderSoft}`,marginBottom:4 }}>
                  <div style={{ fontSize:13,fontWeight:600 }}>{user?.name}</div>
                  <div style={{ fontSize:11,color:T.muted }}>{user?.email||user?.mobile}</div>
                  <div style={{ marginTop:4 }}><span className="tag">{isStore?"🏪 Store":"🛍️ Customer"}</span></div>
                </div>
                {!isStore&&<button onClick={()=>{setMenu(false);setPage("profile");}} style={{ display:"block",width:"100%",textAlign:"left",padding:"8px 12px",background:"none",border:"none",cursor:"pointer",fontSize:13,color:T.ink,borderRadius:8 }} onMouseEnter={e=>e.currentTarget.style.background=T.offWhite} onMouseLeave={e=>e.currentTarget.style.background="none"}>👤 My Profile</button>}
                {isStore&&<button onClick={()=>{setMenu(false);setPage("store-inventory");}} style={{ display:"block",width:"100%",textAlign:"left",padding:"8px 12px",background:"none",border:"none",cursor:"pointer",fontSize:13,color:T.ink,borderRadius:8 }} onMouseEnter={e=>e.currentTarget.style.background=T.offWhite} onMouseLeave={e=>e.currentTarget.style.background="none"}>📦 My Inventory</button>}
                <button onClick={()=>{setMenu(false);onLogout();}} style={{ display:"block",width:"100%",textAlign:"left",padding:"8px 12px",background:"none",border:"none",cursor:"pointer",fontSize:13,color:T.err,borderRadius:8 }} onMouseEnter={e=>e.currentTarget.style.background="#FFEBEE"} onMouseLeave={e=>e.currentTarget.style.background="none"}>🚪 Sign Out</button>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

// ══════════════════════════════════════════════════════════════
//  STORE DASHBOARD
// ══════════════════════════════════════════════════════════════
const StoreDashboard = ({ user, setPage }) => {
  const myProducts = DB.inventory.filter(p=>p.storeId===user.id);
  const activeCount = myProducts.filter(p=>p.active).length;

  useEffect(()=>{
    const els=document.querySelectorAll(".reveal");
    const io=new IntersectionObserver(e=>e.forEach(en=>{if(en.isIntersecting)en.target.classList.add("on");}),{threshold:.1});
    els.forEach(el=>io.observe(el));return()=>io.disconnect();
  },[]);

  return (
    <div style={{ maxWidth:1100,margin:"0 auto",padding:"28px 24px" }}>
      {/* Welcome */}
      <div className="fade-up" style={{ background:`linear-gradient(135deg,#2D2060,#1A1A3E)`,borderRadius:20,padding:"28px 32px",marginBottom:28,position:"relative",overflow:"hidden" }}>
        <div className="blob" style={{ width:300,height:300,background:"#7B68EE",top:-100,right:-60,opacity:.15 }}/>
        <div style={{ position:"relative",zIndex:1 }}>
          <div style={{ display:"flex",justifyContent:"space-between",alignItems:"flex-start",flexWrap:"wrap",gap:16 }}>
            <div>
              <div style={{ fontSize:11,letterSpacing:".15em",textTransform:"uppercase",color:"#D3D3FF",marginBottom:16 }}>Store Portal</div>
            <div style={{ marginBottom:20 }}>
              <L4ULogo size={110} dark noTagline/>
            </div>
              <h1 className="serif" style={{ fontSize:32,color:"#FFFFFF",fontWeight:400,marginBottom:6 }}>
                Welcome back, <em className="lav-text">{user.name}</em>
              </h1>
              <p style={{ color:"#B0B0D8",fontSize:14 }}>📍 {user.city||"Location not set"} · {user.verified?"✅ Verified Store":"⏳ Pending Verification"}</p>
            </div>
            <div style={{ display:"flex",gap:10 }}>
              <button className="btn-primary" onClick={()=>setPage("store-add")} style={{ fontSize:13,padding:"10px 20px" }}>+ Add Product</button>
              <button className="btn-outline" onClick={()=>setPage("store-inventory")} style={{ fontSize:13,padding:"10px 20px",background:"rgba(255,255,255,.1)",borderColor:"rgba(255,255,255,.3)",color:"#FFFFFF" }}>My Inventory</button>
            </div>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div style={{ display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(170px,1fr))",gap:14,marginBottom:28 }}>
        {[["📦",myProducts.length,"Total Products"],["✅",activeCount,"Active Listings"],["💬","24","Chat Inquiries"],["👁️","1,248","Profile Views"],["📈","₹1.2L","Est. Revenue"]].map(([ic,v,l])=>(
          <div key={l} className="reveal card" style={{ padding:"18px 16px" }}>
            <div style={{ display:"flex",justifyContent:"space-between",alignItems:"flex-start" }}>
              <div>
                <div style={{ fontSize:11.5,color:T.muted,marginBottom:5 }}>{l}</div>
                <div className="serif" style={{ fontSize:26,fontWeight:600,color:T.lav }}>{v}</div>
              </div>
              <span style={{ fontSize:24,animation:"float 3s ease-in-out infinite" }}>{ic}</span>
            </div>
          </div>
        ))}
      </div>

      {/* Recent Products */}
      <div className="reveal">
        <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16 }}>
          <h2 className="serif" style={{ fontSize:26,fontWeight:400 }}>Recent <em style={{ color:T.lav }}>Inventory</em></h2>
          <button className="btn-outline" onClick={()=>setPage("store-inventory")} style={{ fontSize:12,padding:"7px 16px" }}>View All</button>
        </div>
        {myProducts.length===0 ? (
          <div style={{ textAlign:"center",padding:"48px 24px",background:T.white,borderRadius:16,border:`1px solid ${T.border}` }}>
            <div style={{ fontSize:48,marginBottom:12,animation:"float 2s ease-in-out infinite" }}>📦</div>
            <h3 style={{ fontSize:16,fontWeight:600,marginBottom:8 }}>No products yet</h3>
            <p style={{ color:T.muted,fontSize:13,marginBottom:16 }}>Add your first product to start selling to customers across South India.</p>
            <button className="btn-primary" onClick={()=>setPage("store-add")}>+ Add Your First Product</button>
          </div>
        ) : (
          <div style={{ display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(240px,1fr))",gap:14 }}>
            {myProducts.slice(0,4).map(p=>(
              <div key={p.id} className="card" style={{ overflow:"hidden" }}>
                <div style={{ position:"relative",height:140 }}>
                  <img src={p.img} alt={p.name} style={{ width:"100%",height:"100%",objectFit:"cover" }}/>
                  <div style={{ position:"absolute",top:8,right:8 }}>
                    <div style={{ background:p.active?"#2E7D32CC":"#C62828CC",color:"#FFF",fontSize:10,fontWeight:700,padding:"2px 8px",borderRadius:10 }}>{p.active?"● Live":"● Off"}</div>
                  </div>
                  <div style={{ position:"absolute",bottom:8,left:8 }}><span className="tag">{p.category}</span></div>
                </div>
                <div style={{ padding:"12px" }}>
                  <h4 style={{ fontSize:13.5,fontWeight:600,marginBottom:4 }}>{p.name}</h4>
                  <div style={{ display:"flex",gap:8,alignItems:"center",marginBottom:4 }}>
                    <span style={{ fontSize:15,fontWeight:700,color:T.ink }}>₹{p.price.toLocaleString()}</span>
                    <span style={{ fontSize:11,color:T.muted,textDecoration:"line-through" }}>₹{p.mrp.toLocaleString()}</span>
                    <span style={{ fontSize:11,fontWeight:600,color:T.ok }}>{p.discount}% off</span>
                  </div>
                  <div style={{ fontSize:11.5,color:T.muted }}>Stock: {p.stock} · {p.sub}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* CTA for incomplete store */}
      {!user.city&&(
        <div className="reveal" style={{ marginTop:20,background:T.lavPale,border:`1px solid ${T.border}`,borderRadius:14,padding:"18px 22px",display:"flex",gap:16,alignItems:"center" }}>
          <span style={{ fontSize:28 }}>⚠️</span>
          <div style={{ flex:1 }}>
            <p style={{ fontWeight:600,marginBottom:3 }}>Complete your store profile</p>
            <p style={{ fontSize:13,color:T.muted }}>Add your city, address, and store details so customers can find you.</p>
          </div>
          <button className="btn-primary" style={{ fontSize:12,padding:"9px 18px",flexShrink:0 }}>Complete Profile</button>
        </div>
      )}
    </div>
  );
};

// ══════════════════════════════════════════════════════════════
//  ADD / EDIT PRODUCT FORM
// ══════════════════════════════════════════════════════════════
const AddProductForm = ({ user, editProduct, onSave, onCancel }) => {
  const isEdit = !!editProduct;
  const init = editProduct || {
    name:"", category:"Women", sub:"", mrp:"", price:"", stock:"",
    fabric:"", description:"", img:"", sizes:[], colors:[], occasions:[],
    customizable:false, isNew:true, isBestseller:false, active:true,
  };
  const [form,setForm] = useState(init);
  const [loading,setLoading] = useState(false);
  const [err,setErr] = useState("");
  const [aiLoading,setAiLoading] = useState(false);
  const [aiSuggestions,setAiSuggestions] = useState(null);

  const set = (k,v) => setForm(f=>({...f,[k]:v}));

  const toggleArr = (key, val) => {
    setForm(f=>({ ...f,[key]:f[key].includes(val)?f[key].filter(x=>x!==val):[...f[key],val] }));
  };

  const CATS = { Women:["Sarees","Kurtis","Lehenga","Salwar Suits","Blouses","Gowns"], Men:["Shirts","Trousers","T-Shirts","Ethnic Wear","Jeans"], Kids:["Girls Frocks","Boys Wear","Ethnic Wear","School Wear"], Fabric:["Dress Material","Fabrics"], Accessories:["Dupattas","Jewellery","Bags"] };
  const SIZES_MAP = { Women:["XS","S","M","L","XL","XXL","3XL","Free Size"], Men:["S(38)","M(40)","L(42)","XL(44)","XXL(46)","3XL(48)"], Kids:["1–2 Yrs","2–3 Yrs","3–4 Yrs","4–5 Yrs","5–6 Yrs","6–7 Yrs","7–8 Yrs"] };
  const ALL_OCCA = ["Casual","Office","Festive","Wedding","Bridal","Daily Wear","Party","Formal"];
  const COLORS = ["White","Black","Red","Blue","Green","Yellow","Pink","Purple","Orange","Beige","Grey","Multi-colour","Off White","Navy"];
  const discount = form.mrp&&form.price ? Math.round(((form.mrp-form.price)/form.mrp)*100) : 0;

  // AI Description generator
  const generateDescription = async () => {
    if(!form.name){ setErr("Add product name first."); return; }
    setAiLoading(true);
    try {
      const res = await fetch("https://api.anthropic.com/v1/messages",{
        method:"POST",
        headers:{ "Content-Type":"application/json" },
        body:JSON.stringify({
          model:"claude-sonnet-4-20250514",max_tokens:400,
          messages:[{ role:"user", content:`Generate a compelling product description for an Indian fashion item sold on a local fashion platform called L4U. Product: ${form.name}, Category: ${form.category} - ${form.sub}, Fabric: ${form.fabric||"unspecified"}, Occasions: ${form.occasions.join(", ")||"general"}. Write 2-3 sentences, enthusiastic but honest, highlighting the fabric quality and occasions. Also suggest 3 one-line highlights as a JSON array. Return JSON only: { "description": "...", "highlights": ["...","...","..."] }` }]
        })
      });
      const data = await res.json();
      const text = data.content?.find(c=>c.type==="text")?.text||"{}";
      const clean = text.replace(/```json|```/g,"").trim();
      const parsed = JSON.parse(clean);
      setAiSuggestions(parsed);
      if(parsed.description) set("description",parsed.description);
    } catch(e) { setErr("Couldn't generate — write manually."); }
    setAiLoading(false);
  };

  const handleSave = () => {
    setErr("");
    if(!form.name.trim()){ setErr("Product name is required."); return; }
    if(!form.mrp||!form.price){ setErr("MRP and selling price are required."); return; }
    if(parseFloat(form.price)>parseFloat(form.mrp)){ setErr("Selling price cannot exceed MRP."); return; }
    if(!form.stock){ setErr("Stock quantity is required."); return; }
    setLoading(true);
    setTimeout(()=>{
      const product = {
        ...form,
        id: isEdit ? form.id : genId("p"),
        storeId: user.id,
        mrp: parseFloat(form.mrp),
        price: parseFloat(form.price),
        stock: parseInt(form.stock),
        discount,
        rating: isEdit ? form.rating : 0,
        reviews: isEdit ? form.reviews : 0,
        addedOn: isEdit ? form.addedOn : new Date().toLocaleDateString("en-IN",{month:"short",year:"numeric"}),
        img: form.img || "https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=500&q=80",
      };
      if(isEdit) {
        const idx = DB.inventory.findIndex(p=>p.id===form.id);
        if(idx>=0) DB.inventory[idx]=product;
      } else {
        DB.inventory.push(product);
      }
      setLoading(false);
      onSave(product);
    },800);
  };

  return (
    <div style={{ maxWidth:860,margin:"0 auto",padding:"24px" }}>
      <div className="fade-up" style={{ display:"flex",alignItems:"center",gap:12,marginBottom:24 }}>
        <button className="btn-ghost" onClick={onCancel} style={{ fontSize:13 }}>← Back</button>
        <div>
          <h1 className="serif" style={{ fontSize:28,fontWeight:400 }}>{isEdit?"Edit":"Add"} <em style={{ color:T.lav }}>Product</em></h1>
          <p style={{ fontSize:13,color:T.muted }}>This will {isEdit?"update":"appear"} live for customers immediately</p>
        </div>
      </div>

      {err&&<Alert type="err" msg={err} onClose={()=>setErr("")}/>}

      <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:18 }}>
        {/* LEFT column */}
        <div style={{ display:"flex",flexDirection:"column",gap:16 }}>
          {/* Product Name */}
          <div className="card" style={{ padding:"18px" }}>
            <h3 style={{ fontSize:14,fontWeight:600,marginBottom:14,color:T.charcoal }}>📝 Basic Info</h3>
            <div style={{ display:"flex",flexDirection:"column",gap:12 }}>
              <div>
                <label style={{ fontSize:12,fontWeight:600,color:T.muted,display:"block",marginBottom:5,textTransform:"uppercase",letterSpacing:".04em" }}>Product Name *</label>
                <input className="input-field" placeholder="e.g. Banarasi Silk Saree" value={form.name} onChange={e=>set("name",e.target.value)}/>
              </div>
              <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:10 }}>
                <div>
                  <label style={{ fontSize:12,fontWeight:600,color:T.muted,display:"block",marginBottom:5,textTransform:"uppercase",letterSpacing:".04em" }}>Category *</label>
                  <select className="input-field" value={form.category} onChange={e=>set("category",e.target.value)} style={{ paddingRight:8 }}>
                    {Object.keys(CATS).map(c=><option key={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label style={{ fontSize:12,fontWeight:600,color:T.muted,display:"block",marginBottom:5,textTransform:"uppercase",letterSpacing:".04em" }}>Type</label>
                  <select className="input-field" value={form.sub} onChange={e=>set("sub",e.target.value)} style={{ paddingRight:8 }}>
                    <option value="">Select type</option>
                    {(CATS[form.category]||[]).map(s=><option key={s}>{s}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label style={{ fontSize:12,fontWeight:600,color:T.muted,display:"block",marginBottom:5,textTransform:"uppercase",letterSpacing:".04em" }}>Fabric</label>
                <input className="input-field" placeholder="e.g. 100% Pure Cotton" value={form.fabric} onChange={e=>set("fabric",e.target.value)}/>
              </div>
            </div>
          </div>

          {/* Pricing */}
          <div className="card" style={{ padding:"18px" }}>
            <h3 style={{ fontSize:14,fontWeight:600,marginBottom:14,color:T.charcoal }}>💰 Pricing & Stock</h3>
            <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:12 }}>
              <div>
                <label style={{ fontSize:12,fontWeight:600,color:T.muted,display:"block",marginBottom:5,textTransform:"uppercase",letterSpacing:".04em" }}>MRP (₹) *</label>
                <input className="input-field" type="number" placeholder="8500" value={form.mrp} onChange={e=>set("mrp",e.target.value)}/>
              </div>
              <div>
                <label style={{ fontSize:12,fontWeight:600,color:T.muted,display:"block",marginBottom:5,textTransform:"uppercase",letterSpacing:".04em" }}>Selling Price (₹) *</label>
                <input className="input-field" type="number" placeholder="6800" value={form.price} onChange={e=>set("price",e.target.value)}/>
              </div>
            </div>
            <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:10 }}>
              <div>
                <label style={{ fontSize:12,fontWeight:600,color:T.muted,display:"block",marginBottom:5,textTransform:"uppercase",letterSpacing:".04em" }}>Stock Qty *</label>
                <input className="input-field" type="number" placeholder="10" value={form.stock} onChange={e=>set("stock",e.target.value)}/>
              </div>
              <div style={{ display:"flex",flexDirection:"column",justifyContent:"flex-end",paddingBottom:2 }}>
                {discount>0&&<div style={{ background:T.lavPale,borderRadius:8,padding:"8px 12px",textAlign:"center" }}>
                  <span style={{ fontSize:13,fontWeight:700,color:T.lav }}>🎉 {discount}% discount</span>
                  <div style={{ fontSize:11,color:T.muted }}>Savings: ₹{parseFloat(form.mrp||0)-parseFloat(form.price||0)}</div>
                </div>}
              </div>
            </div>
          </div>

          {/* Image URL */}
          <div className="card" style={{ padding:"18px" }}>
            <h3 style={{ fontSize:14,fontWeight:600,marginBottom:14,color:T.charcoal }}>🖼️ Product Image</h3>
            <input className="input-field" placeholder="Image URL (https://...)" value={form.img} onChange={e=>set("img",e.target.value)} style={{ marginBottom:10 }}/>
            {form.img&&<img src={form.img} alt="" style={{ width:"100%",height:120,objectFit:"cover",borderRadius:8,border:`1px solid ${T.border}` }} onError={e=>e.target.style.display="none"}/>}
            <p style={{ fontSize:11,color:T.muted,marginTop:6 }}>Paste a direct image URL. Use Unsplash, your website, or Google Drive share link.</p>
          </div>

          {/* Toggles */}
          <div className="card" style={{ padding:"18px" }}>
            <h3 style={{ fontSize:14,fontWeight:600,marginBottom:14,color:T.charcoal }}>⚙️ Settings</h3>
            <div style={{ display:"flex",flexDirection:"column",gap:12 }}>
              <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center" }}>
                <div><div style={{ fontSize:13,fontWeight:500 }}>Listed (Visible to customers)</div><div style={{ fontSize:11,color:T.muted }}>Turn off to hide without deleting</div></div>
                <Toggle on={form.active} onChange={v=>set("active",v)}/>
              </div>
              <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center" }}>
                <div><div style={{ fontSize:13,fontWeight:500 }}>Customizable</div><div style={{ fontSize:11,color:T.muted }}>Accept custom orders</div></div>
                <Toggle on={form.customizable} onChange={v=>set("customizable",v)}/>
              </div>
              <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center" }}>
                <div><div style={{ fontSize:13,fontWeight:500 }}>New Arrival</div></div>
                <Toggle on={form.isNew} onChange={v=>set("isNew",v)}/>
              </div>
              <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center" }}>
                <div><div style={{ fontSize:13,fontWeight:500 }}>Mark as Bestseller</div></div>
                <Toggle on={form.isBestseller} onChange={v=>set("isBestseller",v)}/>
              </div>
            </div>
          </div>
        </div>

        {/* RIGHT column */}
        <div style={{ display:"flex",flexDirection:"column",gap:16 }}>
          {/* Description + AI */}
          <div className="card" style={{ padding:"18px" }}>
            <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14 }}>
              <h3 style={{ fontSize:14,fontWeight:600,color:T.charcoal }}>✍️ Description</h3>
              <button onClick={generateDescription} disabled={aiLoading} style={{ display:"flex",alignItems:"center",gap:5,background:T.lavPale,border:`1px solid ${T.border}`,borderRadius:7,padding:"5px 11px",cursor:"none",fontSize:11.5,fontWeight:600,color:T.lav,fontFamily:"'DM Sans',sans-serif" }}>
                {aiLoading?<Spinner/>:<span>✨</span>} AI Write
              </button>
            </div>
            <textarea className="input-field" rows={4} placeholder="Describe your product — fabric quality, design, occasions..." value={form.description} onChange={e=>set("description",e.target.value)} style={{ resize:"vertical" }}/>
            {aiSuggestions?.highlights&&(
              <div style={{ marginTop:10,background:T.lavPale,borderRadius:8,padding:"10px 12px" }}>
                <p style={{ fontSize:11,fontWeight:600,color:T.lav,marginBottom:7 }}>✨ AI Highlights</p>
                {aiSuggestions.highlights.map((h,i)=>(
                  <div key={i} style={{ fontSize:12,color:T.charcoal,marginBottom:4,display:"flex",gap:6 }}><span style={{ color:T.lav }}>✓</span>{h}</div>
                ))}
              </div>
            )}
          </div>

          {/* Sizes */}
          <div className="card" style={{ padding:"18px" }}>
            <h3 style={{ fontSize:14,fontWeight:600,marginBottom:12,color:T.charcoal }}>📏 Available Sizes</h3>
            <div style={{ display:"flex",flexWrap:"wrap",gap:7 }}>
              {[...(SIZES_MAP[form.category]||SIZES_MAP.Women),"Custom","Free Size"].map(s=>(
                <button key={s} onClick={()=>toggleArr("sizes",s)} style={{ padding:"6px 12px",borderRadius:8,border:`1.5px solid ${form.sizes.includes(s)?T.lav:T.border}`,background:form.sizes.includes(s)?T.lavPale:T.white,color:form.sizes.includes(s)?T.lav:T.charcoal,fontSize:12,cursor:"none",fontFamily:"'DM Sans',sans-serif",fontWeight:500,transition:"all .15s" }}>{s}</button>
              ))}
            </div>
          </div>

          {/* Colors */}
          <div className="card" style={{ padding:"18px" }}>
            <h3 style={{ fontSize:14,fontWeight:600,marginBottom:12,color:T.charcoal }}>🎨 Available Colors</h3>
            <div style={{ display:"flex",flexWrap:"wrap",gap:7 }}>
              {COLORS.map(c=>(
                <button key={c} onClick={()=>toggleArr("colors",c)} style={{ padding:"5px 11px",borderRadius:8,border:`1.5px solid ${form.colors.includes(c)?T.lav:T.border}`,background:form.colors.includes(c)?T.lavPale:T.white,color:form.colors.includes(c)?T.lav:T.charcoal,fontSize:12,cursor:"none",fontFamily:"'DM Sans',sans-serif",fontWeight:500,transition:"all .15s" }}>{c}</button>
              ))}
            </div>
          </div>

          {/* Occasions */}
          <div className="card" style={{ padding:"18px" }}>
            <h3 style={{ fontSize:14,fontWeight:600,marginBottom:12,color:T.charcoal }}>🎉 Occasions</h3>
            <div style={{ display:"flex",flexWrap:"wrap",gap:7 }}>
              {ALL_OCCA.map(o=>(
                <button key={o} onClick={()=>toggleArr("occasions",o)} style={{ padding:"5px 11px",borderRadius:8,border:`1.5px solid ${form.occasions.includes(o)?T.lav:T.border}`,background:form.occasions.includes(o)?T.lavPale:T.white,color:form.occasions.includes(o)?T.lav:T.charcoal,fontSize:12,cursor:"none",fontFamily:"'DM Sans',sans-serif",fontWeight:500,transition:"all .15s" }}>{o}</button>
              ))}
            </div>
          </div>

          {/* Preview & Save */}
          <div className="card" style={{ padding:"18px",background:T.lavPale,border:`1.5px solid ${T.border}` }}>
            <h3 style={{ fontSize:14,fontWeight:600,marginBottom:12,color:T.charcoal }}>👁️ Preview</h3>
            {form.name&&<div style={{ background:T.white,borderRadius:10,overflow:"hidden",border:`1px solid ${T.border}` }}>
              {form.img&&<img src={form.img} alt="" style={{ width:"100%",height:110,objectFit:"cover" }} onError={e=>e.target.style.display="none"}/>}
              <div style={{ padding:"10px 12px" }}>
                <div style={{ fontSize:11,color:T.muted }}>{form.sub||form.category}</div>
                <div style={{ fontSize:14,fontWeight:600,marginBottom:4 }}>{form.name}</div>
                {form.price&&<div style={{ display:"flex",gap:7,alignItems:"baseline" }}>
                  <span style={{ fontSize:16,fontWeight:700 }}>₹{parseFloat(form.price||0).toLocaleString()}</span>
                  {form.mrp&&<span style={{ fontSize:12,color:T.muted,textDecoration:"line-through" }}>₹{parseFloat(form.mrp||0).toLocaleString()}</span>}
                  {discount>0&&<span style={{ fontSize:11,fontWeight:600,color:T.ok }}>{discount}% off</span>}
                </div>}
                <div style={{ display:"flex",gap:4,marginTop:6,flexWrap:"wrap" }}>
                  {form.sizes.slice(0,4).map(s=><span key={s} style={{ fontSize:10,background:T.offWhite,borderRadius:4,padding:"2px 6px",color:T.muted }}>{s}</span>)}
                  {form.customizable&&<span className="tag" style={{ fontSize:9 }}>✂️ Custom</span>}
                  {form.isNew&&<span className="tag" style={{ background:"#E3F2FD",color:T.blue,border:"none",fontSize:9 }}>New</span>}
                </div>
              </div>
            </div>}
            <button className="btn-primary" onClick={handleSave} disabled={loading} style={{ width:"100%",marginTop:12,padding:"14px" }}>
              {loading?<Spinner/>:isEdit?"✅ Update Product":"🚀 Publish Product — Goes Live Now"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ══════════════════════════════════════════════════════════════
//  STORE INVENTORY MANAGEMENT
// ══════════════════════════════════════════════════════════════
const StoreInventory = ({ user, setPage, onEditProduct }) => {
  const [products,setProducts] = useState(DB.inventory.filter(p=>p.storeId===user.id));
  const [filter,setFilter] = useState("all");
  const [search,setSearch] = useState("");
  const [deleteId,setDeleteId] = useState(null);
  const [saved,setSaved] = useState("");

  const refresh = () => setProducts(DB.inventory.filter(p=>p.storeId===user.id));

  const toggleActive = (id) => {
    const idx = DB.inventory.findIndex(p=>p.id===id);
    if(idx>=0){ DB.inventory[idx].active = !DB.inventory[idx].active; refresh(); setSaved(DB.inventory[idx].active?"Product listed ✅":"Product hidden ✅"); setTimeout(()=>setSaved(""),2000); }
  };

  const deleteProduct = (id) => {
    const idx = DB.inventory.findIndex(p=>p.id===id);
    if(idx>=0){ DB.inventory.splice(idx,1); refresh(); setDeleteId(null); setSaved("Product deleted."); setTimeout(()=>setSaved(""),2000); }
  };

  const cats = ["all",...new Set(products.map(p=>p.category))];
  const filtered = products.filter(p=>{
    const catOk = filter==="all"||p.category===filter;
    const srOk = !search||p.name.toLowerCase().includes(search.toLowerCase())||p.sub?.toLowerCase().includes(search.toLowerCase());
    return catOk&&srOk;
  });

  return (
    <div style={{ maxWidth:1100,margin:"0 auto",padding:"24px" }}>
      <div className="fade-up" style={{ display:"flex",justifyContent:"space-between",alignItems:"flex-end",marginBottom:22,flexWrap:"wrap",gap:12 }}>
        <div>
          <h1 className="serif" style={{ fontSize:30,fontWeight:400 }}>My <em style={{ color:T.lav }}>Inventory</em></h1>
          <p style={{ color:T.muted,fontSize:13 }}>{products.length} products · {products.filter(p=>p.active).length} live</p>
        </div>
        <button className="btn-primary" onClick={()=>setPage("store-add")} style={{ fontSize:13,padding:"10px 20px" }}>+ Add Product</button>
      </div>

      {saved&&<Alert type="ok" msg={saved}/>}

      {/* Filters */}
      <div style={{ display:"flex",gap:10,marginBottom:18,flexWrap:"wrap" }}>
        <div style={{ position:"relative",flex:1,minWidth:200 }}>
          <span style={{ position:"absolute",left:11,top:"50%",transform:"translateY(-50%)",color:T.muted }}>🔍</span>
          <input className="input-field" placeholder="Search products…" value={search} onChange={e=>setSearch(e.target.value)} style={{ paddingLeft:34 }}/>
        </div>
        <div style={{ display:"flex",gap:6 }}>
          {cats.map(c=>(
            <button key={c} onClick={()=>setFilter(c)} style={{ padding:"8px 14px",borderRadius:8,border:`1px solid ${filter===c?T.lav:T.border}`,background:filter===c?T.lav:T.white,color:filter===c?T.white:T.charcoal,fontSize:12,cursor:"none",fontFamily:"'DM Sans',sans-serif",fontWeight:500,textTransform:"capitalize",transition:"all .15s" }}>{c}</button>
          ))}
        </div>
      </div>

      {filtered.length===0 ? (
        <div style={{ textAlign:"center",padding:"52px 20px",background:T.white,borderRadius:16,border:`1px solid ${T.border}` }}>
          <div style={{ fontSize:48,marginBottom:12 }}>📦</div>
          <p style={{ fontSize:15,fontWeight:500,marginBottom:6 }}>No products found</p>
          <button className="btn-primary" onClick={()=>setPage("store-add")} style={{ marginTop:8 }}>+ Add Product</button>
        </div>
      ) : (
        <div style={{ display:"flex",flexDirection:"column",gap:12 }}>
          {filtered.map(p=>(
            <div key={p.id} style={{ background:T.white,borderRadius:14,border:`1px solid ${p.active?T.border:T.borderSoft}`,padding:"14px",display:"flex",gap:14,alignItems:"flex-start",opacity:p.active?1:.65,transition:"all .2s" }}>
              <img src={p.img} alt={p.name} style={{ width:80,height:90,borderRadius:10,objectFit:"cover",flexShrink:0 }}/>
              <div style={{ flex:1,minWidth:0 }}>
                <div style={{ display:"flex",gap:6,marginBottom:5,flexWrap:"wrap" }}>
                  <span className="tag">{p.category}</span>
                  {p.sub&&<span className="tag" style={{ background:T.borderSoft }}>{p.sub}</span>}
                  {p.isBestseller&&<span className="tag" style={{ background:"#FFF3E0",color:"#E65100",border:"none" }}>⭐ Bestseller</span>}
                  {p.isNew&&<span className="tag" style={{ background:"#E3F2FD",color:T.blue,border:"none" }}>✨ New</span>}
                  {p.customizable&&<span className="tag" style={{ background:T.lavPale,color:T.lav }}>✂️ Custom</span>}
                </div>
                <h3 style={{ fontSize:15,fontWeight:600,marginBottom:4 }}>{p.name}</h3>
                <div style={{ display:"flex",gap:10,alignItems:"center",marginBottom:4 }}>
                  <span style={{ fontSize:16,fontWeight:700 }}>₹{p.price.toLocaleString()}</span>
                  <span style={{ fontSize:12,color:T.muted,textDecoration:"line-through" }}>₹{p.mrp.toLocaleString()}</span>
                  <span style={{ fontSize:12,fontWeight:600,color:T.ok }}>{p.discount}% off</span>
                </div>
                <div style={{ fontSize:12,color:T.muted }}>
                  📦 Stock: <strong>{p.stock}</strong> · Fabric: {p.fabric||"N/A"} · Added: {p.addedOn}
                  {p.rating>0&&<> · <Stars rating={p.rating} count={p.reviews} size={11}/></>}
                </div>
              </div>
              <div style={{ display:"flex",flexDirection:"column",gap:8,flexShrink:0,alignItems:"flex-end" }}>
                {/* Live status */}
                <div style={{ display:"flex",alignItems:"center",gap:7 }}>
                  <span style={{ fontSize:11,color:p.active?T.ok:T.muted,fontWeight:600 }}>{p.active?"● Live":"● Hidden"}</span>
                  <Toggle on={p.active} onChange={()=>toggleActive(p.id)}/>
                </div>
                <div style={{ display:"flex",gap:6 }}>
                  <button onClick={()=>onEditProduct(p)} style={{ padding:"6px 12px",borderRadius:7,border:`1px solid ${T.border}`,background:T.white,color:T.charcoal,fontSize:12,cursor:"none",fontFamily:"'DM Sans',sans-serif",transition:"all .15s" }} onMouseEnter={e=>e.currentTarget.style.borderColor=T.lav} onMouseLeave={e=>e.currentTarget.style.borderColor=T.border}>✏️ Edit</button>
                  <button onClick={()=>setDeleteId(p.id)} style={{ padding:"6px 12px",borderRadius:7,border:`1px solid #FFCDD2`,background:"#FFEBEE",color:T.err,fontSize:12,cursor:"none",fontFamily:"'DM Sans',sans-serif" }}>🗑️</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Delete confirm modal */}
      {deleteId&&(
        <div style={{ position:"fixed",inset:0,background:"rgba(0,0,0,.5)",backdropFilter:"blur(4px)",zIndex:500,display:"flex",alignItems:"center",justifyContent:"center",padding:20 }}>
          <div className="pop-in" style={{ background:T.white,borderRadius:16,padding:"28px",maxWidth:360,width:"100%",textAlign:"center" }}>
            <div style={{ fontSize:44,marginBottom:12 }}>🗑️</div>
            <h3 style={{ fontSize:18,fontWeight:600,marginBottom:8 }}>Delete Product?</h3>
            <p style={{ fontSize:13.5,color:T.muted,marginBottom:20 }}>This will permanently remove the product from your store and from customer view.</p>
            <div style={{ display:"flex",gap:10 }}>
              <button className="btn-outline" onClick={()=>setDeleteId(null)} style={{ flex:1 }}>Cancel</button>
              <button onClick={()=>deleteProduct(deleteId)} style={{ flex:1,padding:"12px",borderRadius:10,background:T.err,color:T.white,border:"none",fontFamily:"'DM Sans',sans-serif",fontWeight:600,fontSize:14,cursor:"none" }}>Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ══════════════════════════════════════════════════════════════
//  STORE DATA WITH REAL GPS COORDS (South India shops)
// ══════════════════════════════════════════════════════════════
const STORES_GEO = [
  // ── TELANGANA — Hyderabad ──────────────────────────────────────────────────
  { id:"s1",  name:"Swarna Kanchi Mall",           city:"Nalgonda",           state:"Telangana",     stateCode:"TG", area:"Nagarjuna Colony",    type:"store",    lat:17.0627, lng:79.2665, rating:4.7, reviews:1477,  phone:"+91 91009 03659", tags:["Sarees","Kurtas","Bridal","Ethnic"],              hours:"10 AM – 10 PM · Daily",       img:"https://images.unsplash.com/photo-1594938298603-c8148c4b4157?w=400&q=80",  verified:true,  onboarded:true,  placeId:"ChIJ-",  snippet:"Beautiful kurta sets & sarees. Staff patiently helps." },
  { id:"s2",  name:"KALKI Hyderabad",              city:"Hyderabad",           state:"Telangana",     stateCode:"TG", area:"Banjara Hills",       type:"boutique", lat:17.4161, lng:78.4432, rating:4.9, reviews:2923,  phone:"+91 40 4520 9977", tags:["Bridal","Designer","Lehenga","Premium"],           hours:"10:30 AM – 9 PM · Daily",     img:"https://images.unsplash.com/photo-1583391733956-6c78276477e2?w=400&q=80",  verified:true,  onboarded:true,  placeId:"ChIJ-",  snippet:"Top-tier bridal boutique. Premium collection." },
  { id:"s3",  name:"Prashanti Sarees",             city:"Hyderabad",           state:"Telangana",     stateCode:"TG", area:"Banjara Hills",       type:"store",    lat:17.4124, lng:78.4495, rating:4.8, reviews:18232, phone:"+91 99625 75533", tags:["Sarees","Kanchipuram","Banarasi","Bridal"],        hours:"10 AM – 9 PM · Daily",        img:"https://images.unsplash.com/photo-1610030469983-98e550d6193c?w=400&q=80",  verified:true,  onboarded:true,  placeId:"ChIJ-",  snippet:"Wide range from Kanchipuram silks to Banarasi. Multiple floors." },
  { id:"s4",  name:"Nexus Hyderabad Mall",         city:"Hyderabad",           state:"Telangana",     stateCode:"TG", area:"Kukatpally",          type:"mall",     lat:17.4844, lng:78.3893, rating:4.4, reviews:132926,phone:null,              tags:["Fashion","Multi-brand","Family","130k+ Reviews"],  hours:"10 AM – 10 PM · Daily",       img:"https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=400&q=80",  verified:true,  onboarded:false, placeId:"ChIJ-",  snippet:"Hyderabad's most-reviewed mall. 130k+ Google reviews." },
  { id:"s5",  name:"Unlimited Fashion Nalgonda",   city:"Nalgonda",            state:"Telangana",     stateCode:"TG", area:"Ravindra Nagar",      type:"store",    lat:17.0663, lng:79.2650, rating:4.9, reviews:3909,  phone:"+91 99596 36675", tags:["Men","Women","Kids","Casual","Formals"],           hours:"11 AM – 10 PM · Daily",       img:"https://images.unsplash.com/photo-1555529669-e69e7aa0ba9a?w=400&q=80",     verified:true,  onboarded:false, placeId:"ChIJ-",  snippet:"Huge variety. Family shopping destination in Nalgonda." },
  { id:"s6",  name:"Deepa Designer Studio",        city:"Suryapet",            state:"Telangana",     stateCode:"TG", area:"Sri Sri Nagar",       type:"boutique", lat:17.1481, lng:79.6216, rating:5.0, reviews:45,    phone:"+91 90303 33682", tags:["Stitching","Custom","Bridal","Designer"],          hours:"9 AM – 9 PM · Daily",         img:"https://images.unsplash.com/photo-1641726988765-3b8a7b5c1afa?w=400&q=80",  verified:true,  onboarded:false, placeId:"ChIJ-",  snippet:"5-star rated designer boutique in Suryapet." },
  { id:"s7",  name:"Unlimited Fashion Warangal",   city:"Warangal",            state:"Telangana",     stateCode:"TG", area:"MGM Circle",          type:"store",    lat:18.0032, lng:79.5571, rating:4.8, reviews:6813,  phone:"+91 92890 62243", tags:["Men","Women","Kids","All Wear"],                   hours:"11 AM – 10 PM · Daily",       img:"https://images.unsplash.com/photo-1523381294911-8d3cead13475?w=400&q=80",  verified:true,  onboarded:false, placeId:"ChIJ-",  snippet:"Trendy, high-quality. Helpful non-pushy staff." },
  // ── TELANGANA — Miryalaguda ────────────────────────────────────────────────
  { id:"s21", name:"GV Mall Miryalaguda",          city:"Miryalaguda",         state:"Telangana",     stateCode:"TG", area:"Sagar Rd, Hanumanpet", type:"mall",    lat:16.8644, lng:79.5569, rating:4.0, reviews:796,   phone:"+91 87422 55457", tags:["Men","Women","Kids","Multi-floor","Family"],       hours:"9:30 AM – 9:30 PM · Daily",   img:"https://images.unsplash.com/photo-1555529669-e69e7aa0ba9a?w=400&q=80",     verified:true,  onboarded:false, placeId:"ChIJjQ04UIwvNToR-rZzaIg78P4", snippet:"Biggest mall in MLG. G+3 floors. Complete family shopping." },
  { id:"s22", name:"CMR Shopping Mall Miryalaguda",city:"Miryalaguda",         state:"Telangana",     stateCode:"TG", area:"Sagar Rd",             type:"mall",    lat:16.8645, lng:79.5565, rating:4.7, reviews:1877,  phone:"+91 76600 02090", tags:["Sarees","Ladies Wear","Men","Kids","Collection"],  hours:"10 AM – 9:30 PM · Daily",     img:"https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=400&q=80",  verified:true,  onboarded:false, placeId:"ChIJoWwWXDcvNToRnLxbuQx84Xs", snippet:"Great service & best collection. Staff very welcoming." },
  { id:"s23", name:"M.S. Mall Miryalaguda",        city:"Miryalaguda",         state:"Telangana",     stateCode:"TG", area:"Doctors Colony",       type:"store",   lat:16.8686, lng:79.5607, rating:4.7, reviews:45,    phone:"+91 99593 34059", tags:["Sarees","Fabrics","Stitching","Blouses"],         hours:"10 AM – 9 PM · Daily",        img:"https://images.unsplash.com/photo-1594938298603-c8148c4b4157?w=400&q=80",  verified:true,  onboarded:false, placeId:"ChIJpUw1zzolNToR9LVUNT8YoFQ", snippet:"One-stop for all occasions. Good sarees, fabrics & stitching." },
  { id:"s24", name:"TRENDS Miryalaguda",           city:"Miryalaguda",         state:"Telangana",     stateCode:"TG", area:"Sagar Rd, Reddy Colony",type:"store",   lat:16.8659, lng:79.5583, rating:3.9, reviews:1176,  phone:"+91 93919 91271", tags:["Men","Women","Kids","Budget","Casual"],            hours:"11 AM – 9:30 PM · Daily",     img:"https://images.unsplash.com/photo-1523381294911-8d3cead13475?w=400&q=80",  verified:false, onboarded:false, placeId:"ChIJA-iRsokvNToRNlXcwCScmVY", snippet:"Good options within ₹2K range. Multiple varieties." },
  // ── TELANGANA — Nizamabad ──────────────────────────────────────────────────
  { id:"s25", name:"Anokhi Designer Nizamabad",    city:"Nizamabad",           state:"Telangana",     stateCode:"TG", area:"Barkatpura",           type:"boutique", lat:18.6722, lng:78.0935, rating:4.9, reviews:25,    phone:"+91 92950 40404", tags:["Designer","Wedding","Bridal","Daily Wear"],        hours:"10 AM – 10 PM · Daily",       img:"https://images.unsplash.com/photo-1576566588028-4147f3842f27?w=400&q=80",  verified:true,  onboarded:false, placeId:"ChIJfW2jOADbzTsRRBQZRRkIYNQ", snippet:"Best quality with various patterns. Latest fashion. Excellent staff." },
  { id:"s26", name:"V.D.M Boutique Nizamabad",     city:"Nizamabad",           state:"Telangana",     stateCode:"TG", area:"Kumar Galli",          type:"boutique", lat:18.6736, lng:78.0961, rating:4.8, reviews:65,    phone:null,              tags:["Stitching","Maggam Work","Bridal","Wedding"],      hours:"9:30 AM – 7:40 PM · Closed Sat–Sun", img:"https://images.unsplash.com/photo-1641726988765-3b8a7b5c1afa?w=400&q=80", verified:true,  onboarded:false, placeId:"ChIJ5X4mb7HbzTsRArPy3554Roc", snippet:"Best wedding designer in Nizamabad. Perfect stitching & finishing." },
  { id:"s27", name:"Ishaq Fashions Nizamabad",     city:"Nizamabad",           state:"Telangana",     stateCode:"TG", area:"Barkatpura",           type:"store",    lat:18.6713, lng:78.0917, rating:4.4, reviews:21,    phone:"+91 73964 06482", tags:["Sarees","Suits","Wedding","Traditional"],          hours:"11 AM – 9 PM · Sun 12–6 PM",  img:"https://images.unsplash.com/photo-1610030469983-98e550d6193c?w=400&q=80",  verified:false, onboarded:false, placeId:"ChIJDZ5PBpLbzTsRnfHCkoIs62w", snippet:"Stunning sarees & suits. Traditional & modern styles at affordable prices." },
  // ── TELANGANA — Kamareddy ──────────────────────────────────────────────────
  { id:"s28", name:"KISAN FASHION MALL Kamareddy", city:"Kamareddy",           state:"Telangana",     stateCode:"TG", area:"Nizam Sagar X Road",  type:"mall",     lat:18.3203, lng:78.3344, rating:3.8, reviews:895,   phone:"+91 95229 52293", tags:["Sarees","Ladies","Men","Kids","Multi-brand"],      hours:"9 AM – 9:30 PM · Daily",      img:"https://images.unsplash.com/photo-1567401893414-76b7b1e5a7a5?w=400&q=80",  verified:false, onboarded:false, placeId:"ChIJ-0NC3a9JzDsRf1rtrreMkAg", snippet:"Good collection of sarees. Large variety for all occasions." },
  { id:"s29", name:"TRENDS Kamareddy",             city:"Kamareddy",           state:"Telangana",     stateCode:"TG", area:"Vidhya Nagar Colony",  type:"store",    lat:18.3217, lng:78.3333, rating:3.9, reviews:986,   phone:"+91 78159 22205", tags:["Men","Women","Kids","Reliance","Budget"],          hours:"11 AM – 9:30 PM · Daily",     img:"https://images.unsplash.com/photo-1489987707025-afc232f7ea0f?w=400&q=80",  verified:true,  onboarded:false, placeId:"ChIJXQ_Ao6ZJzDsRvUo4Avurisw", snippet:"Reliance Trends — good collections at reasonable prices." },
  { id:"s30", name:"VISHALA Shopping Mall Kamareddy",city:"Kamareddy",         state:"Telangana",     stateCode:"TG", area:"Nizam Sagar Chowrastha",type:"mall",    lat:18.3205, lng:78.3334, rating:4.2, reviews:15,    phone:null,              tags:["Sarees","Kids","Ladies","Men"],                    hours:"9 AM – 10:30 PM · Daily",     img:"https://images.unsplash.com/photo-1555529669-e69e7aa0ba9a?w=400&q=80",     verified:false, onboarded:false, placeId:"ChIJN54GJABJzDsRyxJcbcf1IyQ", snippet:"Shopping mall in Kamareddy with sarees, kids & ladies wear." },
  // ── TELANGANA — Khammam ────────────────────────────────────────────────────
  { id:"s31", name:"Fashion Factory Khammam",      city:"Khammam",             state:"Telangana",     stateCode:"TG", area:"Wyra Rd, VVC Complex", type:"store",    lat:17.2507, lng:80.1559, rating:4.9, reviews:2270,  phone:"+91 1800 891 0001",tags:["Women","Men","Kids","Sports","Branded"],            hours:"11 AM – 9:30 PM · Daily",     img:"https://images.unsplash.com/photo-1489987707025-afc232f7ea0f?w=400&q=80",  verified:true,  onboarded:false, placeId:"ChIJicZsMgBZNDoR_3r0itoQvsQ", snippet:"Better deals for better fashion. Affordable collection." },
  { id:"s32", name:"Unlimited Fashion Khammam",    city:"Khammam",             state:"Telangana",     stateCode:"TG", area:"Indira Nagar",         type:"store",    lat:17.2490, lng:80.1670, rating:4.8, reviews:7678,  phone:"+91 92890 62320", tags:["Men","Women","Kids","Footwear","Affordable"],      hours:"11 AM – 10 PM · Daily",       img:"https://images.unsplash.com/photo-1523381294911-8d3cead13475?w=400&q=80",  verified:true,  onboarded:false, placeId:"ChIJ1To4dm9ZNDoR1M9Mc7WaOR8", snippet:"Great collection. Cooperative staff. Good for children's dresses." },
  { id:"s33", name:"GV Mall Khammam",              city:"Khammam",             state:"Telangana",     stateCode:"TG", area:"Kasba Bazar",          type:"mall",     lat:17.2467, lng:80.1419, rating:3.9, reviews:8041,  phone:null,              tags:["Sarees","Men","Women","Kids","4 Floors"],          hours:"9:30 AM – 9:30 PM · Daily",   img:"https://images.unsplash.com/photo-1567401893414-76b7b1e5a7a5?w=400&q=80",  verified:false, onboarded:false, placeId:"ChIJaZUvEi1YNDoR9G6OJk30Ixg", snippet:"4-floor mall. Wide variety. Mid-range pricing for all family." },
  { id:"s34", name:"KLM Fashion Mall Khammam",     city:"Khammam",             state:"Telangana",     stateCode:"TG", area:"Kasba Bazar",          type:"mall",     lat:17.2451, lng:80.1412, rating:4.2, reviews:1682,  phone:"+91 87422 24666", tags:["Ethnic","Western","Festive","Men","Women","Kids"],  hours:"9 AM – 10 PM · Daily",        img:"https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=400&q=80",  verified:true,  onboarded:false, placeId:"ChIJizEHlUBZNDoRcxMmiSa1a6U", snippet:"Trendy fashion for every generation. Ethnic, western & casual." },
  { id:"s35", name:"The Chennai Mall Khammam",     city:"Khammam",             state:"Telangana",     stateCode:"TG", area:"Vijaynagar Colony",    type:"mall",     lat:17.2596, lng:80.1568, rating:4.1, reviews:5376,  phone:"+91 90007 77444", tags:["Sarees","Lehenga","Men","Women","Kids","Alterations"],hours:"10 AM – 10 PM · Daily",      img:"https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=400&q=80",     verified:true,  onboarded:false, placeId:"ChIJk5cyacNZNDoRErdOuG_QfQw", snippet:"Impressive saree collection. Friendly staff. In-store alterations available." },
  { id:"s36", name:"South India Shopping Mall Khammam",city:"Khammam",         state:"Telangana",     stateCode:"TG", area:"Wyra Rd",              type:"mall",     lat:17.2471, lng:80.1508, rating:4.2, reviews:2056,  phone:"+91 1800 4254 6464",tags:["Sarees","Women","Kids","Valet Parking","Family"],  hours:"10 AM – 10 PM · Daily",       img:"https://images.unsplash.com/photo-1555529669-e69e7aa0ba9a?w=400&q=80",     verified:true,  onboarded:false, placeId:"ChIJ0eZEYz5ZNDoRfVnGYNv8VZc", snippet:"Best family shopping mall. 365-day offers. Valet parking available." },
  // ── ANDHRA PRADESH — Visakhapatnam & Vijayawada ───────────────────────────
  { id:"s8",  name:"Unlimited Fashion Vizag",      city:"Visakhapatnam",       state:"Andhra Pradesh",stateCode:"AP", area:"Rama Talkies Rd",     type:"store",    lat:17.6921, lng:83.2017, rating:4.9, reviews:8026,  phone:"+91 92890 62315", tags:["Men","Women","Kids","Casual","Festive"],           hours:"11 AM – 10 PM · Daily",       img:"https://images.unsplash.com/photo-1567401893414-76b7b1e5a7a5?w=400&q=80",  verified:true,  onboarded:false, placeId:"ChIJ-", snippet:"Excellent team. Wide variety. Trendy & affordable." },
  { id:"s19", name:"Sowrabi Boutique Vijayawada",  city:"Vijayawada",          state:"Andhra Pradesh",stateCode:"AP", area:"Benz Circle",          type:"boutique", lat:16.5027, lng:80.6380, rating:5.0, reviews:36,    phone:"+91 82979 83000", tags:["Boutique","Women","Custom","Designer","Bridal"],   hours:"10 AM – 8:30 PM · Closed Sun",img:"https://images.unsplash.com/photo-1641726988765-3b8a7b5c1afa?w=400&q=80",  verified:true,  onboarded:false, placeId:"ChIJ-", snippet:"Wonderful ambience near Benz circle. Great collections." },
  // ── ANDHRA PRADESH — Rajahmundry (new) ────────────────────────────────────
  { id:"s37", name:"Unlimited Fashion Rajahmundry",city:"Rajahmundry",         state:"Andhra Pradesh",stateCode:"AP", area:"JN Road, Srinivas Nagar",type:"store",  lat:17.0077, lng:81.7924, rating:4.7, reviews:3605,  phone:"+91 92890 62312", tags:["Men","Women","Kids","Trendy","Affordable"],        hours:"11 AM – 10 PM · Daily",       img:"https://images.unsplash.com/photo-1523381294911-8d3cead13475?w=400&q=80",  verified:true,  onboarded:false, placeId:"ChIJFTIB-iijNzoRWMrb-EQ1DCQ", snippet:"Great shopping experience. Friendly staff. Good variety & fabrics." },
  { id:"s38", name:"GV Mall Rajahmundry",          city:"Rajahmundry",         state:"Andhra Pradesh",stateCode:"AP", area:"Tadithota",             type:"mall",     lat:17.0000, lng:81.7817, rating:4.4, reviews:1995,  phone:"+91 83310 02002", tags:["Men","Women","Kids","Multi-floor","Affordable"],   hours:"9:30 AM – 9:30 PM · Daily",   img:"https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=400&q=80",  verified:true,  onboarded:false, placeId:"ChIJ____P4mjNzoRFZIs49QcA0A", snippet:"Good collection. Supportive staff. Reasonable prices for men, women & kids." },
  { id:"s39", name:"Fashion Factory Rajahmundry",  city:"Rajahmundry",         state:"Andhra Pradesh",stateCode:"AP", area:"Central Jail Rd",       type:"store",    lat:17.0230, lng:81.7988, rating:4.9, reviews:1921,  phone:"+91 1800 891 0001",tags:["Women","Men","Kids","Brands","Affordable"],         hours:"11 AM – 9:30 PM · Daily",     img:"https://images.unsplash.com/photo-1489987707025-afc232f7ea0f?w=400&q=80",  verified:true,  onboarded:false, placeId:"ChIJnx7OmkqjNzoRh8xLkzoukP0", snippet:"Nice collection with affordable prices. Best brands available." },
  { id:"s40", name:"Siri Dress Divine Rajahmundry",city:"Rajahmundry",         state:"Andhra Pradesh",stateCode:"AP", area:"JN Road, Gandhipuram",  type:"store",    lat:17.0078, lng:81.7941, rating:4.8, reviews:2842,  phone:"+91 95153 42222", tags:["Women","Long Dresses","Kurtis","Trendy","Cotton"],  hours:"10 AM – 9:30 PM · Daily",     img:"https://images.unsplash.com/photo-1614897823648-6b27b9ab19b1?w=400&q=80",  verified:true,  onboarded:false, placeId:"ChIJDddNb-OjNzoRYQ8zXMP64Ok", snippet:"Go-to shop for long dresses since 2022. Warm welcoming staff." },
  { id:"s41", name:"Z Mall Rajahmundry",           city:"Rajahmundry",         state:"Andhra Pradesh",stateCode:"AP", area:"Prakasam Nagar",         type:"mall",     lat:17.0072, lng:81.7896, rating:4.5, reviews:176,   phone:null,              tags:["Premium Brands","Allen Solly","Puma","Arrow"],     hours:"10 AM – 10 PM · Daily",       img:"https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=400&q=80",  verified:false, onboarded:false, placeId:"ChIJg8xCJ-ajNzoRUHB_2o2I1DI", snippet:"Premium brands: Allen Solly, Rare Rabbit, Puma. Great deals during sales." },
  // ── ANDHRA PRADESH — Tirupati (new) ───────────────────────────────────────
  { id:"s42", name:"BNR SV Mall Tirupati",         city:"Tirupati",            state:"Andhra Pradesh",stateCode:"AP", area:"STV Nagar",              type:"mall",     lat:13.6223, lng:79.4247, rating:4.2, reviews:3161,  phone:null,              tags:["Pantaloons","MAX","Smart Bazaar","Family"],        hours:"10 AM – 11:30 PM · Daily",    img:"https://images.unsplash.com/photo-1555529669-e69e7aa0ba9a?w=400&q=80",     verified:true,  onboarded:false, placeId:"ChIJywcpzulLTToRBQA5UiD8MHw", snippet:"Pantaloons, MAX, Smart Bazaar under one roof. City centre location." },
  { id:"s43", name:"Unlimited Fashion Tirupati",   city:"Tirupati",            state:"Andhra Pradesh",stateCode:"AP", area:"Air Bypass Rd",           type:"store",    lat:13.6227, lng:79.4192, rating:4.8, reviews:10109, phone:"+91 92890 62313", tags:["Men","Women","Kids","Ethnic","Western","Casual"],  hours:"11 AM – 9 PM · Daily",        img:"https://images.unsplash.com/photo-1523381294911-8d3cead13475?w=400&q=80",  verified:true,  onboarded:false, placeId:"ChIJHwD4EY5LTToR3FogVWu9VYw", snippet:"Broad collection across men's, women's & kids wear. Reasonable prices." },
  { id:"s44", name:"Subhamasthu Shopping Mall",    city:"Tirupati",            state:"Andhra Pradesh",stateCode:"AP", area:"VV Mahal Rd",             type:"mall",     lat:13.6369, lng:79.4205, rating:4.3, reviews:8107,  phone:"+91 63019 59310", tags:["Sarees","Men","Women","Kids","Quality"],           hours:"9:30 AM – 9:30 PM · Daily",   img:"https://images.unsplash.com/photo-1610030469983-98e550d6193c?w=400&q=80",  verified:true,  onboarded:false, placeId:"ChIJGdhuaQFLTToRtnACAo14r1E", snippet:"Whole family clothing under one roof. Excellent quality & affordable prices." },
  { id:"s45", name:"CMR Shopping Mall Tirupati",   city:"Tirupati",            state:"Andhra Pradesh",stateCode:"AP", area:"VV Mahal Rd",             type:"mall",     lat:13.6369, lng:79.4198, rating:4.3, reviews:2280,  phone:"+91 76709 01465", tags:["Sarees","Dress Materials","Ethnic","Western"],     hours:"9:30 AM – 9:30 PM · Daily",   img:"https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=400&q=80",  verified:true,  onboarded:false, placeId:"ChIJ__Zm441LTToR-6UNiGp3Ebk", snippet:"Wide range of apparel. Varied, stylish collection for every occasion." },
  // ── ANDHRA PRADESH — Kurnool (new) ────────────────────────────────────────
  { id:"s46", name:"Dress Circle Shopping Mall",   city:"Kurnool",             state:"Andhra Pradesh",stateCode:"AP", area:"Gandhi Nagar",            type:"mall",     lat:15.8302, lng:78.0398, rating:4.6, reviews:6558,  phone:"+91 88869 89992", tags:["Sarees","Pattu","Wedding","Ethnic","Jewellery"],   hours:"10 AM – 10 PM · Daily",       img:"https://images.unsplash.com/photo-1583391733956-6c78276477e2?w=400&q=80",  verified:true,  onboarded:false, placeId:"ChIJ38uKfl7ntTsRj9In4cDFtt8", snippet:"Wonderful saree experience. Patient staff. Best pattu saree collection." },
  { id:"s47", name:"TGV Anantha City Square Mall", city:"Kurnool",             state:"Andhra Pradesh",stateCode:"AP", area:"New Bus Stand Rd",        type:"mall",     lat:15.8267, lng:78.0353, rating:4.3, reviews:5156,  phone:"+91 63038 01585", tags:["Pantaloons","Zudio","MAX","KFC","Multiplex"],      hours:"10 AM – 10 PM · Daily",       img:"https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=400&q=80",     verified:true,  onboarded:false, placeId:"ChIJx97X8oHntTsRbfN30nbHzSg", snippet:"Top-notch destination — Pantaloons, Zudio, MAX, KFC, SVC Movies." },
  { id:"s48", name:"Style Union Kurnool",          city:"Kurnool",             state:"Andhra Pradesh",stateCode:"AP", area:"City Square Mall",         type:"store",    lat:15.8266, lng:78.0354, rating:4.8, reviews:2449,  phone:"+91 73040 53032", tags:["Men","Women","Kids","Trendy","Affordable"],        hours:"11 AM – 9:30 PM · Daily",     img:"https://images.unsplash.com/photo-1489987707025-afc232f7ea0f?w=400&q=80",  verified:true,  onboarded:false, placeId:"ChIJcaPQMxnntTsR0oFpAUt9aOk", snippet:"Good fashion at reasonable & affordable prices. Great value." },
  { id:"s49", name:"Chandana Brothers Kurnool",    city:"Kurnool",             state:"Andhra Pradesh",stateCode:"AP", area:"Gandhi Nagar",             type:"store",    lat:15.8294, lng:78.0397, rating:4.0, reviews:1273,  phone:"+91 85182 49141", tags:["Sarees","Wedding","Traditional","365-Day Offers"],  hours:"10:30 AM – 9 PM · Daily",     img:"https://images.unsplash.com/photo-1610030469983-98e550d6193c?w=400&q=80",  verified:true,  onboarded:false, placeId:"ChIJKwL4Yl7ntTsRcCfSEVLeQCA", snippet:"First and biggest cloth mall in Kurnool. 365-day offer zone." },
  // ── TAMIL NADU ──────────────────────────────────────────────────────────────
  { id:"s9",  name:"Kaksh By Kavita Chennai",      city:"Chennai",             state:"Tamil Nadu",    stateCode:"TN", area:"Kilpauk",               type:"boutique", lat:13.0748, lng:80.2431, rating:5.0, reviews:315,   phone:"+91 98406 76686", tags:["Designer","Bridal","Wedding","Custom"],            hours:"11 AM – 8 PM · Daily",        img:"https://images.unsplash.com/photo-1576566588028-4147f3842f27?w=400&q=80",  verified:true,  onboarded:false, placeId:"ChIJ-", snippet:"Best designers in Chennai. World-class bridal." },
  { id:"s10", name:"The Chennai Silks Coimbatore", city:"Coimbatore",          state:"Tamil Nadu",    stateCode:"TN", area:"Town Hall",              type:"mall",     lat:11.0168, lng:76.9558, rating:4.5, reviews:40703, phone:"+91 422 239 1555",tags:["Sarees","Silk","6 Floors","Men","Women"],          hours:"9:30 AM – 9:30 PM · Daily",   img:"https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=400&q=80",     verified:true,  onboarded:false, placeId:"ChIJ-", snippet:"6-storied biggest in Coimbatore. Humble staff." },
  { id:"s11", name:"POTHYS Madurai",               city:"Madurai",             state:"Tamil Nadu",    stateCode:"TN", area:"West Masi Street",      type:"mall",     lat:9.9252,  lng:78.1198, rating:4.6, reviews:25875, phone:"+91 80 6968 0286",tags:["Sarees","Men","Women","Kids","Traditional"],       hours:"9 AM – 10 PM · Daily",        img:"https://images.unsplash.com/photo-1607082349566-187342175e2f?w=400&q=80",  verified:true,  onboarded:false, placeId:"ChIJ-", snippet:"All in one shop. Lot of options. Staff polite." },
  // ── KARNATAKA ───────────────────────────────────────────────────────────────
  { id:"s12", name:"KALKI Bengaluru",              city:"Bengaluru",           state:"Karnataka",     stateCode:"KA", area:"Jayanagar",             type:"boutique", lat:12.9249, lng:77.5933, rating:4.8, reviews:3329,  phone:"+91 8071 189 800",tags:["Bridal","Designer","Mirror Work","Linen"],          hours:"11 AM – 9 PM · Daily",        img:"https://images.unsplash.com/photo-1614897823648-6b27b9ab19b1?w=400&q=80",  verified:true,  onboarded:false, placeId:"ChIJ-", snippet:"Unbeatable quality. Mirror work & linen — all here." },
  { id:"s13", name:"7 Colors Fashion Bengaluru",   city:"Bengaluru",           state:"Karnataka",     stateCode:"KA", area:"Rajajinagar",           type:"store",    lat:12.9915, lng:77.5563, rating:4.9, reviews:568,   phone:"+91 96113 36777", tags:["Men","Women","Casual","Youth","Trendy"],           hours:"10:30 AM – 10 PM · Daily",    img:"https://images.unsplash.com/photo-1598300042247-d088f8ab3a91?w=400&q=80",  verified:true,  onboarded:false, placeId:"ChIJ-", snippet:"Mind-blowing collection. Must visit!" },
  { id:"s14", name:"Mall of Mysore",               city:"Mysuru",              state:"Karnataka",     stateCode:"KA", area:"MG Road",               type:"mall",     lat:12.3006, lng:76.6394, rating:4.3, reviews:32078, phone:"+91 821 453 4565",tags:["Fashion","Multi-brand","Cinema","Family"],          hours:"10:30 AM – 10 PM · Daily",    img:"https://images.unsplash.com/photo-1489987707025-afc232f7ea0f?w=400&q=80",  verified:true,  onboarded:false, placeId:"ChIJ-", snippet:"Best in Mysore. Premium vibe. Good mix of brands." },
  // ── KARNATAKA — Mangaluru (new) ──────────────────────────────────────────────
  { id:"s50", name:"Style Union City Centre Mangaluru",city:"Mangaluru",       state:"Karnataka",     stateCode:"KA", area:"City Centre Mall, Hampankatta",type:"store",lat:12.8715, lng:74.8427, rating:4.8, reviews:42663, phone:"+91 73040 53032", tags:["Men","Women","Kids","Trendy","Affordable","Mall"],  hours:"11 AM – 9:30 PM · Daily",     img:"https://images.unsplash.com/photo-1523381294911-8d3cead13475?w=400&q=80",  verified:true,  onboarded:false, placeId:"ChIJHS3urRRbozsRQQiuyw8G9lE", snippet:"42k+ reviews! Good collection at reasonable prices for all age groups." },
  { id:"s51", name:"MANTRA IXE Designer Studio",   city:"Mangaluru",           state:"Karnataka",     stateCode:"KA", area:"Car Street, Alake",     type:"boutique", lat:12.8716, lng:74.8348, rating:4.8, reviews:420,   phone:"+91 73535 75444", tags:["Bridal","Wedding","Designer","Indo-Western","Premium"],hours:"10 AM – 8 PM · Daily",       img:"https://images.unsplash.com/photo-1576566588028-4147f3842f27?w=400&q=80",  verified:true,  onboarded:false, placeId:"ChIJsZ117dVbozsRQcpR3qK8XNw", snippet:"Best designer studio. Wedding outfits, lehengas, Indo-western. Artists choose Mantra!" },
  { id:"s52", name:"Soch Balmatta Mangaluru",       city:"Mangaluru",           state:"Karnataka",     stateCode:"KA", area:"Balmatta Rd",           type:"store",    lat:12.8732, lng:74.8523, rating:4.9, reviews:105,   phone:"+91 824 411 4949", tags:["Women","Ethnic","Sarees","Kurtis","Premium"],      hours:"10:30 AM – 9 PM · Daily",     img:"https://images.unsplash.com/photo-1614897823648-6b27b9ab19b1?w=400&q=80",  verified:true,  onboarded:false, placeId:"ChIJzSqYdW5bozsR0MOH0razDL8", snippet:"Gorgeous clothes and friendly, professional service. Beautiful collections." },
  { id:"s53", name:"ANAM FASHIONS Mangaluru",       city:"Mangaluru",           state:"Karnataka",     stateCode:"KA", area:"Malabar Hill, Attavar",  type:"store",    lat:12.8677, lng:74.8478, rating:4.8, reviews:85,    phone:"+91 80508 94665", tags:["Women","Salwars","Kurtis","Designer Materials"],   hours:"9:45 AM – 9 PM · Thu closes 5 PM", img:"https://images.unsplash.com/photo-1567401893414-76b7b1e5a7a5?w=400&q=80", verified:true,  onboarded:false, placeId:"ChIJq6quAktaozsR1KycF4puF4s", snippet:"Best designer salwar materials & kurtis. Friendly staff. Highly recommended." },
  { id:"s54", name:"Fashion Spot Mangaluru",        city:"Mangaluru",           state:"Karnataka",     stateCode:"KA", area:"PM Rao Rd, Hampankatta", type:"boutique", lat:12.8692, lng:74.8410, rating:4.4, reviews:75,    phone:"+91 97388 92017", tags:["Salwars","Kurtis","Daily Wear","Women","Durable"],  hours:"9:30 AM – 9:30 PM · Daily",   img:"https://images.unsplash.com/photo-1641726988765-3b8a7b5c1afa?w=400&q=80",  verified:true,  onboarded:false, placeId:"ChIJBZLmVTBbozsRtmNf2AOoQkQ", snippet:"Go-to for readymade daily wear. Lovely collections. Colours don't fade." },
  // ── KERALA ──────────────────────────────────────────────────────────────────
  { id:"s15", name:"Fashion Bug Kochi",             city:"Kochi",               state:"Kerala",        stateCode:"KL", area:"Pullepady",             type:"store",    lat:9.9791,  lng:76.2903, rating:4.9, reviews:2595,  phone:"+91 484 409 9988", tags:["Women","Formal","Western","Uniforms"],             hours:"10 AM – 9 PM · Daily",        img:"https://images.unsplash.com/photo-1624378439575-d8705ad7ae80?w=400&q=80",  verified:true,  onboarded:false, placeId:"ChIJ-", snippet:"Exceptional customer service. Best formal women's wear." },
  { id:"s16", name:"POTHYS Thiruvananthapuram",     city:"Thiruvananthapuram",  state:"Kerala",        stateCode:"KL", area:"Vanchiyoor",             type:"mall",     lat:8.5003,  lng:76.9544, rating:4.4, reviews:55199, phone:"+91 80 6968 0290",tags:["Sarees","Men","Women","Kids","Traditional"],       hours:"9:30 AM – 9 PM · Daily",      img:"https://images.unsplash.com/photo-1612336307429-8a898d10e223?w=400&q=80",  verified:true,  onboarded:false, placeId:"ChIJ-", snippet:"Everything under one roof. Great staff & fabric." },
  { id:"s17", name:"Thottathil Silks Kozhikode",   city:"Kozhikode",           state:"Kerala",        stateCode:"KL", area:"Pavamani Road",          type:"store",    lat:11.2526, lng:75.7873, rating:4.9, reviews:17997, phone:"+91 495 272 1610",tags:["Silk","Cotton","Sarees","Linen","Multi-floor"],    hours:"9:30 AM – 8:30 PM · Daily",   img:"https://images.unsplash.com/photo-1583391733956-6c78276477e2?w=400&q=80",  verified:true,  onboarded:false, placeId:"ChIJ-", snippet:"Multi-level store. Lowest price for Kerala quality!" },
  { id:"s20", name:"Walton's Boutique Kochi",       city:"Kochi",               state:"Kerala",        stateCode:"KL", area:"Fort Kochi",             type:"boutique", lat:9.9650,  lng:76.2430, rating:4.8, reviews:76,    phone:"+91 73561 44046", tags:["Cotton","Handcrafted","Ethical","Fort Kochi"],     hours:"10 AM – 9 PM · Daily",        img:"https://images.unsplash.com/photo-1576566588028-4147f3842f27?w=400&q=80",  verified:true,  onboarded:false, placeId:"ChIJ-", snippet:"THE dress shop in Fort Kochi. Fabulous cotton prints." },
];

// Haversine distance formula (returns km)
const haversine = (lat1, lng1, lat2, lng2) => {
  const R = 6371;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a = Math.sin(dLat/2)**2 + Math.cos(lat1*Math.PI/180)*Math.cos(lat2*Math.PI/180)*Math.sin(dLng/2)**2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
};

const RANGE_OPTIONS = [5, 10, 25, 50, 100];
const TYPE_ICON = { mall:"🏬", store:"🏪", boutique:"✂️" };
const STATE_COLORS = { TG:"#8B7FD4", AP:"#7B68EE", TN:"#9B8FEE", KA:"#6A5ACD", KL:"#8A7BCE" };

// ══════════════════════════════════════════════════════════════
//  LOCATION PERMISSION MODAL
// ══════════════════════════════════════════════════════════════
const LocationModal = ({ onAllow, onDeny, loading }) => (
  <div style={{ position:"fixed",inset:0,background:"rgba(26,26,46,.8)",backdropFilter:"blur(8px)",zIndex:1000,display:"flex",alignItems:"center",justifyContent:"center",padding:20 }}>
    <div className="pop-in" style={{ background:T.white,borderRadius:24,padding:"40px 32px",maxWidth:440,width:"100%",textAlign:"center",boxShadow:"0 32px 80px rgba(123,104,238,.25)" }}>
      {/* Animated pin icon */}
      <div style={{ position:"relative",width:80,height:80,margin:"0 auto 20px",display:"flex",alignItems:"center",justifyContent:"center" }}>
        <div style={{ position:"absolute",inset:0,borderRadius:"50%",background:T.lavPale,animation:"ping 1.8s ease-out infinite" }}/>
        <div style={{ position:"absolute",inset:8,borderRadius:"50%",background:`linear-gradient(135deg,${T.lav},${T.lavDark})`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:32 }}>📍</div>
      </div>

      <div style={{ display:"inline-flex",alignItems:"center",gap:6,background:T.lavPale,border:`1px solid ${T.border}`,borderRadius:20,padding:"4px 14px",marginBottom:16 }}>
        <span style={{ width:6,height:6,borderRadius:"50%",background:T.lav,animation:"pulse 1.3s infinite",display:"inline-block" }}/>
        <span style={{ fontSize:11,color:T.lav,fontWeight:600,letterSpacing:".08em",textTransform:"uppercase" }}>Location Access</span>
      </div>

      <h2 className="serif" style={{ fontSize:28,fontWeight:400,marginBottom:10,lineHeight:1.2 }}>
        Find Shops <em style={{ color:T.lav }}>Near You</em>
      </h2>
      <p style={{ fontSize:14,color:T.muted,lineHeight:1.65,marginBottom:20 }}>
        L4U uses your location to show <strong style={{ color:T.ink }}>malls, boutiques & fashion stores</strong> sorted by distance — from 5 km to 100 km around you.
      </p>

      {/* Range preview pills */}
      <div style={{ display:"flex",gap:6,justifyContent:"center",flexWrap:"wrap",marginBottom:24 }}>
        {RANGE_OPTIONS.map(r=>(
          <span key={r} style={{ background:T.lavPale,border:`1px solid ${T.border}`,borderRadius:20,padding:"4px 12px",fontSize:12,color:T.lav,fontWeight:500 }}>📍 {r} km</span>
        ))}
      </div>

      {loading ? (
        <div style={{ padding:"18px",textAlign:"center" }}>
          <Spinner/>
          <p style={{ color:T.muted,fontSize:13,marginTop:10 }}>Detecting your location…</p>
          <p style={{ color:T.muted,fontSize:11,marginTop:4 }}>Please allow location access in your browser popup.</p>
        </div>
      ) : (
        <>
          <button className="btn-primary" onClick={onAllow} style={{ width:"100%",marginBottom:10,padding:"14px",fontSize:14 }}>
            ✅ Allow Location Access
          </button>
          <button className="btn-outline" onClick={onDeny} style={{ width:"100%",fontSize:13 }}>
            🔍 Browse by Filters Instead
          </button>
        </>
      )}
      <p style={{ fontSize:11,color:T.muted,marginTop:12,lineHeight:1.5 }}>
        Your location is used only to find nearby shops.<br/>It is never stored or shared.
      </p>
    </div>
  </div>
);

// ══════════════════════════════════════════════════════════════
//  STORE CARD
// ══════════════════════════════════════════════════════════════
const StoreCard = ({ store, distKm }) => {
  const [liked, setLiked] = useState(false);
  const open = store.hours.toLowerCase().includes("daily") || store.hours.toLowerCase().includes("24");
  const products = DB.inventory.filter(p => p.storeId === store.id && p.active);
  const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(store.name + " " + store.city)}&query_place_id=${store.placeId || ""}`;

  return (
    <div className="card" style={{ overflow:"hidden", animation:"fadeUp .5s ease both", display:"flex", flexDirection:"column" }}>
      {/* Image */}
      <div style={{ position:"relative", height:165, overflow:"hidden", flexShrink:0 }}>
        <img src={store.img} alt={store.name} style={{ width:"100%", height:"100%", objectFit:"cover", transition:"transform .5s" }}
          onMouseEnter={e=>e.currentTarget.style.transform="scale(1.07)"}
          onMouseLeave={e=>e.currentTarget.style.transform="scale(1)"}/>
        <div style={{ position:"absolute", inset:0, background:"linear-gradient(to top,rgba(26,26,46,.55) 0%,transparent 55%)" }}/>
        {/* Top badges */}
        <div style={{ position:"absolute", top:8, left:8, display:"flex", gap:5, flexWrap:"wrap" }}>
          {store.verified && <span style={{ background:`${T.lav}EE`, color:T.white, fontSize:10, fontWeight:600, padding:"2px 8px", borderRadius:10 }}>✅ Verified</span>}
          {!store.onboarded && <span style={{ background:"rgba(230,81,0,.88)", color:"#FFF", fontSize:10, fontWeight:700, padding:"2px 8px", borderRadius:10 }}>⏳ Yet to Onboard</span>}
          {store.onboarded && products.length > 0 && <span style={{ background:"rgba(46,125,50,.9)", color:"#FFF", fontSize:10, fontWeight:600, padding:"2px 8px", borderRadius:10 }}>📦 {products.length} Live Products</span>}
        </div>
        <button onClick={e=>{e.stopPropagation();setLiked(!liked);}} style={{ position:"absolute", top:8, right:8, width:28, height:28, borderRadius:"50%", background:"rgba(255,255,255,.88)", border:"none", cursor:"none", fontSize:14, display:"flex", alignItems:"center", justifyContent:"center" }}>{liked?"❤️":"🤍"}</button>
        {/* Bottom badges */}
        <div style={{ position:"absolute", bottom:8, left:8 }}>
          <span style={{ background:open?"#2E7D3299":"#C6282899", color:T.white, fontSize:9.5, fontWeight:700, padding:"2px 7px", borderRadius:8 }}>{open?"● Open":"● Closed"}</span>
        </div>
        {distKm != null && (
          <div style={{ position:"absolute", bottom:8, right:8 }}>
            <span style={{ background:"rgba(26,26,46,.78)", color:T.white, fontSize:10, fontWeight:600, padding:"2px 8px", borderRadius:8 }}>
              📍 {distKm < 1 ? `${Math.round(distKm*1000)} m` : `${distKm.toFixed(1)} km`}
            </span>
          </div>
        )}
      </div>

      {/* Body */}
      <div style={{ padding:"13px", flex:1, display:"flex", flexDirection:"column", gap:5 }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", gap:5 }}>
          <div style={{ flex:1 }}>
            <h3 style={{ fontSize:14.5, fontWeight:600, lineHeight:1.2, marginBottom:2 }}>{store.name}</h3>
            <p style={{ fontSize:11, color:T.muted }}>{TYPE_ICON[store.type]} {store.area} · {store.city}</p>
          </div>
          <span style={{ fontSize:11, fontWeight:600, color:STATE_COLORS[store.stateCode]||T.lav, background:`${STATE_COLORS[store.stateCode]||T.lav}18`, borderRadius:8, padding:"2px 8px", whiteSpace:"nowrap", flexShrink:0 }}>{store.stateCode}</span>
        </div>

        <div style={{ display:"flex", alignItems:"center", gap:6 }}>
          <Stars rating={store.rating} count={store.reviews} size={12}/>
        </div>

        <p style={{ fontSize:12, color:T.muted, lineHeight:1.5, flex:1 }}>{store.snippet}</p>

        {/* Hours */}
        <p style={{ fontSize:11, color:T.muted }}>🕐 {store.hours}</p>

        <div style={{ display:"flex", gap:4, flexWrap:"wrap" }}>
          {store.tags.slice(0,3).map(t=><span key={t} className="tag">{t}</span>)}
        </div>

        {/* Onboarding status banner */}
        {!store.onboarded && (
          <div style={{ background:"#FFF3E0", border:"1px solid #FFB300", borderRadius:8, padding:"8px 10px", marginTop:3 }}>
            <p style={{ fontSize:11.5, color:"#E65100", fontWeight:600, marginBottom:3 }}>⏳ Yet to Onboard on L4U</p>
            <p style={{ fontSize:11, color:"#BF360C", lineHeight:1.45 }}>This shop is not yet on L4U. View on Google Maps for details, or invite them to join!</p>
          </div>
        )}

        {/* Action buttons */}
        <div style={{ display:"flex", gap:7, marginTop:6 }}>
          {store.onboarded ? (
            <>
              <button className="btn-primary" style={{ flex:1, padding:"8px", fontSize:12 }}>🛍️ View Shop</button>
              <button className="btn-outline" style={{ flex:1, padding:"8px", fontSize:12 }}>💬 Chat</button>
            </>
          ) : (
            <>
              <a href={mapsUrl} target="_blank" rel="noreferrer" style={{ flex:2, textDecoration:"none" }}>
                <button className="btn-primary" style={{ width:"100%", padding:"8px", fontSize:12, background:"linear-gradient(135deg,#1565C0,#0D47A1)" }}>
                  🗺️ View on Google Maps
                </button>
              </a>
              <button className="btn-outline" style={{ flex:1, padding:"8px", fontSize:11.5, borderColor:"#FF6F00", color:"#FF6F00" }}>
                Invite to L4U
              </button>
            </>
          )}
        </div>

        {store.phone && (
          <a href={`tel:${store.phone}`} style={{ textDecoration:"none" }}>
            <p style={{ fontSize:11.5, color:T.teal, fontWeight:500, textAlign:"center", marginTop:2 }}>📞 {store.phone}</p>
          </a>
        )}
      </div>
    </div>
  );
};

// ══════════════════════════════════════════════════════════════
//  CUSTOMER HOME — location-aware nearby stores + filter fallback
// ══════════════════════════════════════════════════════════════
const CustomerHome = ({ user, userLoc, onRequestLocation }) => {
  // ── View mode: "stores" | "products"
  const [view, setView] = useState("stores");

  // ── Location-based state
  const [range, setRange] = useState(50);       // km radius

  // ── Manual filter state (used when location denied)
  const [stateF, setStateF]   = useState("ALL");
  const [cityF,  setCityF]    = useState("All");
  const [typeF,  setTypeF]    = useState("all");
  const [sortF,  setSortF]    = useState("rating");
  const [search, setSearch]   = useState("");

  // ── Product filters
  const [catF, setCatF] = useState("All");
  const PROD_CATS = ["All","Women","Men","Kids","Fabric","Accessories"];

  const hasLoc = !!userLoc;
  const allLive = DB.inventory.filter(p=>p.active);

  // Compute distances and filter stores
  const storesWithDist = STORES_GEO.map(s => ({
    ...s,
    distKm: hasLoc ? haversine(userLoc.lat, userLoc.lng, s.lat, s.lng) : null,
  }));

  const nearbyStores = hasLoc
    ? storesWithDist.filter(s => s.distKm <= range).sort((a,b)=>a.distKm-b.distKm)
    : [];

  // Manual filter stores (no location)
  const citiesForState = stateF==="ALL"
    ? ["All",...new Set(STORES_GEO.map(s=>s.city))]
    : ["All",...new Set(STORES_GEO.filter(s=>s.stateCode===stateF).map(s=>s.city))];

  const manualFiltered = storesWithDist.filter(s=>{
    const stOk = stateF==="ALL"||s.stateCode===stateF;
    const ciOk = cityF==="All"||s.city===cityF;
    const tyOk = typeF==="all"||s.type===typeF;
    const srOk = !search||s.name.toLowerCase().includes(search.toLowerCase())||s.city.toLowerCase().includes(search.toLowerCase())||s.tags.some(t=>t.toLowerCase().includes(search.toLowerCase()));
    return stOk&&ciOk&&tyOk&&srOk;
  }).sort((a,b)=>sortF==="rating"?b.rating-a.rating:sortF==="reviews"?b.reviews-a.reviews:a.name.localeCompare(b.name));

  const displayStores = hasLoc ? nearbyStores : manualFiltered;

  // Products
  const displayProducts = allLive
    .filter(p=>catF==="All"||p.category===catF)
    .map(p=>({ ...p, store: STORES_GEO.find(s=>s.id===p.storeId)||DB.stores.find(s=>s.id===p.storeId) }))
    .filter(p=>{
      if(!hasLoc) return true;
      const st = STORES_GEO.find(s=>s.id===p.storeId);
      if(!st) return true;
      return haversine(userLoc.lat,userLoc.lng,st.lat,st.lng) <= range;
    });

  useEffect(()=>{
    const els=document.querySelectorAll(".reveal");
    const io=new IntersectionObserver(e=>e.forEach(en=>{if(en.isIntersecting)en.target.classList.add("on");}),{threshold:.1});
    els.forEach(el=>io.observe(el));return()=>io.disconnect();
  },[view, hasLoc, range]);

  return (
    <div>
      {/* ── HERO ── */}
      <section style={{ background:"linear-gradient(150deg,#1A1A3E 0%,#2D2060 55%,#3A2070 100%)",minHeight:340,display:"flex",alignItems:"center",padding:"44px 24px",position:"relative",overflow:"hidden" }}>
        <div className="blob" style={{ width:340,height:340,background:"#7B68EE",top:-80,right:-50,opacity:.15 }}/>
        <div style={{ maxWidth:1200,margin:"0 auto",width:"100%",position:"relative",zIndex:2 }}>
          <div className="fade-up">
            <div style={{ display:"inline-flex",alignItems:"center",gap:8,background:"rgba(211,211,255,.15)",border:"1px solid rgba(211,211,255,.3)",borderRadius:20,padding:"5px 14px",marginBottom:14 }}>
              <span style={{ width:7,height:7,borderRadius:"50%",background:"#D3D3FF",animation:"pulse 1.5s infinite",display:"inline-block" }}/>
              <span style={{ fontSize:11,color:"#D3D3FF",letterSpacing:".1em",textTransform:"uppercase",fontWeight:500 }}>Welcome back, {user.name?.split(" ")[0]}!</span>
            </div>
            <h1 className="serif" style={{ fontSize:"clamp(28px,5vw,56px)",color:"#FFFFFF",lineHeight:1.1,marginBottom:10,fontWeight:400 }}>
              {hasLoc ? <>Shops <em className="lav-text">Near You</em></> : <>Shop Local. <em className="lav-text">Go Digital.</em></>}
            </h1>

            {/* Location status bar */}
            {hasLoc ? (
              <div style={{ display:"flex",alignItems:"center",gap:10,background:"rgba(46,125,50,.2)",border:"1px solid rgba(46,125,50,.35)",borderRadius:10,padding:"9px 14px",marginBottom:18,maxWidth:520 }}>
                <span style={{ fontSize:16 }}>📍</span>
                <div style={{ flex:1 }}>
                  <span style={{ fontSize:13,color:"#A5D6A7",fontWeight:600 }}>Location detected</span>
                  <span style={{ fontSize:12,color:"#81C784",marginLeft:8 }}>{userLoc.label}</span>
                </div>
                <span style={{ fontSize:12,color:"#A5D6A7",fontWeight:600 }}>{nearbyStores.length} shops within {range} km</span>
              </div>
            ) : (
              <div style={{ display:"flex",alignItems:"center",gap:10,background:"rgba(211,211,255,.12)",border:"1px solid rgba(211,211,255,.25)",borderRadius:10,padding:"9px 14px",marginBottom:18,maxWidth:520 }}>
                <span style={{ fontSize:16 }}>🔍</span>
                <span style={{ fontSize:13,color:"#C0C0E8" }}>Browsing all {STORES_GEO.length} shops · Use filters to narrow down</span>
                <button onClick={onRequestLocation} style={{ background:T.lav,border:"none",color:T.white,borderRadius:8,padding:"5px 11px",fontSize:11.5,cursor:"none",fontFamily:"'DM Sans',sans-serif",fontWeight:600,flexShrink:0 }}>📍 Enable</button>
              </div>
            )}

            {/* Stats */}
            <div style={{ display:"flex",gap:20,flexWrap:"wrap" }}>
              {(hasLoc
                ? [["🏪",nearbyStores.length,`Shops within ${range} km`],["📦",displayProducts.length,"Products nearby"],["🗺️","5","South India States"]]
                : [["🏪",STORES_GEO.length,"Total Shops"],["📦",allLive.length,"Live Products"],["🗺️","5","States"]]
              ).map(([ic,v,l])=>(
                <div key={l}>
                  <div className="serif" style={{ fontSize:20,fontWeight:600,color:"#D3D3FF",lineHeight:1 }}>{ic} {v}</div>
                  <div style={{ fontSize:11,color:"#9090C0",marginTop:2 }}>{l}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <div style={{ maxWidth:1200,margin:"0 auto",padding:"24px" }}>

        {/* ── VIEW TOGGLE (Stores | Products) ── */}
        <div style={{ display:"flex",gap:8,marginBottom:22,alignItems:"center",flexWrap:"wrap" }}>
          <div style={{ display:"flex",gap:6,background:T.offWhite,borderRadius:12,padding:3,flex:"0 0 auto" }}>
            {[["stores","🏪 Nearby Stores"],["products","📦 Products"]].map(([id,l])=>(
              <button key={id} onClick={()=>setView(id)} style={{ padding:"9px 18px",borderRadius:9,border:"none",fontFamily:"'DM Sans',sans-serif",fontSize:13,fontWeight:600,cursor:"none",transition:"all .2s",background:view===id?T.white:T.offWhite,color:view===id?T.lav:T.muted,boxShadow:view===id?"0 2px 10px #D3D3FF30":"none" }}>{l}</button>
            ))}
          </div>

          {/* Range selector — only when location is available */}
          {hasLoc && view==="stores" && (
            <div style={{ display:"flex",alignItems:"center",gap:8,flex:"0 0 auto" }}>
              <span style={{ fontSize:12.5,fontWeight:600,color:T.muted,whiteSpace:"nowrap" }}>📍 Range:</span>
              <div style={{ display:"flex",gap:5 }}>
                {RANGE_OPTIONS.map(r=>(
                  <button key={r} onClick={()=>setRange(r)} style={{ padding:"7px 12px",borderRadius:8,border:`1px solid ${range===r?T.lav:T.border}`,background:range===r?T.lav:T.white,color:range===r?T.white:T.charcoal,fontSize:12,cursor:"none",fontFamily:"'DM Sans',sans-serif",fontWeight:500,whiteSpace:"nowrap",transition:"all .15s" }}>{r} km</button>
                ))}
              </div>
            </div>
          )}

          {/* Search bar */}
          <div style={{ position:"relative",flex:1,minWidth:180 }}>
            <span style={{ position:"absolute",left:11,top:"50%",transform:"translateY(-50%)",color:T.muted,fontSize:14 }}>🔍</span>
            <input className="input-field" placeholder={view==="stores"?"Search shops, cities, tags…":"Search products…"} value={search} onChange={e=>setSearch(e.target.value)} style={{ paddingLeft:34,paddingTop:10,paddingBottom:10 }}/>
          </div>
        </div>

        {/* ══ STORES VIEW ══ */}
        {view==="stores" && (
          <>
            {/* Manual filters (shown only when no location) */}
            {!hasLoc && (
              <div style={{ background:T.white,borderRadius:14,border:`1px solid ${T.border}`,padding:"16px 18px",marginBottom:20 }}>
                <div style={{ display:"flex",alignItems:"center",gap:8,marginBottom:12 }}>
                  <span style={{ fontSize:13 }}>🔧</span>
                  <span style={{ fontSize:13.5,fontWeight:600,color:T.charcoal }}>Filter Shops</span>
                  <span style={{ fontSize:11,color:T.muted }}>· Enable location for distance-based discovery</span>
                  <button onClick={onRequestLocation} style={{ marginLeft:"auto",background:T.lavPale,border:`1px solid ${T.border}`,borderRadius:8,padding:"5px 12px",fontSize:12,cursor:"none",color:T.lav,fontFamily:"'DM Sans',sans-serif",fontWeight:600 }}>📍 Enable Location</button>
                </div>
                <div style={{ display:"flex",gap:10,flexWrap:"wrap" }}>
                  {/* State filter */}
                  <div style={{ flex:"1 1 140px" }}>
                    <label style={{ fontSize:11,fontWeight:600,color:T.muted,display:"block",marginBottom:4,textTransform:"uppercase",letterSpacing:".04em" }}>State</label>
                    <select className="input-field" value={stateF} onChange={e=>{setStateF(e.target.value);setCityF("All");}} style={{ padding:"9px 12px" }}>
                      <option value="ALL">All States</option>
                      {[...new Set(STORES_GEO.map(s=>s.stateCode))].map(c=>(
                        <option key={c} value={c}>{c} — {STORES_GEO.find(s=>s.stateCode===c)?.state}</option>
                      ))}
                    </select>
                  </div>
                  {/* City filter */}
                  <div style={{ flex:"1 1 140px" }}>
                    <label style={{ fontSize:11,fontWeight:600,color:T.muted,display:"block",marginBottom:4,textTransform:"uppercase",letterSpacing:".04em" }}>City</label>
                    <select className="input-field" value={cityF} onChange={e=>setCityF(e.target.value)} style={{ padding:"9px 12px" }}>
                      {citiesForState.map(c=><option key={c}>{c}</option>)}
                    </select>
                  </div>
                  {/* Type filter */}
                  <div style={{ flex:"1 1 130px" }}>
                    <label style={{ fontSize:11,fontWeight:600,color:T.muted,display:"block",marginBottom:4,textTransform:"uppercase",letterSpacing:".04em" }}>Type</label>
                    <select className="input-field" value={typeF} onChange={e=>setTypeF(e.target.value)} style={{ padding:"9px 12px" }}>
                      <option value="all">All Types</option>
                      <option value="mall">🏬 Malls</option>
                      <option value="store">🏪 Stores</option>
                      <option value="boutique">✂️ Boutiques</option>
                    </select>
                  </div>
                  {/* Sort */}
                  <div style={{ flex:"1 1 130px" }}>
                    <label style={{ fontSize:11,fontWeight:600,color:T.muted,display:"block",marginBottom:4,textTransform:"uppercase",letterSpacing:".04em" }}>Sort By</label>
                    <select className="input-field" value={sortF} onChange={e=>setSortF(e.target.value)} style={{ padding:"9px 12px" }}>
                      <option value="rating">Highest Rated</option>
                      <option value="reviews">Most Reviewed</option>
                      <option value="name">Name A–Z</option>
                    </select>
                  </div>
                </div>
                {/* Active filter chips */}
                {(stateF!=="ALL"||cityF!=="All"||typeF!=="all") && (
                  <div style={{ display:"flex",gap:6,flexWrap:"wrap",marginTop:10 }}>
                    {stateF!=="ALL"&&<span style={{ display:"flex",alignItems:"center",gap:5,fontSize:12,background:T.lavPale,color:T.lav,border:`1px solid ${T.border}`,borderRadius:12,padding:"3px 10px" }}>State: {stateF} <span style={{ cursor:"none" }} onClick={()=>setStateF("ALL")}>✕</span></span>}
                    {cityF!=="All"&&<span style={{ display:"flex",alignItems:"center",gap:5,fontSize:12,background:T.lavPale,color:T.lav,border:`1px solid ${T.border}`,borderRadius:12,padding:"3px 10px" }}>City: {cityF} <span style={{ cursor:"none" }} onClick={()=>setCityF("All")}>✕</span></span>}
                    {typeF!=="all"&&<span style={{ display:"flex",alignItems:"center",gap:5,fontSize:12,background:T.lavPale,color:T.lav,border:`1px solid ${T.border}`,borderRadius:12,padding:"3px 10px" }}>Type: {typeF} <span style={{ cursor:"none" }} onClick={()=>setTypeF("all")}>✕</span></span>}
                    <button onClick={()=>{setStateF("ALL");setCityF("All");setTypeF("all");setSortF("rating");setSearch("");}} style={{ fontSize:12,color:T.muted,background:"none",border:`1px solid ${T.border}`,borderRadius:12,padding:"3px 10px",cursor:"none",fontFamily:"'DM Sans',sans-serif" }}>Clear All</button>
                  </div>
                )}
              </div>
            )}

            {/* Results header */}
            <div className="reveal" style={{ display:"flex",justifyContent:"space-between",alignItems:"baseline",marginBottom:16,flexWrap:"wrap",gap:8 }}>
              <div>
                <h2 className="serif" style={{ fontSize:26,fontWeight:400 }}>
                  {hasLoc
                    ? <><strong style={{ color:T.lav }}>{displayStores.length}</strong> shops within <em style={{ color:T.lav }}>{range} km</em></>
                    : <><strong style={{ color:T.lav }}>{displayStores.length}</strong> shops <em style={{ color:T.lav }}>found</em></>}
                </h2>
                {hasLoc && <p style={{ fontSize:12,color:T.muted,marginTop:2 }}>Near {userLoc.label} · sorted by distance</p>}
              </div>
              {hasLoc && nearbyStores.length===0 && (
                <button onClick={()=>setRange(r=>Math.min(r*2,100))} className="btn-outline" style={{ fontSize:12,padding:"7px 16px" }}>
                  Expand to {Math.min(range*2,100)} km →
                </button>
              )}
            </div>

            {/* No results */}
            {displayStores.length===0 && (
              <div style={{ textAlign:"center",padding:"52px 20px",background:T.white,borderRadius:16,border:`1px solid ${T.border}` }}>
                <div style={{ fontSize:52,marginBottom:12,animation:"float 2s ease-in-out infinite" }}>🔍</div>
                <h3 style={{ fontSize:18,fontWeight:600,marginBottom:8 }}>{hasLoc?`No shops within ${range} km`:"No shops match your filters"}</h3>
                <p style={{ fontSize:13.5,color:T.muted,marginBottom:16 }}>
                  {hasLoc?"Try expanding your range or browse all shops.":"Try adjusting your state, city or type filters."}
                </p>
                <div style={{ display:"flex",gap:10,justifyContent:"center",flexWrap:"wrap" }}>
                  {hasLoc&&range<100&&<button className="btn-primary" onClick={()=>setRange(100)}>Show all shops (100 km)</button>}
                  {hasLoc&&<button className="btn-outline" onClick={()=>{}}>Browse without location</button>}
                  {!hasLoc&&<button className="btn-primary" onClick={()=>{setStateF("ALL");setCityF("All");setTypeF("all");setSearch("");}}>Clear Filters</button>}
                </div>
              </div>
            )}

            {/* Shop grid */}
            <div style={{ display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(280px,1fr))",gap:18 }}>
              {displayStores
                .filter(s=>!search||s.name.toLowerCase().includes(search.toLowerCase())||s.city.toLowerCase().includes(search.toLowerCase())||s.tags.some(t=>t.toLowerCase().includes(search.toLowerCase())))
                .map(s=>(
                  <StoreCard key={s.id} store={s} distKm={s.distKm} userLoc={userLoc}/>
              ))}
            </div>
          </>
        )}

        {/* ══ PRODUCTS VIEW ══ */}
        {view==="products" && (
          <>
            {/* Category pills */}
            <div style={{ display:"flex",gap:7,marginBottom:20,overflowX:"auto",paddingBottom:2 }}>
              {PROD_CATS.map(c=>(
                <button key={c} onClick={()=>setCatF(c)} style={{ padding:"8px 18px",borderRadius:20,border:`1px solid ${catF===c?T.lav:T.border}`,background:catF===c?T.lav:T.white,color:catF===c?T.white:T.charcoal,fontSize:13,cursor:"none",fontFamily:"'DM Sans',sans-serif",fontWeight:500,whiteSpace:"nowrap",transition:"all .15s" }}>
                  {c} <span style={{ fontSize:11,opacity:.7 }}>({c==="All"?displayProducts.length:displayProducts.filter(p=>p.category===c).length})</span>
                </button>
              ))}
            </div>

            <div className="reveal" style={{ marginBottom:16 }}>
              <h2 className="serif" style={{ fontSize:26,fontWeight:400 }}>
                {hasLoc
                  ? <><strong style={{ color:T.lav }}>{displayProducts.filter(p=>catF==="All"||p.category===catF).length}</strong> products <em style={{ color:T.lav }}>nearby</em></>
                  : <><em style={{ color:T.lav }}>{catF==="All"?"All":catF}</em> Products</>}
              </h2>
              {hasLoc&&<p style={{ fontSize:12,color:T.muted,marginTop:2 }}>From shops within {range} km of {userLoc.label}</p>}
            </div>

            <div style={{ display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(220px,1fr))",gap:16 }}>
              {displayProducts.filter(p=>catF==="All"||p.category===catF)
                .filter(p=>!search||p.name.toLowerCase().includes(search.toLowerCase()))
                .map((p,i)=>(
                <div key={p.id} className="reveal card" style={{ overflow:"hidden",animationDelay:`${i*.04}s` }}>
                  <div style={{ position:"relative",height:195 }}>
                    <img src={p.img} alt={p.name} style={{ width:"100%",height:"100%",objectFit:"cover",transition:"transform .4s" }} onMouseEnter={e=>e.currentTarget.style.transform="scale(1.06)"} onMouseLeave={e=>e.currentTarget.style.transform="scale(1)"}/>
                    <button style={{ position:"absolute",top:9,right:9,width:28,height:28,borderRadius:"50%",background:"rgba(255,255,255,.9)",border:"none",cursor:"none",fontSize:14,display:"flex",alignItems:"center",justifyContent:"center" }}>🤍</button>
                    {p.discount>0&&<div style={{ position:"absolute",top:9,left:9,background:T.ok,color:"#FFF",fontSize:11,fontWeight:700,padding:"2px 8px",borderRadius:6 }}>{p.discount}% OFF</div>}
                    {p.isBestseller&&<div style={{ position:"absolute",bottom:9,left:9 }}><span className="tag" style={{ background:"#FF6F00EE",color:"#FFF",border:"none" }}>Bestseller</span></div>}
                    {p.isNew&&!p.isBestseller&&<div style={{ position:"absolute",bottom:9,left:9 }}><span className="tag" style={{ background:"#1565C0EE",color:"#FFF",border:"none" }}>New</span></div>}
                    {p.stock<=3&&p.stock>0&&<div style={{ position:"absolute",bottom:9,right:9,background:"rgba(198,40,40,.9)",color:"#FFF",fontSize:10,fontWeight:700,padding:"2px 7px",borderRadius:6 }}>Only {p.stock} left!</div>}
                  </div>
                  <div style={{ padding:"12px" }}>
                    <p style={{ fontSize:11,color:T.muted,marginBottom:2 }}>{p.sub}</p>
                    <h3 style={{ fontSize:13.5,fontWeight:600,marginBottom:3,lineHeight:1.3 }}>{p.name}</h3>
                    {p.store && (
                      <div style={{ display:"flex",alignItems:"center",gap:5,marginBottom:5 }}>
                        <span style={{ fontSize:11,color:T.lav,fontWeight:500 }}>🏪 {p.store.name}</span>
                        {p.store.distKm!=null&&<span style={{ fontSize:10,color:T.muted }}>· {p.store.distKm<1?`${Math.round(p.store.distKm*1000)}m`:`${p.store.distKm.toFixed(1)}km`}</span>}
                      </div>
                    )}
                    {p.rating>0&&<Stars rating={p.rating} count={p.reviews} size={11}/>}
                    <div style={{ display:"flex",alignItems:"center",gap:7,marginTop:6 }}>
                      <span style={{ fontSize:15,fontWeight:700 }}>₹{p.price.toLocaleString()}</span>
                      <span style={{ fontSize:11,color:T.muted,textDecoration:"line-through" }}>₹{p.mrp.toLocaleString()}</span>
                      <span style={{ fontSize:11,fontWeight:600,color:T.ok }}>{p.discount}% off</span>
                    </div>
                    {p.customizable&&<div style={{ fontSize:11,color:T.teal,marginTop:4 }}>✂️ Customizable</div>}
                    <button className="btn-primary" style={{ width:"100%",marginTop:10,padding:"9px",fontSize:12 }}>View Product</button>
                  </div>
                </div>
              ))}
            </div>

            {displayProducts.filter(p=>catF==="All"||p.category===catF).length===0&&(
              <div style={{ textAlign:"center",padding:"52px 20px" }}>
                <div style={{ fontSize:48,marginBottom:12 }}>🛍️</div>
                <p style={{ fontSize:15,color:T.muted }}>{hasLoc?`No ${catF==="All"?"products":catF} found within ${range} km.`:`No ${catF} products yet.`}</p>
                {hasLoc&&<button className="btn-outline" style={{ marginTop:14 }} onClick={()=>setRange(100)}>Expand to 100 km</button>}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

// ══════════════════════════════════════════════════════════════
//  ANIMATED COUNTER
// ══════════════════════════════════════════════════════════════
const Counter = ({ target, suffix="", duration=1500 }) => {
  const [val, setVal] = useState(0);
  const ref = useRef(null);
  useEffect(()=>{
    const io = new IntersectionObserver(entries=>{
      if(!entries[0].isIntersecting) return;
      io.disconnect();
      const start = Date.now();
      const tick = ()=>{
        const p = Math.min((Date.now()-start)/duration, 1);
        const e = 1-Math.pow(1-p,3);
        setVal(Math.round(e*target));
        if(p<1) requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    },{threshold:.5});
    if(ref.current) io.observe(ref.current);
    return ()=>io.disconnect();
  },[target,duration]);
  return <span ref={ref}>{val.toLocaleString()}{suffix}</span>;
};

// ══════════════════════════════════════════════════════════════
//  3D TILT WRAPPER
// ══════════════════════════════════════════════════════════════
const Tilt3D = ({ children, style, intensity=8 }) => {
  const ref = useRef(null);
  const onMove = e => {
    if(!ref.current) return;
    const r = ref.current.getBoundingClientRect();
    const x = (e.clientX - r.left) / r.width - .5;
    const y = (e.clientY - r.top) / r.height - .5;
    ref.current.style.transform = `perspective(700px) rotateY(${x*intensity*2}deg) rotateX(${-y*intensity}deg) translateZ(8px)`;
  };
  const onLeave = () => { if(ref.current) ref.current.style.transform = "perspective(700px) rotateY(0) rotateX(0) translateZ(0)"; };
  return (
    <div ref={ref} onMouseMove={onMove} onMouseLeave={onLeave}
      style={{ transformStyle:"preserve-3d", transition:"transform .35s ease", ...style }}>
      {children}
    </div>
  );
};

// ══════════════════════════════════════════════════════════════
//  PARTICLE BURST
// ══════════════════════════════════════════════════════════════
const useParticles = () => useCallback((x, y, container) => {
  if(!container) return;
  for(let i=0;i<14;i++){
    const p = document.createElement("div");
    const angle = (i/14)*Math.PI*2;
    const dist = 35+Math.random()*45;
    const colors = ["#7B68EE","#D3D3FF","#5A4FCF","#B0A8FF"];
    p.style.cssText = `position:absolute;width:${3+Math.random()*5}px;height:${3+Math.random()*5}px;
      background:${colors[Math.floor(Math.random()*colors.length)]};border-radius:50%;
      left:${x}px;top:${y}px;pointer-events:none;z-index:999;
      --tx:${Math.cos(angle)*dist}px;--ty:${Math.sin(angle)*dist}px;
      animation:particleFly ${.5+Math.random()*.5}s ease-out forwards;`;
    container.appendChild(p);
    setTimeout(()=>p.remove(), 1000);
  }
},[]);

// ══════════════════════════════════════════════════════════════
//  CUSTOMER PROFILE PAGE
// ═════════��════��═══════════════════════════════════════════════
const CustomerProfile = ({ user, setPage, onLogout }) => {
  const [tab, setTab] = useState("overview");
  const [editing, setEditing] = useState(false);
  const [saved, setSaved] = useState("");
  const burstRef = useRef(null);
  const burst = useParticles();

  // Editable profile fields
  const [form, setForm] = useState({
    name: user.name || "",
    email: user.email || "",
    mobile: user.mobile || "",
    dob: user.dob || "",
    gender: user.gender || "",
    city: user.city || "",
    state: user.state || "",
    pincode: user.pincode || "",
    address: user.address || "",
    altMobile: user.altMobile || "",
  });

  // Mock extended data
  const ORDERS = [
    { id:"ORD-001", date:"22 Apr 2025", status:"Delivered", total:6800, items:1, store:"Swarna Kanchi Mall",
      product:"Banarasi Silk Saree", img:"https://images.unsplash.com/photo-1610030469983-98e550d6193c?w=200&q=80",
      rating:null, deliveredOn:"24 Apr 2025", tracking:"TLG8492017", city:"Nalgonda" },
    { id:"ORD-002", date:"15 Apr 2025", status:"Out for Delivery", total:899, items:1, store:"Swarna Kanchi Mall",
      product:"Cotton Anarkali Kurti", img:"https://images.unsplash.com/photo-1614897823648-6b27b9ab19b1?w=200&q=80",
      rating:null, deliveredOn:null, tracking:"TLG8492044", city:"Nalgonda" },
    { id:"ORD-003", date:"02 Apr 2025", status:"Delivered", total:999, items:1, store:"Swarna Kanchi Mall",
      product:"Premium Linen Shirt", img:"https://images.unsplash.com/photo-1598300042247-d088f8ab3a91?w=200&q=80",
      rating:5, deliveredOn:"04 Apr 2025", tracking:"TLG8491878", city:"Nalgonda" },
    { id:"ORD-004", date:"18 Mar 2025", status:"Cancelled", total:16500, items:1, store:"KALKI Hyderabad",
      product:"Embroidered Bridal Lehenga", img:"https://images.unsplash.com/photo-1576566588028-4147f3842f27?w=200&q=80",
      rating:null, deliveredOn:null, tracking:null, city:"Hyderabad" },
  ];

  const WISHLIST = [
    { id:"p1", name:"Banarasi Silk Saree", price:6800, mrp:8500, discount:20, store:"Swarna Kanchi Mall",
      img:"https://images.unsplash.com/photo-1610030469983-98e550d6193c?w=300&q=80", inStock:true },
    { id:"p4", name:"Embroidered Bridal Lehenga", price:16500, mrp:22000, discount:25, store:"KALKI Hyderabad",
      img:"https://images.unsplash.com/photo-1576566588028-4147f3842f27?w=300&q=80", inStock:true },
    { id:"p5", name:"Kancheepuram Silk Saree", price:12500, mrp:18000, discount:31, store:"Thottathil Silks",
      img:"https://images.unsplash.com/photo-1583391733956-6c78276477e2?w=300&q=80", inStock:false },
  ];

  const ADDRESSES = [
    { id:"a1", label:"Home", name:user.name, mobile:user.mobile, line1:"12-4, Nagarjuna Colony", line2:"Near NGO Colony", city:"Nalgonda", state:"Telangana", pin:"508001", isDefault:true },
    { id:"a2", label:"Work", name:user.name, mobile:user.mobile, line1:"3rd Floor, Cyber Towers", line2:"Madhapur", city:"Hyderabad", state:"Telangana", pin:"500081", isDefault:false },
  ];

  const SAVED_STORES = [
    { id:"s1", name:"Swarna Kanchi Mall", city:"Nalgonda", rating:4.7, img:"https://images.unsplash.com/photo-1594938298603-c8148c4b4157?w=300&q=80", type:"store" },
    { id:"s2", name:"KALKI Hyderabad",    city:"Hyderabad", rating:4.9, img:"https://images.unsplash.com/photo-1583391733956-6c78276477e2?w=300&q=80", type:"boutique" },
    { id:"s7", name:"Fashion Bug Kochi",  city:"Kochi",     rating:4.9, img:"https://images.unsplash.com/photo-1624378439575-d8705ad7ae80?w=300&q=80", type:"store" },
  ];

  const REVIEWS = [
    { id:"r1", product:"Premium Linen Shirt", store:"Swarna Kanchi Mall", rating:5, date:"04 Apr 2025",
      text:"Best linen shirt ever. So comfortable for office wear. Fabric quality is excellent.",
      img:"https://images.unsplash.com/photo-1598300042247-d088f8ab3a91?w=200&q=80", helpful:12 },
    { id:"r2", product:"Banarasi Silk Saree", store:"Swarna Kanchi Mall", rating:5, date:"25 Apr 2025",
      text:"Absolutely stunning saree. The zari work is very detailed. Wore it for Akshaya Tritiya — got so many compliments!",
      img:"https://images.unsplash.com/photo-1610030469983-98e550d6193c?w=200&q=80", helpful:8 },
  ];

  const COUPONS = [
    { code:"WELCOME50", desc:"50% off on first order above ₹999", expiry:"30 Jun 2025", type:"percentage", value:50, minOrder:999, active:true },
    { code:"LOCAL200",  desc:"₹200 off — Support Local Stores", expiry:"31 May 2025", type:"flat", value:200, minOrder:500, active:true },
    { code:"NALGONDA10",desc:"10% off on Nalgonda shops", expiry:"31 Dec 2025", type:"percentage", value:10, minOrder:0, active:true },
    { code:"BRIDAL15",  desc:"15% off on Bridal collection", expiry:"15 May 2025", type:"percentage", value:15, minOrder:5000, active:false },
  ];

  const NOTIFICATIONS = [
    { id:"n1", type:"order", icon:"📦", title:"Order Delivered!", msg:"Your Banarasi Silk Saree has been delivered.", time:"2h ago", read:false },
    { id:"n2", type:"promo", icon:"🎁", title:"₹200 Coupon Added!", msg:"We added a coupon to your wallet. Use code LOCAL200.", time:"1d ago", read:false },
    { id:"n3", type:"store", icon:"🏪", title:"New Arrival at Swarna Kanchi", msg:"5 new Kurtis just added to their collection.", time:"2d ago", read:true },
    { id:"n4", type:"promo", icon:"💜", title:"Flash Sale Tomorrow!", msg:"50% off on all Lehengas — Saturday only.", time:"3d ago", read:true },
  ];

  const [notifications, setNotifications] = useState(NOTIFICATIONS);
  const unread = notifications.filter(n=>!n.read).length;
  const [wishlist, setWishlist] = useState(WISHLIST);
  const [addresses, setAddresses] = useState(ADDRESSES);
  const [copiedCode, setCopiedCode] = useState("");

  const statusColor = { Delivered:T.ok, "Out for Delivery":"#E65100", Cancelled:T.err, Processing:T.lav, Confirmed:T.blue };
  const statusIcon  = { Delivered:"✅", "Out for Delivery":"🚚", Cancelled:"❌", Processing:"⏳", Confirmed:"✔️" };

  const save = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    burst(e.clientX-r.left, e.clientY-r.top, burstRef.current);
    Object.assign(user, form);
    setEditing(false);
    setSaved("Profile updated successfully!");
    setTimeout(()=>setSaved(""),3000);
  };

  const copyCode = (code) => {
    navigator.clipboard?.writeText(code).catch(()=>{});
    setCopiedCode(code);
    setTimeout(()=>setCopiedCode(""),2000);
  };

  const markAllRead = () => setNotifications(n=>n.map(x=>({...x,read:true})));
  const removeWish = (id) => setWishlist(w=>w.filter(p=>p.id!==id));
  const setDefault = (id) => setAddresses(a=>a.map(x=>({...x,isDefault:x.id===id})));

  const memberSince = user.joined || "Apr 2025";
  const totalSpent = ORDERS.filter(o=>o.status==="Delivered").reduce((s,o)=>s+o.total,0);
  const loyalty = totalSpent > 20000 ? "Gold" : totalSpent > 5000 ? "Silver" : "Bronze";
  const loyaltyColor = { Gold:"#F9A825", Silver:"#78909C", Bronze:"#8D6E63" };
  const loyaltyNext  = { Gold: null, Silver:20000, Bronze:5000 };
  const loyaltyPct   = loyalty==="Bronze" ? Math.min((totalSpent/5000)*100,100) : loyalty==="Silver" ? Math.min(((totalSpent-5000)/15000)*100,100) : 100;

  const TABS = [
    {id:"overview",  label:"Overview",    icon:"🏠"},
    {id:"orders",    label:"My Orders",   icon:"📦", badge:ORDERS.filter(o=>o.status!=="Delivered"&&o.status!=="Cancelled").length||null},
    {id:"wishlist",  label:"Wishlist",    icon:"❤️", badge:wishlist.length},
    {id:"addresses", label:"Addresses",   icon:"📍"},
    {id:"reviews",   label:"My Reviews",  icon:"⭐"},
    {id:"coupons",   label:"Coupons",     icon:"🎟️", badge:COUPONS.filter(c=>c.active).length},
    {id:"stores",    label:"Saved Shops", icon:"🏪"},
    {id:"notifs",    label:"Notifications",icon:"🔔", badge:unread||null},
    {id:"settings",  label:"Settings",    icon:"⚙️"},
  ];

  // Scroll reveal
  useEffect(()=>{
    const els = document.querySelectorAll(".reveal");
    const io = new IntersectionObserver(e=>e.forEach(en=>{if(en.isIntersecting)en.target.classList.add("on");}),{threshold:.08});
    els.forEach(el=>io.observe(el));
    return ()=>io.disconnect();
  },[tab]);

  return (
    <div style={{ maxWidth:1200, margin:"0 auto", padding:"24px" }}>

      {/* ── TOP PROFILE HERO ── */}
      <div className="grad-bg tilt-in" style={{ borderRadius:24, padding:"32px", marginBottom:24, position:"relative", overflow:"hidden", minHeight:200 }}>
        {/* Background blobs */}
        <div style={{ position:"absolute",width:280,height:280,borderRadius:"50%",background:"#7B68EE",top:-80,right:-60,opacity:.12,filter:"blur(40px)" }}/>
        <div style={{ position:"absolute",width:200,height:200,borderRadius:"50%",background:"#D3D3FF",bottom:-60,left:80,opacity:.08,filter:"blur(35px)" }}/>

        <div style={{ position:"relative",zIndex:1,display:"flex",gap:24,alignItems:"center",flexWrap:"wrap" }}>
          {/* Avatar with animated ring */}
          <div style={{ position:"relative" }}>
            <div className="avatar-ring">
              <div className="avatar-ring-inner" style={{ width:90,height:90,borderRadius:"50%",background:`linear-gradient(135deg,${T.lav},${T.lavDark})`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:36,fontWeight:700,color:"#FFF",boxShadow:`0 8px 32px ${T.lav}60` }}>
                {user.name?.[0]?.toUpperCase()||"U"}
              </div>
            </div>
            {/* Online dot */}
            <div style={{ position:"absolute",bottom:4,right:4,width:14,height:14,borderRadius:"50%",background:T.ok,border:"2px solid white",boxShadow:"0 0 8px #2E7D3280" }}/>
          </div>

          {/* User info */}
          <div style={{ flex:1 }}>
            <div style={{ display:"flex",alignItems:"center",gap:10,flexWrap:"wrap",marginBottom:6 }}>
              <h1 className="serif" style={{ fontSize:28,fontWeight:400,color:"#FFFFFF",lineHeight:1 }}>{user.name}</h1>
              <div className="badge-pop" style={{ background:`${loyaltyColor[loyalty]}22`,border:`1.5px solid ${loyaltyColor[loyalty]}`,borderRadius:20,padding:"3px 12px",display:"flex",alignItems:"center",gap:5 }}>
                <span style={{ fontSize:14 }}>{loyalty==="Gold"?"🥇":loyalty==="Silver"?"🥈":"🥉"}</span>
                <span style={{ fontSize:12,fontWeight:700,color:loyaltyColor[loyalty] }}>{loyalty} Member</span>
              </div>
              {user.verified&&<span style={{ fontSize:13,color:"#90CAF9" }}>✅ Verified</span>}
            </div>
            <p style={{ color:"#B0B0D8",fontSize:13,marginBottom:10 }}>
              📍 {user.city||"Location not set"} &nbsp;·&nbsp; 📅 Member since {memberSince} &nbsp;·&nbsp; 📞 +91 {user.mobile}
            </p>
            {/* Loyalty progress */}
            {loyalty!=="Gold" && (
              <div style={{ maxWidth:320 }}>
                <div style={{ display:"flex",justifyContent:"space-between",marginBottom:4 }}>
                  <span style={{ fontSize:11,color:"#D3D3FF" }}>Next: {loyalty==="Bronze"?"Silver":"Gold"}</span>
                  <span style={{ fontSize:11,color:"#D3D3FF" }}>₹{totalSpent.toLocaleString()} / ₹{(loyaltyNext[loyalty]||0).toLocaleString()}</span>
                </div>
                <div className="progress-anim">
                  <div className="progress-anim-fill" style={{ "--pct":`${loyaltyPct}%` }}/>
                </div>
              </div>
            )}
          </div>

          {/* Quick stats */}
          <div className="hide-mob" style={{ display:"flex",gap:16 }}>
            {[
              ["📦", ORDERS.length, "Orders"],
              ["❤️", wishlist.length, "Wishlist"],
              ["⭐", REVIEWS.length, "Reviews"],
              ["💰", `₹${(totalSpent/1000).toFixed(0)}k`, "Spent"],
            ].map(([ic,v,l])=>(
              <div key={l} style={{ textAlign:"center",background:"rgba(255,255,255,.08)",backdropFilter:"blur(10px)",borderRadius:12,padding:"12px 16px",border:"1px solid rgba(255,255,255,.12)" }}>
                <div style={{ fontSize:20 }}>{ic}</div>
                <div className="serif" style={{ fontSize:20,fontWeight:600,color:"#D3D3FF",lineHeight:1.2 }}>{v}</div>
                <div style={{ fontSize:10,color:"#9090C0",marginTop:2 }}>{l}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {saved && (
        <div className="pop-in" style={{ background:"#E8F5E9",border:"1px solid #A5D6A7",borderRadius:10,padding:"11px 16px",marginBottom:16,display:"flex",gap:8,alignItems:"center" }}>
          <span>✅</span><span style={{ fontSize:13,color:T.ok,fontWeight:500 }}>{saved}</span>
        </div>
      )}

      <div style={{ display:"flex",gap:20,alignItems:"flex-start",flexWrap:"wrap" }} ref={burstRef}>

        {/* ── SIDEBAR TABS ── */}
        <div style={{ width:220,flexShrink:0,background:T.white,borderRadius:16,border:`1px solid ${T.border}`,padding:"12px",position:"sticky",top:80 }}>
          {TABS.map((t,i)=>(
            <button key={t.id} onClick={()=>setTab(t.id)}
              style={{ display:"flex",width:"100%",alignItems:"center",gap:10,padding:"10px 12px",borderRadius:10,border:"none",background:tab===t.id?T.lavPale:"transparent",color:tab===t.id?T.lav:T.charcoal,fontFamily:"'DM Sans',sans-serif",fontSize:13,fontWeight:tab===t.id?600:400,cursor:"pointer",transition:"all .18s",marginBottom:2,textAlign:"left",position:"relative",animationDelay:`${i*.04}s` }}
              className="walk-in"
              onMouseEnter={e=>{if(tab!==t.id){e.currentTarget.style.background=T.offWhite;}}}
              onMouseLeave={e=>{if(tab!==t.id){e.currentTarget.style.background="transparent";}}}>
              <span style={{ fontSize:16,transition:"transform .2s" }}>{t.icon}</span>
              <span style={{ flex:1 }}>{t.label}</span>
              {t.badge&&<span style={{ background:T.lav,color:"#FFF",borderRadius:20,padding:"1px 7px",fontSize:10,fontWeight:700 }}>{t.badge}</span>}
              {tab===t.id&&<span style={{ position:"absolute",left:0,top:"50%",transform:"translateY(-50%)",width:3,height:20,background:T.lav,borderRadius:"0 3px 3px 0" }}/>}
            </button>
          ))}
          <div style={{ borderTop:`1px solid ${T.border}`,marginTop:8,paddingTop:8 }}>
            <button onClick={onLogout} style={{ display:"flex",width:"100%",alignItems:"center",gap:10,padding:"10px 12px",borderRadius:10,border:"none",background:"transparent",color:T.err,fontFamily:"'DM Sans',sans-serif",fontSize:13,fontWeight:400,cursor:"pointer" }}
              onMouseEnter={e=>e.currentTarget.style.background="#FFEBEE"} onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
              <span style={{ fontSize:16 }}>🚪</span> Sign Out
            </button>
          </div>
        </div>

        {/* ── MAIN CONTENT ── */}
        <div style={{ flex:1,minWidth:0 }} key={tab}>

          {/* ════ OVERVIEW ════ */}
          {tab==="overview" && (
            <div>
              {/* 3D stat cards */}
              <div style={{ display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(160px,1fr))",gap:14,marginBottom:22 }}>
                {[
                  { ic:"📦",v:ORDERS.length,l:"Total Orders",color:T.lav,sub:"2 this month" },
                  { ic:"❤️",v:wishlist.length,l:"Wishlist Items",color:"#E91E63",sub:`₹${wishlist.reduce((s,p)=>s+p.price,0).toLocaleString()} value` },
                  { ic:"💰",v:`₹${totalSpent.toLocaleString()}`,l:"Total Spent",color:T.ok,sub:"All time" },
                  { ic:"⭐",v:REVIEWS.length,l:"Reviews Given",color:"#F9A825",sub:"Avg 5.0 ★" },
                  { ic:"🎟️",v:COUPONS.filter(c=>c.active).length,l:"Active Coupons",color:T.teal,sub:"Use them now!" },
                ].map((s,i)=>(
                  <Tilt3D key={s.l} style={{ borderRadius:14 }}>
                    <div className="card reveal" style={{ padding:"18px",overflow:"hidden",position:"relative",animationDelay:`${i*.08}s` }}>
                      <div style={{ position:"absolute",top:-15,right:-15,fontSize:56,opacity:.07,transform:"rotate(-15deg)" }}>{s.ic}</div>
                      <div style={{ fontSize:22,marginBottom:8 }}>{s.ic}</div>
                      <div className="serif" style={{ fontSize:26,fontWeight:600,color:s.color }}><Counter target={typeof s.v==="number"?s.v:0} duration={1200}/>{typeof s.v==="string"?s.v:""}</div>
                      <div style={{ fontSize:12.5,fontWeight:600,color:T.charcoal,marginTop:2 }}>{s.l}</div>
                      <div style={{ fontSize:11,color:T.muted,marginTop:2 }}>{s.sub}</div>
                    </div>
                  </Tilt3D>
                ))}
              </div>

              {/* Recent orders preview */}
              <div className="card reveal" style={{ padding:"20px",marginBottom:18 }}>
                <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16 }}>
                  <h2 className="serif" style={{ fontSize:22,fontWeight:400 }}>Recent <em style={{ color:T.lav }}>Orders</em></h2>
                  <button className="btn-ghost" onClick={()=>setTab("orders")} style={{ fontSize:13 }}>View All →</button>
                </div>
                {ORDERS.slice(0,2).map((o,i)=>(
                  <div key={o.id} className="card3d reveal" style={{ display:"flex",gap:14,padding:"13px",borderRadius:12,border:`1px solid ${T.border}`,marginBottom:10,background:T.white,animationDelay:`${i*.1}s` }}>
                    <img src={o.img} alt={o.product} style={{ width:60,height:70,borderRadius:9,objectFit:"cover",flexShrink:0 }}/>
                    <div style={{ flex:1 }}>
                      <div style={{ fontSize:12,color:T.muted,marginBottom:2 }}>{o.store} · {o.date}</div>
                      <div style={{ fontSize:14,fontWeight:600,marginBottom:4 }}>{o.product}</div>
                      <div style={{ display:"flex",alignItems:"center",gap:8 }}>
                        <span style={{ fontSize:15,fontWeight:700 }}>₹{o.total.toLocaleString()}</span>
                        <span style={{ background:`${statusColor[o.status]}18`,color:statusColor[o.status],borderRadius:20,padding:"2px 9px",fontSize:11,fontWeight:600 }}>{statusIcon[o.status]} {o.status}</span>
                      </div>
                    </div>
                    <button className="btn-outline" style={{ padding:"7px 14px",fontSize:12,alignSelf:"center" }} onClick={()=>setTab("orders")}>Track</button>
                  </div>
                ))}
              </div>

              {/* L4U local stores impact */}
              <div className="card reveal" style={{ padding:"20px",background:"linear-gradient(135deg,#F3F2FF,#EEEEFF)",border:`1.5px solid ${T.border}`,position:"relative",overflow:"hidden" }}>
                <div style={{ position:"absolute",top:-20,right:-20,fontSize:80,opacity:.06 }}>🏪</div>
                <h3 className="serif" style={{ fontSize:20,fontWeight:400,marginBottom:8 }}>Your <em style={{ color:T.lav }}>Local Impact</em></h3>
                <p style={{ fontSize:13,color:T.muted,lineHeight:1.65,marginBottom:14 }}>
                  You've supported <strong style={{ color:T.lav }}>2 local shops</strong> in South India. Your purchases directly help offline stores compete with Amazon & Flipkart.
                </p>
                <div style={{ display:"flex",gap:12,flexWrap:"wrap" }}>
                  {[["🏪","2 local shops","supported"],["🛍️",`₹${totalSpent.toLocaleString()}`,"kept local"],["🗺️","Nalgonda","your impact zone"]].map(([ic,v,l])=>(
                    <div key={l} style={{ background:"rgba(255,255,255,.7)",borderRadius:10,padding:"10px 14px",textAlign:"center",flex:1,minWidth:100 }}>
                      <div style={{ fontSize:20 }}>{ic}</div>
                      <div style={{ fontSize:14,fontWeight:700,color:T.lav }}>{v}</div>
                      <div style={{ fontSize:10,color:T.muted }}>{l}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* ════ MY ORDERS ════ */}
          {tab==="orders" && (
            <div>
              <h2 className="serif tilt-in" style={{ fontSize:28,fontWeight:400,marginBottom:18 }}>My <em style={{ color:T.lav }}>Orders</em></h2>
              {/* Order filter */}
              <div style={{ display:"flex",gap:7,marginBottom:16,flexWrap:"wrap" }}>
                {["All","Delivered","Out for Delivery","Cancelled"].map(s=>(
                  <button key={s} style={{ padding:"7px 14px",borderRadius:20,border:`1px solid ${T.border}`,background:T.white,color:T.charcoal,fontSize:12.5,cursor:"pointer",fontFamily:"'DM Sans',sans-serif",transition:"all .15s" }}
                    onMouseEnter={e=>{e.currentTarget.style.borderColor=T.lav;e.currentTarget.style.color=T.lav;}}
                    onMouseLeave={e=>{e.currentTarget.style.borderColor=T.border;e.currentTarget.style.color=T.charcoal;}}>
                    {statusIcon[s]||"📋"} {s}
                  </button>
                ))}
              </div>
              {ORDERS.map((o,i)=>(
                <Tilt3D key={o.id} style={{ marginBottom:14 }}>
                  <div className="card reveal" style={{ padding:"18px",animationDelay:`${i*.08}s` }}>
                    <div style={{ display:"flex",justifyContent:"space-between",alignItems:"flex-start",flexWrap:"wrap",gap:10,marginBottom:14 }}>
                      <div>
                        <span style={{ fontSize:12,fontWeight:600,color:T.muted }}>{o.id}</span>
                        <span style={{ fontSize:12,color:T.muted,marginLeft:10 }}>Placed: {o.date}</span>
                      </div>
                      <span style={{ background:`${statusColor[o.status]}18`,color:statusColor[o.status],borderRadius:20,padding:"4px 12px",fontSize:12.5,fontWeight:700 }}>{statusIcon[o.status]} {o.status}</span>
                    </div>
                    <div style={{ display:"flex",gap:14,alignItems:"flex-start" }}>
                      <img src={o.img} alt={o.product} style={{ width:75,height:85,borderRadius:10,objectFit:"cover",flexShrink:0 }}/>
                      <div style={{ flex:1 }}>
                        <div style={{ fontSize:15,fontWeight:600,marginBottom:3 }}>{o.product}</div>
                        <div style={{ fontSize:12.5,color:T.lav,marginBottom:5 }}>🏪 {o.store} · {o.city}</div>
                        <div style={{ fontSize:14,fontWeight:700,color:T.ink }}>₹{o.total.toLocaleString()}</div>
                        {o.deliveredOn&&<div style={{ fontSize:11.5,color:T.ok,marginTop:3 }}>✅ Delivered on {o.deliveredOn}</div>}
                        {o.tracking&&<div style={{ fontSize:11,color:T.muted,marginTop:2 }}>🔍 Tracking: {o.tracking}</div>}
                      </div>
                    </div>
                    {/* Order progress bar for active orders */}
                    {o.status==="Out for Delivery" && (
                      <div style={{ marginTop:14 }}>
                        <div style={{ display:"flex",justifyContent:"space-between",marginBottom:5 }}>
                          {["Placed","Packed","Shipped","Out for Delivery","Delivered"].map((s,si)=>(
                            <div key={s} style={{ textAlign:"center",flex:1 }}>
                              <div style={{ width:22,height:22,borderRadius:"50%",background:si<=3?T.lav:T.border,margin:"0 auto 3px",display:"flex",alignItems:"center",justifyContent:"center",fontSize:10,color:"#FFF",transition:"all .3s",boxShadow:si===3?`0 0 10px ${T.lav}80`:"none" }}>
                                {si<=3?"✓":""}
                              </div>
                              <div style={{ fontSize:9,color:si<=3?T.lav:T.muted,fontWeight:si===3?700:400 }}>{s}</div>
                            </div>
                          ))}
                        </div>
                        <div className="progress-anim"><div className="progress-anim-fill" style={{ "--pct":"80%" }}/></div>
                      </div>
                    )}
                    <div style={{ display:"flex",gap:8,marginTop:14,flexWrap:"wrap" }}>
                      {o.status==="Delivered"&&!o.rating&&<button className="btn-primary" style={{ padding:"8px 16px",fontSize:12 }}>⭐ Rate & Review</button>}
                      {o.status==="Delivered"&&<button className="btn-outline" style={{ padding:"7px 14px",fontSize:12 }}>🔄 Reorder</button>}
                      {o.status==="Delivered"&&<button className="btn-outline" style={{ padding:"7px 14px",fontSize:12 }}>🔧 Return / Exchange</button>}
                      {o.status==="Delivered"&&<button className="btn-outline" style={{ padding:"7px 14px",fontSize:12 }}>📄 Invoice</button>}
                      {(o.status==="Confirmed"||o.status==="Processing")&&<button style={{ padding:"7px 14px",fontSize:12,borderRadius:10,border:`1px solid ${T.err}`,color:T.err,background:"#FFEBEE",cursor:"pointer",fontFamily:"'DM Sans',sans-serif" }}>❌ Cancel Order</button>}
                      <button className="btn-outline" style={{ padding:"7px 14px",fontSize:12 }}>💬 Chat with Store</button>
                    </div>
                  </div>
                </Tilt3D>
              ))}
            </div>
          )}

          {/* ════ WISHLIST ════ */}
          {tab==="wishlist" && (
            <div>
              <h2 className="serif tilt-in" style={{ fontSize:28,fontWeight:400,marginBottom:18 }}>My <em style={{ color:T.lav }}>Wishlist</em> <span className="heart-beat" style={{ display:"inline-block" }}>❤️</span></h2>
              {wishlist.length===0 ? (
                <div style={{ textAlign:"center",padding:"52px",background:T.white,borderRadius:16,border:`1px solid ${T.border}` }}>
                  <div style={{ fontSize:52,marginBottom:12 }}>🤍</div>
                  <p style={{ fontSize:16,fontWeight:500,marginBottom:6 }}>Your wishlist is empty</p>
                  <p style={{ color:T.muted,fontSize:13,marginBottom:16 }}>Save products you love to find them easily later.</p>
                  <button className="btn-primary" onClick={()=>setPage("home")}>Browse Products</button>
                </div>
              ) : (
                <div style={{ display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(220px,1fr))",gap:16 }}>
                  {wishlist.map((p,i)=>(
                    <Tilt3D key={p.id} style={{ borderRadius:14 }}>
                      <div className="card reveal card-flip" style={{ overflow:"hidden",animationDelay:`${i*.07}s`,position:"relative" }}>
                        <div style={{ position:"relative",height:190 }}>
                          <img src={p.img} alt={p.name} style={{ width:"100%",height:"100%",objectFit:"cover",transition:"transform .5s" }} onMouseEnter={e=>e.currentTarget.style.transform="scale(1.07)"} onMouseLeave={e=>e.currentTarget.style.transform="scale(1)"}/>
                          <button onClick={()=>removeWish(p.id)} style={{ position:"absolute",top:9,right:9,width:30,height:30,borderRadius:"50%",background:"rgba(255,255,255,.92)",border:"none",cursor:"pointer",fontSize:14,display:"flex",alignItems:"center",justifyContent:"center" }}>❤️</button>
                          {!p.inStock&&<div style={{ position:"absolute",inset:0,background:"rgba(0,0,0,.5)",display:"flex",alignItems:"center",justifyContent:"center" }}><span style={{ color:"#FFF",fontWeight:700,fontSize:13 }}>Out of Stock</span></div>}
                          {p.discount>0&&<div style={{ position:"absolute",top:9,left:9,background:T.ok,color:"#FFF",fontSize:11,fontWeight:700,padding:"2px 8px",borderRadius:6 }}>{p.discount}% OFF</div>}
                        </div>
                        <div style={{ padding:"12px" }}>
                          <p style={{ fontSize:11,color:T.lav,marginBottom:2 }}>🏪 {p.store}</p>
                          <h3 style={{ fontSize:13.5,fontWeight:600,marginBottom:5 }}>{p.name}</h3>
                          <div style={{ display:"flex",gap:7,alignItems:"baseline",marginBottom:10 }}>
                            <span style={{ fontSize:16,fontWeight:700 }}>₹{p.price.toLocaleString()}</span>
                            <span style={{ fontSize:11,color:T.muted,textDecoration:"line-through" }}>₹{p.mrp.toLocaleString()}</span>
                          </div>
                          <div style={{ display:"flex",gap:7 }}>
                            <button className="btn-primary" style={{ flex:1,padding:"8px",fontSize:12 }} disabled={!p.inStock}>🛒 Add to Cart</button>
                            <button className="btn-outline" style={{ padding:"8px 10px",fontSize:12 }}>💬</button>
                          </div>
                        </div>
                      </div>
                    </Tilt3D>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ════ ADDRESSES ════ */}
          {tab==="addresses" && (
            <div>
              <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:18 }}>
                <h2 className="serif tilt-in" style={{ fontSize:28,fontWeight:400 }}>My <em style={{ color:T.lav }}>Addresses</em></h2>
                <button className="btn-primary" style={{ padding:"9px 18px",fontSize:13 }}>+ Add Address</button>
              </div>
              <div style={{ display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(280px,1fr))",gap:16 }}>
                {addresses.map((a,i)=>(
                  <Tilt3D key={a.id} style={{ borderRadius:14 }}>
                    <div className="card reveal" style={{ padding:"18px",border:`2px solid ${a.isDefault?T.lav:T.border}`,animationDelay:`${i*.1}s`,position:"relative",background:a.isDefault?T.lavPale:T.white }}>
                      {a.isDefault&&<div style={{ position:"absolute",top:-1,right:14,background:T.lav,color:"#FFF",fontSize:10,fontWeight:700,padding:"2px 10px",borderRadius:"0 0 8px 8px" }}>DEFAULT</div>}
                      <div style={{ display:"flex",alignItems:"center",gap:8,marginBottom:10 }}>
                        <span style={{ fontSize:22 }}>{a.label==="Home"?"🏠":"🏢"}</span>
                        <span style={{ fontSize:14,fontWeight:700,color:T.charcoal }}>{a.label}</span>
                      </div>
                      <p style={{ fontSize:13.5,fontWeight:600,marginBottom:3 }}>{a.name}</p>
                      <p style={{ fontSize:13,color:T.muted,lineHeight:1.55 }}>
                        {a.line1}{a.line2?`, ${a.line2}`:""}<br/>{a.city}, {a.state} — {a.pin}<br/>📞 {a.mobile}
                      </p>
                      <div style={{ display:"flex",gap:8,marginTop:13 }}>
                        <button className="btn-outline" style={{ flex:1,padding:"7px",fontSize:12 }}>✏️ Edit</button>
                        {!a.isDefault&&<button className="btn-outline" style={{ flex:1,padding:"7px",fontSize:12 }} onClick={()=>setDefault(a.id)}>Set Default</button>}
                        {!a.isDefault&&<button style={{ padding:"7px 12px",borderRadius:9,border:`1px solid ${T.border}`,background:T.white,color:T.err,cursor:"pointer",fontSize:12,fontFamily:"'DM Sans',sans-serif" }}>🗑️</button>}
                      </div>
                    </div>
                  </Tilt3D>
                ))}
                {/* Add address card */}
                <div className="card reveal" style={{ padding:"24px",border:`2px dashed ${T.border}`,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:10,cursor:"pointer",minHeight:180,background:"transparent",transition:"all .2s" }}
                  onMouseEnter={e=>{e.currentTarget.style.borderColor=T.lav;e.currentTarget.style.background=T.lavPale;}}
                  onMouseLeave={e=>{e.currentTarget.style.borderColor=T.border;e.currentTarget.style.background="transparent";}}>
                  <div style={{ fontSize:32,color:T.lavMid }}>+</div>
                  <p style={{ fontSize:13.5,fontWeight:600,color:T.muted }}>Add New Address</p>
                  <p style={{ fontSize:12,color:T.muted,textAlign:"center" }}>Home, work, or any delivery address</p>
                </div>
              </div>
            </div>
          )}

          {/* ════ MY REVIEWS ════ */}
          {tab==="reviews" && (
            <div>
              <h2 className="serif tilt-in" style={{ fontSize:28,fontWeight:400,marginBottom:18 }}>My <em style={{ color:T.lav }}>Reviews</em></h2>
              {REVIEWS.length===0 ? (
                <div style={{ textAlign:"center",padding:"52px",background:T.white,borderRadius:16,border:`1px solid ${T.border}` }}>
                  <div style={{ fontSize:48,marginBottom:12 }}>⭐</div>
                  <p style={{ fontSize:16,fontWeight:500 }}>No reviews yet</p>
                  <p style={{ color:T.muted,fontSize:13,marginTop:6 }}>Rate your purchased products to help other shoppers.</p>
                </div>
              ) : REVIEWS.map((r,i)=>(
                <Tilt3D key={r.id} style={{ marginBottom:14 }}>
                  <div className="card reveal" style={{ padding:"18px",animationDelay:`${i*.1}s` }}>
                    <div style={{ display:"flex",gap:14 }}>
                      <img src={r.img} alt={r.product} style={{ width:64,height:72,borderRadius:10,objectFit:"cover",flexShrink:0 }}/>
                      <div style={{ flex:1 }}>
                        <div style={{ display:"flex",justifyContent:"space-between",flexWrap:"wrap",gap:6,marginBottom:5 }}>
                          <div>
                            <div style={{ fontSize:14.5,fontWeight:600 }}>{r.product}</div>
                            <div style={{ fontSize:12,color:T.lav }}>🏪 {r.store}</div>
                          </div>
                          <span style={{ fontSize:11.5,color:T.muted }}>{r.date}</span>
                        </div>
                        <div style={{ display:"flex",gap:2,marginBottom:7 }}>
                          {[1,2,3,4,5].map(s=><span key={s} style={{ fontSize:16,color:s<=r.rating?T.lav:T.border }}>★</span>)}
                          <span style={{ fontSize:12,color:T.muted,marginLeft:5 }}>{r.rating}/5</span>
                        </div>
                        <p style={{ fontSize:13.5,color:T.charcoal,lineHeight:1.6,marginBottom:8 }}>"{r.text}"</p>
                        <div style={{ display:"flex",gap:10,alignItems:"center" }}>
                          <span style={{ fontSize:12,color:T.muted }}>👍 {r.helpful} people found this helpful</span>
                          <button className="btn-ghost" style={{ fontSize:12 }}>✏️ Edit</button>
                        </div>
                      </div>
                    </div>
                  </div>
                </Tilt3D>
              ))}
              {/* Pending reviews */}
              <div className="card reveal" style={{ padding:"18px",border:`2px dashed ${T.border}`,marginTop:8 }}>
                <h3 style={{ fontSize:15,fontWeight:600,marginBottom:12,color:T.charcoal }}>⏳ Pending Reviews</h3>
                <p style={{ fontSize:13,color:T.muted,marginBottom:12 }}>You have {ORDERS.filter(o=>o.status==="Delivered"&&!o.rating).length} delivered items waiting for your review.</p>
                {ORDERS.filter(o=>o.status==="Delivered"&&!o.rating).map(o=>(
                  <div key={o.id} style={{ display:"flex",gap:12,alignItems:"center",marginBottom:10,padding:"10px",background:T.offWhite,borderRadius:10 }}>
                    <img src={o.img} alt={o.product} style={{ width:44,height:50,borderRadius:7,objectFit:"cover" }}/>
                    <div style={{ flex:1 }}>
                      <div style={{ fontSize:13,fontWeight:600 }}>{o.product}</div>
                      <div style={{ fontSize:11.5,color:T.muted }}>Delivered {o.deliveredOn}</div>
                    </div>
                    <button className="btn-primary" style={{ padding:"7px 14px",fontSize:12 }}>Rate Now ★</button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ════ COUPONS ════ */}
          {tab==="coupons" && (
            <div>
              <h2 className="serif tilt-in" style={{ fontSize:28,fontWeight:400,marginBottom:6 }}>My <em style={{ color:T.lav }}>Coupons</em> & Offers</h2>
              <p style={{ fontSize:13,color:T.muted,marginBottom:18 }}>Exclusive deals for L4U members — supporting local shops!</p>
              <div style={{ display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(300px,1fr))",gap:14 }}>
                {COUPONS.map((c,i)=>(
                  <Tilt3D key={c.code} style={{ borderRadius:16 }}>
                    <div className="reveal card-flip" style={{ borderRadius:16,overflow:"hidden",border:`2px solid ${c.active?T.lav:T.border}`,opacity:c.active?1:.55,animationDelay:`${i*.09}s` }}>
                      {/* Coupon header */}
                      <div style={{ background:c.active?`linear-gradient(135deg,${T.lav},${T.lavDark})`:"#B0B0C8",padding:"16px 18px",position:"relative",overflow:"hidden" }}>
                        <div style={{ position:"absolute",top:-10,right:-10,width:60,height:60,borderRadius:"50%",background:"rgba(255,255,255,.08)" }}/>
                        <div style={{ display:"flex",justifyContent:"space-between",alignItems:"flex-start" }}>
                          <div>
                            <div style={{ fontSize:11,color:"rgba(255,255,255,.7)",letterSpacing:".06em",textTransform:"uppercase",marginBottom:4 }}>Coupon Code</div>
                            <div style={{ fontSize:22,fontWeight:800,color:"#FFF",letterSpacing:".1em",fontFamily:"monospace" }}>{c.code}</div>
                          </div>
                          <div style={{ textAlign:"right" }}>
                            <div style={{ fontSize:24,fontWeight:800,color:"#FFF" }}>{c.type==="flat"?`₹${c.value}`:c.value+"%"}</div>
                            <div style={{ fontSize:11,color:"rgba(255,255,255,.7)" }}>OFF</div>
                          </div>
                        </div>
                      </div>
                      {/* Coupon body */}
                      <div style={{ padding:"14px 18px",background:T.white }}>
                        {/* Dotted divider */}
                        <div style={{ borderTop:`2px dashed ${T.border}`,margin:"0 -18px 12px",position:"relative" }}>
                          <div style={{ position:"absolute",left:-10,top:-10,width:18,height:18,borderRadius:"50%",background:"#F3F2FF",border:`2px solid ${T.border}` }}/>
                          <div style={{ position:"absolute",right:-10,top:-10,width:18,height:18,borderRadius:"50%",background:"#F3F2FF",border:`2px solid ${T.border}` }}/>
                        </div>
                        <p style={{ fontSize:13,color:T.charcoal,marginBottom:6 }}>{c.desc}</p>
                        {c.minOrder>0&&<p style={{ fontSize:11.5,color:T.muted,marginBottom:8 }}>Min. order: ₹{c.minOrder.toLocaleString()}</p>}
                        <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center" }}>
                          <span style={{ fontSize:11.5,color:c.active?T.ok:T.err,fontWeight:600 }}>{c.active?`✅ Valid till ${c.expiry}`:`❌ Expired`}</span>
                          <button onClick={()=>copyCode(c.code)} disabled={!c.active}
                            style={{ padding:"6px 14px",borderRadius:8,border:`1px solid ${T.lav}`,background:copiedCode===c.code?T.lav:T.lavPale,color:copiedCode===c.code?"#FFF":T.lav,fontSize:12,cursor:"pointer",fontFamily:"'DM Sans',sans-serif",fontWeight:600,transition:"all .2s" }}>
                            {copiedCode===c.code?"✅ Copied!":"📋 Copy"}
                          </button>
                        </div>
                      </div>
                    </div>
                  </Tilt3D>
                ))}
              </div>
            </div>
          )}

          {/* ════ SAVED STORES ════ */}
          {tab==="stores" && (
            <div>
              <h2 className="serif tilt-in" style={{ fontSize:28,fontWeight:400,marginBottom:18 }}>Saved <em style={{ color:T.lav }}>Shops</em></h2>
              <div style={{ display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(260px,1fr))",gap:16 }}>
                {SAVED_STORES.map((s,i)=>(
                  <Tilt3D key={s.id} style={{ borderRadius:14 }}>
                    <div className="card reveal card-flip" style={{ overflow:"hidden",animationDelay:`${i*.08}s` }}>
                      <div style={{ position:"relative",height:150 }}>
                        <img src={s.img} alt={s.name} style={{ width:"100%",height:"100%",objectFit:"cover",transition:"transform .5s" }} onMouseEnter={e=>e.currentTarget.style.transform="scale(1.07)"} onMouseLeave={e=>e.currentTarget.style.transform="scale(1)"}/>
                        <div style={{ position:"absolute",inset:0,background:"linear-gradient(to top,rgba(26,26,46,.4) 0%,transparent 60%)" }}/>
                        <div style={{ position:"absolute",top:8,right:8 }}>
                          <span style={{ background:T.lav+"EE",color:"#FFF",fontSize:10,fontWeight:600,padding:"2px 8px",borderRadius:10 }}>📌 Saved</span>
                        </div>
                      </div>
                      <div style={{ padding:"13px" }}>
                        <h3 style={{ fontSize:14.5,fontWeight:600,marginBottom:3 }}>{s.name}</h3>
                        <p style={{ fontSize:12,color:T.muted,marginBottom:5 }}>📍 {s.city}</p>
                        <Stars rating={s.rating} size={12}/>
                        <div style={{ display:"flex",gap:8,marginTop:11 }}>
                          <button className="btn-primary" style={{ flex:1,padding:"8px",fontSize:12 }} onClick={()=>setPage("home")}>🛍️ Visit</button>
                          <button className="btn-outline" style={{ flex:1,padding:"8px",fontSize:12 }}>💬 Chat</button>
                          <button style={{ padding:"8px",borderRadius:9,border:`1px solid ${T.border}`,background:T.white,cursor:"pointer",fontSize:14 }}>🗑️</button>
                        </div>
                      </div>
                    </div>
                  </Tilt3D>
                ))}
              </div>
            </div>
          )}

          {/* ════ NOTIFICATIONS ════ */}
          {tab==="notifs" && (
            <div>
              <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:18 }}>
                <h2 className="serif tilt-in" style={{ fontSize:28,fontWeight:400 }}>
                  Notifications {unread>0&&<span className="badge-pop" style={{ background:T.err,color:"#FFF",borderRadius:20,padding:"2px 9px",fontSize:13,fontWeight:700,marginLeft:8 }}>{unread}</span>}
                </h2>
                {unread>0&&<button className="btn-ghost" onClick={markAllRead} style={{ fontSize:13 }}>Mark all read</button>}
              </div>
              {notifications.map((n,i)=>(
                <div key={n.id} className={`reveal notify-slide`} style={{ display:"flex",gap:14,padding:"15px 16px",marginBottom:10,borderRadius:13,background:n.read?T.white:T.lavPale,border:`1px solid ${n.read?T.border:T.lav}30`,cursor:"pointer",transition:"all .2s",animationDelay:`${i*.07}s` }}
                  onClick={()=>setNotifications(ns=>ns.map(x=>x.id===n.id?{...x,read:true}:x))}>
                  <div style={{ width:44,height:44,borderRadius:"50%",background:n.read?T.offWhite:T.lavPale,display:"flex",alignItems:"center",justifyContent:"center",fontSize:22,flexShrink:0,border:`1px solid ${T.border}` }}>{n.icon}</div>
                  <div style={{ flex:1 }}>
                    <div style={{ display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:8 }}>
                      <span style={{ fontSize:14,fontWeight:n.read?500:700,color:T.ink }}>{n.title}</span>
                      <span style={{ fontSize:11,color:T.muted,whiteSpace:"nowrap",flexShrink:0 }}>{n.time}</span>
                    </div>
                    <p style={{ fontSize:13,color:T.muted,marginTop:3,lineHeight:1.45 }}>{n.msg}</p>
                  </div>
                  {!n.read&&<div style={{ width:8,height:8,borderRadius:"50%",background:T.lav,flexShrink:0,marginTop:4 }}/>}
                </div>
              ))}
            </div>
          )}

          {/* ════ SETTINGS ════ */}
          {tab==="settings" && (
            <div>
              <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:18 }}>
                <h2 className="serif tilt-in" style={{ fontSize:28,fontWeight:400 }}>Profile <em style={{ color:T.lav }}>Settings</em></h2>
                {!editing
                  ? <button className="btn-primary" onClick={()=>setEditing(true)} style={{ padding:"9px 20px",fontSize:13 }}>✏️ Edit Profile</button>
                  : <div style={{ display:"flex",gap:8 }}>
                      <button className="btn-outline" onClick={()=>setEditing(false)} style={{ padding:"9px 16px",fontSize:13 }}>Cancel</button>
                      <button className="btn-primary" onClick={save} style={{ padding:"9px 20px",fontSize:13 }}>💾 Save</button>
                    </div>}
              </div>

              {/* Personal Info */}
              <div className="card reveal" style={{ padding:"22px",marginBottom:18 }}>
                <h3 style={{ fontSize:15,fontWeight:600,marginBottom:16,display:"flex",alignItems:"center",gap:8 }}><span style={{ fontSize:20 }}>👤</span> Personal Information</h3>
                <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:14 }}>
                  {[
                    {k:"name",l:"Full Name",ph:"Your full name",ic:"👤"},
                    {k:"email",l:"Email Address",ph:"your@email.com",ic:"📧"},
                    {k:"mobile",l:"Mobile Number",ph:"10-digit number",ic:"📞"},
                    {k:"altMobile",l:"Alternate Mobile",ph:"Optional",ic:"📱"},
                    {k:"dob",l:"Date of Birth",ph:"DD/MM/YYYY",ic:"🎂",type:"date"},
                    {k:"gender",l:"Gender",ph:"",ic:"👥",type:"select",options:["","Male","Female","Non-binary","Prefer not to say"]},
                  ].map(({k,l,ph,ic,type,options})=>(
                    <div key={k} style={{ gridColumn:["name","email"].includes(k)?"span 1":"span 1" }}>
                      <label style={{ fontSize:12,fontWeight:600,color:T.muted,display:"block",marginBottom:5,textTransform:"uppercase",letterSpacing:".04em" }}>{ic} {l}</label>
                      {type==="select" ? (
                        <select className="input-field" disabled={!editing} value={form[k]} onChange={e=>setForm(f=>({...f,[k]:e.target.value}))} style={{ opacity:editing?1:.75 }}>
                          {options.map(o=><option key={o} value={o}>{o||"Select"}</option>)}
                        </select>
                      ) : (
                        <input className="input-field" type={type||"text"} disabled={!editing} placeholder={ph} value={form[k]} onChange={e=>setForm(f=>({...f,[k]:e.target.value}))} style={{ opacity:editing?1:.75,background:editing?"#FFF":T.offWhite }}/>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Location */}
              <div className="card reveal" style={{ padding:"22px",marginBottom:18 }}>
                <h3 style={{ fontSize:15,fontWeight:600,marginBottom:16,display:"flex",alignItems:"center",gap:8 }}><span style={{ fontSize:20 }}>📍</span> Location Details</h3>
                <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:14 }}>
                  {[{k:"city",l:"City",ph:"Your city"},{k:"state",l:"State",ph:"Your state"},{k:"pincode",l:"Pincode",ph:"6-digit"},{k:"address",l:"Default Address",ph:"Your address"}].map(({k,l,ph})=>(
                    <div key={k} style={{ gridColumn:k==="address"?"span 2":"span 1" }}>
                      <label style={{ fontSize:12,fontWeight:600,color:T.muted,display:"block",marginBottom:5,textTransform:"uppercase",letterSpacing:".04em" }}>{l}</label>
                      <input className="input-field" disabled={!editing} placeholder={ph} value={form[k]} onChange={e=>setForm(f=>({...f,[k]:e.target.value}))} style={{ opacity:editing?1:.75,background:editing?"#FFF":T.offWhite }}/>
                    </div>
                  ))}
                </div>
              </div>

              {/* L4U Preferences */}
              <div className="card reveal" style={{ padding:"22px",marginBottom:18 }}>
                <h3 style={{ fontSize:15,fontWeight:600,marginBottom:16,display:"flex",alignItems:"center",gap:8 }}><span style={{ fontSize:20 }}>💜</span> Shopping Preferences</h3>
                <div style={{ display:"flex",flexDirection:"column",gap:14 }}>
                  {[
                    {label:"Show nearby shops first",sub:"Sort shops by your location",on:true},
                    {label:"Notify me of new arrivals",sub:"From your saved stores",on:true},
                    {label:"Price drop alerts",sub:"On your wishlist items",on:false},
                    {label:"Customisation offers",sub:"From boutiques near you",on:true},
                    {label:"Festival sale alerts",sub:"Puja, Diwali, Eid & more",on:true},
                  ].map((pref,i)=>(
                    <div key={pref.label} style={{ display:"flex",justifyContent:"space-between",alignItems:"center",padding:"12px 14px",background:T.offWhite,borderRadius:10 }}>
                      <div>
                        <div style={{ fontSize:13.5,fontWeight:500 }}>{pref.label}</div>
                        <div style={{ fontSize:12,color:T.muted }}>{pref.sub}</div>
                      </div>
                      <Toggle on={pref.on} onChange={()=>{}}/>
                    </div>
                  ))}
                </div>
              </div>

              {/* Account Actions */}
              <div className="card reveal" style={{ padding:"22px" }}>
                <h3 style={{ fontSize:15,fontWeight:600,marginBottom:16,display:"flex",alignItems:"center",gap:8 }}><span style={{ fontSize:20 }}>⚙️</span> Account</h3>
                <div style={{ display:"flex",flexDirection:"column",gap:10 }}>
                  {[
                    {ic:"🔒",label:"Change Password",color:T.charcoal},
                    {ic:"📱",label:"Update Mobile Number",color:T.charcoal},
                    {ic:"🗑️",label:"Delete Account",color:T.err},
                  ].map(a=>(
                    <button key={a.label} style={{ display:"flex",alignItems:"center",gap:12,padding:"13px 16px",borderRadius:10,background:T.offWhite,border:`1px solid ${T.border}`,cursor:"pointer",fontFamily:"'DM Sans',sans-serif",fontSize:13.5,fontWeight:500,color:a.color,textAlign:"left",transition:"all .2s" }}
                      onMouseEnter={e=>{e.currentTarget.style.background=T.lavPale;e.currentTarget.style.borderColor=T.lav;}}
                      onMouseLeave={e=>{e.currentTarget.style.background=T.offWhite;e.currentTarget.style.borderColor=T.border;}}>
                      <span style={{ fontSize:18 }}>{a.ic}</span> {a.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ══════════════════════════════════════════════════════════════
//  EXPLORE STATES PAGE
// ══════════════════════════════════════════════════════════════
const STATE_META = [
  { code:"TG", name:"Telangana",        emoji:"🌶️", capital:"Hyderabad",   color:"#8B7FD4", bg:"#EDE9FF", desc:"Home to Hyderabad's world-class malls, heritage sarees & Nalgonda boutiques." },
  { code:"AP", name:"Andhra Pradesh",   emoji:"🦚", capital:"Visakhapatnam",color:"#7B68EE", bg:"#EEF0FF", desc:"Vizag's trendy stores & Vijayawada's bridal boutiques — coastal fashion." },
  { code:"TN", name:"Tamil Nadu",       emoji:"🏛️", capital:"Chennai",      color:"#9B8FEE", bg:"#F3F0FF", desc:"Chennai's designer ateliers, Coimbatore's silk malls & Madurai's POTHYS." },
  { code:"KA", name:"Karnataka",        emoji:"🦁", capital:"Bengaluru",    color:"#6A5ACD", bg:"#EEEAFF", desc:"Bengaluru's fashion streets, Mysuru's heritage malls & designer studios." },
  { code:"KL", name:"Kerala",           emoji:"🌴", capital:"Kochi",        color:"#8A7BCE", bg:"#F0EEFF", desc:"Kochi's handcrafted boutiques, Kozhikode silks & TVM's POTHYS paradise." },
];

const ExploreStates = ({ setPage }) => {
  const [activeState, setActiveState] = useState(null);
  const [animKey, setAnimKey] = useState(0);

  const storesForState = (code) => STORES_GEO.filter(s=>s.stateCode===code);

  useEffect(()=>{
    const els=document.querySelectorAll(".reveal");
    const io=new IntersectionObserver(e=>e.forEach(en=>{if(en.isIntersecting)en.target.classList.add("on");}),{threshold:.1});
    els.forEach(el=>io.observe(el));
    return()=>io.disconnect();
  },[activeState]);

  return (
    <div>
      {/* Hero */}
      <section style={{ background:"linear-gradient(150deg,#1A1A3E 0%,#2D2060 55%,#3A2070 100%)",padding:"52px 24px",position:"relative",overflow:"hidden" }}>
        <div style={{ position:"absolute",width:300,height:300,borderRadius:"50%",background:"#7B68EE",top:-80,right:-60,opacity:.1,filter:"blur(40px)" }}/>
        <div style={{ position:"absolute",width:200,height:200,borderRadius:"50%",background:"#D3D3FF",bottom:-50,left:100,opacity:.07,filter:"blur(35px)",animation:"morphBlob 8s ease-in-out infinite" }}/>
        <div style={{ maxWidth:1200,margin:"0 auto",position:"relative",zIndex:2,textAlign:"center" }}>
          <div className="fade-up">
            <div style={{ display:"flex",justifyContent:"center",marginBottom:16 }}>
              <L4ULogo size={80} dark noTagline animated/>
            </div>
            <div style={{ display:"inline-flex",alignItems:"center",gap:8,background:"rgba(211,211,255,.15)",border:"1px solid rgba(211,211,255,.3)",borderRadius:20,padding:"5px 16px",marginBottom:16 }}>
              <span style={{ width:7,height:7,borderRadius:"50%",background:"#D3D3FF",animation:"pulse 1.4s infinite",display:"inline-block" }}/>
              <span style={{ fontSize:11,color:"#D3D3FF",letterSpacing:".12em",textTransform:"uppercase",fontWeight:500 }}>5 States · South India</span>
            </div>
            <h1 className="serif" style={{ fontSize:"clamp(32px,5vw,60px)",color:"#FFFFFF",lineHeight:1.1,marginBottom:12,fontWeight:400 }}>
              Explore <em style={{ background:"linear-gradient(90deg,#9B8FEE,#D3D3FF,#7B68EE)",backgroundSize:"200% auto",WebkitBackgroundClip:"text",WebkitTextFillColor:"transparent",animation:"lavFlow 3s linear infinite" }}>South India</em>
            </h1>
            <p style={{ color:"#C0C0E8",fontSize:15,maxWidth:520,margin:"0 auto" }}>
              {STORES_GEO.length} real shops across 5 states. Click a state to browse its shops & fashion.
            </p>
          </div>
          {/* State emoji row */}
          <div style={{ display:"flex",justifyContent:"center",gap:20,marginTop:28,flexWrap:"wrap" }}>
            {STATE_META.map((s,i)=>(
              <div key={s.code} className="card3d"
                onClick={()=>{setActiveState(activeState===s.code?null:s.code);setAnimKey(k=>k+1);}}
                style={{ background:activeState===s.code?"rgba(211,211,255,.2)":"rgba(255,255,255,.06)",backdropFilter:"blur(10px)",borderRadius:16,padding:"18px 22px",cursor:"pointer",border:`1.5px solid ${activeState===s.code?"#D3D3FF":"rgba(255,255,255,.1)"}`,transition:"all .25s",animation:`fadeUp .5s ease ${i*.08}s both`,minWidth:140,textAlign:"center" }}>
                <div style={{ fontSize:32,marginBottom:8,animation:"float 3s ease-in-out infinite",animationDelay:`${i*.4}s` }}>{s.emoji}</div>
                <div style={{ fontWeight:700,color:"#FFFFFF",fontSize:14 }}>{s.name}</div>
                <div style={{ fontSize:11,color:"#9090C0",marginTop:3 }}>{storesForState(s.code).length} shops</div>
                {activeState===s.code && <div style={{ marginTop:6,width:"100%",height:2,background:"linear-gradient(90deg,transparent,#D3D3FF,transparent)",borderRadius:1 }}/>}
              </div>
            ))}
          </div>
        </div>
      </section>

      <div style={{ maxWidth:1200,margin:"0 auto",padding:"32px 24px" }}>
        {/* State detail panel */}
        {activeState && (()=>{
          const meta = STATE_META.find(s=>s.code===activeState);
          const shops = storesForState(activeState);
          const cities = [...new Set(shops.map(s=>s.city))];
          return (
            <div key={animKey} className="tilt-in" style={{ background:`linear-gradient(135deg,${meta.bg},#FFFFFF)`,borderRadius:20,padding:"28px",marginBottom:32,border:`1.5px solid ${meta.color}30` }}>
              <div style={{ display:"flex",gap:20,alignItems:"flex-start",flexWrap:"wrap" }}>
                <div style={{ fontSize:60,lineHeight:1,animation:"float 3s ease-in-out infinite" }}>{meta.emoji}</div>
                <div style={{ flex:1 }}>
                  <h2 className="serif" style={{ fontSize:32,fontWeight:400,marginBottom:6 }}>{meta.name}</h2>
                  <p style={{ fontSize:14,color:T.muted,marginBottom:14,lineHeight:1.6 }}>{meta.desc}</p>
                  <div style={{ display:"flex",gap:10,flexWrap:"wrap" }}>
                    {cities.map(c=>(
                      <span key={c} style={{ background:`${meta.color}18`,color:meta.color,borderRadius:20,padding:"4px 14px",fontSize:12.5,fontWeight:600,border:`1px solid ${meta.color}30` }}>📍 {c}</span>
                    ))}
                  </div>
                </div>
                <div style={{ display:"flex",flexDirection:"column",gap:12 }}>
                  {[["🏬",shops.filter(s=>s.type==="mall").length,"Malls"],["🏪",shops.filter(s=>s.type==="store").length,"Stores"],["✂️",shops.filter(s=>s.type==="boutique").length,"Boutiques"]].map(([ic,v,l])=>(
                    <div key={l} style={{ textAlign:"center",background:"rgba(255,255,255,.7)",borderRadius:10,padding:"10px 16px",minWidth:80 }}>
                      <div style={{ fontSize:20 }}>{ic}</div>
                      <div className="serif" style={{ fontSize:22,fontWeight:600,color:meta.color }}>{v}</div>
                      <div style={{ fontSize:10,color:T.muted }}>{l}</div>
                    </div>
                  ))}
                </div>
              </div>
              {/* Shops grid */}
              <div style={{ marginTop:24,display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(260px,1fr))",gap:16 }}>
                {shops.map((shop,i)=>(
                  <Tilt3D key={shop.id}>
                    <StoreCard store={shop} distKm={null}/>
                  </Tilt3D>
                ))}
              </div>
            </div>
          );
        })()}

        {/* All states overview cards */}
        {!activeState && (
          <div style={{ display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(300px,1fr))",gap:20 }}>
            {STATE_META.map((s,i)=>{
              const shops = storesForState(s.code);
              const cities = [...new Set(shops.map(sh=>sh.city))];
              return (
                <Tilt3D key={s.code}>
                  <div className="reveal card-flip" style={{ background:"#FFFFFF",borderRadius:18,overflow:"hidden",border:`1px solid ${T.border}`,cursor:"pointer",animationDelay:`${i*.07}s` }}
                    onClick={()=>{setActiveState(s.code);setAnimKey(k=>k+1);}}>
                    {/* State color header */}
                    <div style={{ background:`linear-gradient(135deg,${s.bg},${s.color}30)`,padding:"22px",textAlign:"center",position:"relative",overflow:"hidden" }}>
                      <div style={{ position:"absolute",top:-20,right:-20,fontSize:90,opacity:.08 }}>{s.emoji}</div>
                      <div style={{ fontSize:48,marginBottom:8,animation:`float ${2.5+i*.3}s ease-in-out infinite` }}>{s.emoji}</div>
                      <h3 style={{ fontSize:20,fontWeight:700,color:s.color,marginBottom:3 }}>{s.name}</h3>
                      <p style={{ fontSize:12,color:T.muted }}>Capital: {s.capital}</p>
                    </div>
                    <div style={{ padding:"16px" }}>
                      <p style={{ fontSize:13,color:T.muted,lineHeight:1.6,marginBottom:12 }}>{s.desc}</p>
                      <div style={{ display:"flex",gap:8,marginBottom:12,flexWrap:"wrap" }}>
                        {cities.slice(0,4).map(c=><span key={c} style={{ fontSize:11,background:s.bg,color:s.color,borderRadius:12,padding:"2px 9px",fontWeight:500 }}>{c}</span>)}
                      </div>
                      <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center" }}>
                        <span style={{ fontSize:13,fontWeight:600,color:s.color }}>{shops.length} shops listed</span>
                        <button style={{ padding:"7px 16px",borderRadius:8,background:`linear-gradient(135deg,${s.color},${s.color}CC)`,color:"#FFF",border:"none",fontSize:12,fontWeight:600,cursor:"pointer",fontFamily:"'DM Sans',sans-serif" }}>
                          Explore →
                        </button>
                      </div>
                    </div>
                  </div>
                </Tilt3D>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

// ══════════════════════════════════════════════════════════════
//  DISCOVER SHOPS PAGE
// ══════════════════════════════════════════════════════════════
const DiscoverShops = ({ setPage }) => {
  const [search, setSearch]   = useState("");
  const [stateF, setStateF]   = useState("ALL");
  const [typeF,  setTypeF]    = useState("all");
  const [sortF,  setSortF]    = useState("rating");
  const [view,   setView]     = useState("grid"); // grid | list
  const [featured, setFeatured] = useState(false);

  useEffect(()=>{
    const els=document.querySelectorAll(".reveal");
    const io=new IntersectionObserver(e=>e.forEach(en=>{if(en.isIntersecting)en.target.classList.add("on");}),{threshold:.1});
    els.forEach(el=>io.observe(el));
    return()=>io.disconnect();
  },[stateF, typeF, sortF]);

  const allStates=[{code:"ALL",name:"All States"},...STATE_META];

  const filtered = STORES_GEO.filter(s=>{
    if(stateF!=="ALL" && s.stateCode!==stateF) return false;
    if(typeF!=="all" && s.type!==typeF) return false;
    if(featured && !s.verified) return false;
    if(search){
      const q=search.toLowerCase();
      return s.name.toLowerCase().includes(q)||s.city.toLowerCase().includes(q)||s.tags.some(t=>t.toLowerCase().includes(q))||s.state.toLowerCase().includes(q);
    }
    return true;
  }).sort((a,b)=>
    sortF==="rating"   ? b.rating-a.rating :
    sortF==="reviews"  ? b.reviews-a.reviews :
    sortF==="name"     ? a.name.localeCompare(b.name) :
    sortF==="newest"   ? (b.id>a.id?1:-1) : 0
  );

  const catCounts = { all:STORES_GEO.length, mall:STORES_GEO.filter(s=>s.type==="mall").length, store:STORES_GEO.filter(s=>s.type==="store").length, boutique:STORES_GEO.filter(s=>s.type==="boutique").length };

  return (
    <div style={{ minHeight:"100vh" }}>
      {/* Hero strip */}
      <div style={{ background:"linear-gradient(135deg,#2D2060,#1A1A3E)",padding:"32px 24px",position:"relative",overflow:"hidden" }}>
        <div style={{ position:"absolute",width:250,height:250,borderRadius:"50%",background:"#7B68EE",top:-80,right:-40,opacity:.1,filter:"blur(35px)" }}/>
        <div style={{ maxWidth:1200,margin:"0 auto",position:"relative",zIndex:1 }}>
          <div className="fade-up" style={{ display:"flex",alignItems:"center",gap:16,flexWrap:"wrap" }}>
            <div>
              <div style={{ fontSize:11,letterSpacing:".15em",textTransform:"uppercase",color:"#D3D3FF",marginBottom:6 }}>✦ Real Shops · South India</div>
              <h1 className="serif" style={{ fontSize:"clamp(26px,4vw,44px)",color:"#FFFFFF",fontWeight:400,marginBottom:4 }}>
                Discover <em style={{ color:"#D3D3FF" }}>Fashion Shops</em>
              </h1>
              <p style={{ color:"#9090C0",fontSize:13 }}>{filtered.length} of {STORES_GEO.length} shops · 5 states</p>
            </div>
            {/* Big search */}
            <div style={{ flex:1,minWidth:260,position:"relative" }}>
              <span style={{ position:"absolute",left:14,top:"50%",transform:"translateY(-50%)",color:"#9090C0",fontSize:16 }}>🔍</span>
              <input value={search} onChange={e=>setSearch(e.target.value)}
                placeholder="Search shops, cities, sarees, bridal…"
                style={{ width:"100%",padding:"14px 14px 14px 42px",borderRadius:12,border:"1.5px solid rgba(211,211,255,.25)",background:"rgba(255,255,255,.08)",color:"#FFFFFF",fontSize:14,fontFamily:"'DM Sans',sans-serif",outline:"none" }}/>
              {search && <button onClick={()=>setSearch("")} style={{ position:"absolute",right:12,top:"50%",transform:"translateY(-50%)",background:"none",border:"none",color:"#9090C0",cursor:"pointer",fontSize:16 }}>✕</button>}
            </div>
          </div>
        </div>
      </div>

      <div style={{ maxWidth:1200,margin:"0 auto",padding:"20px 24px" }}>
        {/* Filter bar */}
        <div style={{ background:"#FFFFFF",borderRadius:14,padding:"14px 18px",marginBottom:20,border:`1px solid ${T.border}`,display:"flex",gap:12,flexWrap:"wrap",alignItems:"center",boxShadow:"0 2px 12px #D3D3FF15" }}>
          {/* State pills */}
          <div style={{ display:"flex",gap:6,overflowX:"auto",maxWidth:"100%",paddingBottom:2 }}>
            {allStates.map(s=>(
              <button key={s.code} onClick={()=>setStateF(s.code)}
                style={{ padding:"6px 13px",borderRadius:20,border:`1px solid ${stateF===s.code?T.lav:T.border}`,background:stateF===s.code?T.lav:"#FFFFFF",color:stateF===s.code?"#FFF":T.charcoal,fontSize:12,cursor:"pointer",fontFamily:"'DM Sans',sans-serif",fontWeight:500,whiteSpace:"nowrap",transition:"all .15s",flexShrink:0 }}>
                {s.emoji&&<span style={{ marginRight:5 }}>{s.emoji}</span>}{s.name}
              </button>
            ))}
          </div>
          <div style={{ display:"flex",gap:8,marginLeft:"auto",alignItems:"center",flexWrap:"wrap" }}>
            {/* Type */}
            <select value={typeF} onChange={e=>setTypeF(e.target.value)}
              style={{ padding:"8px 12px",borderRadius:9,border:`1px solid ${T.border}`,fontSize:12.5,background:"#FFF",color:T.charcoal,fontFamily:"'DM Sans',sans-serif" }}>
              <option value="all">🏬 All ({catCounts.all})</option>
              <option value="mall">🏬 Malls ({catCounts.mall})</option>
              <option value="store">🏪 Stores ({catCounts.store})</option>
              <option value="boutique">✂️ Boutiques ({catCounts.boutique})</option>
            </select>
            {/* Sort */}
            <select value={sortF} onChange={e=>setSortF(e.target.value)}
              style={{ padding:"8px 12px",borderRadius:9,border:`1px solid ${T.border}`,fontSize:12.5,background:"#FFF",color:T.charcoal,fontFamily:"'DM Sans',sans-serif" }}>
              <option value="rating">⭐ Top Rated</option>
              <option value="reviews">💬 Most Reviewed</option>
              <option value="name">🔤 A–Z</option>
            </select>
            {/* Verified toggle */}
            <label style={{ display:"flex",alignItems:"center",gap:7,cursor:"pointer",fontSize:13,color:T.muted,whiteSpace:"nowrap" }}>
              <div className={`switch-track ${featured?"on":""}`} onClick={()=>setFeatured(!featured)}>
                <div className="switch-thumb"/>
              </div>
              Verified only
            </label>
            {/* View toggle */}
            <div style={{ display:"flex",gap:4,background:T.offWhite,borderRadius:8,padding:3 }}>
              {["grid","list"].map(v=>(
                <button key={v} onClick={()=>setView(v)}
                  style={{ padding:"5px 9px",borderRadius:6,border:"none",background:view===v?"#FFF":T.offWhite,color:view===v?T.lav:T.muted,cursor:"pointer",fontSize:15,boxShadow:view===v?"0 1px 6px #D3D3FF30":"none",transition:"all .15s" }}>
                  {v==="grid"?"⊞":"☰"}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Active filters */}
        {(stateF!=="ALL"||typeF!=="all"||featured||search) && (
          <div style={{ display:"flex",gap:7,flexWrap:"wrap",marginBottom:14 }}>
            {stateF!=="ALL" && <span style={{ display:"flex",alignItems:"center",gap:5,fontSize:12,background:T.lavPale,color:T.lav,border:`1px solid ${T.border}`,borderRadius:12,padding:"3px 10px" }}>
              {STATE_META.find(s=>s.code===stateF)?.emoji} {stateF} <span style={{ cursor:"pointer" }} onClick={()=>setStateF("ALL")}>✕</span></span>}
            {typeF!=="all" && <span style={{ display:"flex",alignItems:"center",gap:5,fontSize:12,background:T.lavPale,color:T.lav,border:`1px solid ${T.border}`,borderRadius:12,padding:"3px 10px" }}>
              Type: {typeF} <span style={{ cursor:"pointer" }} onClick={()=>setTypeF("all")}>✕</span></span>}
            {featured && <span style={{ display:"flex",alignItems:"center",gap:5,fontSize:12,background:T.lavPale,color:T.lav,border:`1px solid ${T.border}`,borderRadius:12,padding:"3px 10px" }}>
              ✅ Verified <span style={{ cursor:"pointer" }} onClick={()=>setFeatured(false)}>✕</span></span>}
            {search && <span style={{ display:"flex",alignItems:"center",gap:5,fontSize:12,background:T.lavPale,color:T.lav,border:`1px solid ${T.border}`,borderRadius:12,padding:"3px 10px" }}>
              🔍 "{search}" <span style={{ cursor:"pointer" }} onClick={()=>setSearch("")}>✕</span></span>}
            <button onClick={()=>{setStateF("ALL");setTypeF("all");setFeatured(false);setSearch("");}}
              style={{ fontSize:12,color:T.muted,background:"none",border:`1px solid ${T.border}`,borderRadius:12,padding:"3px 11px",cursor:"pointer",fontFamily:"'DM Sans',sans-serif" }}>
              Clear All
            </button>
          </div>
        )}

        {/* Results count */}
        <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:18 }}>
          <p style={{ fontSize:13.5,color:T.muted }}><strong style={{ color:T.ink }}>{filtered.length}</strong> shops found</p>
          {filtered.length===0 && <button className="btn-primary" style={{ fontSize:12,padding:"8px 16px" }} onClick={()=>{setStateF("ALL");setTypeF("all");setFeatured(false);setSearch("");}}>Clear Filters</button>}
        </div>

        {/* Grid view */}
        {view==="grid" && (
          <div style={{ display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(272px,1fr))",gap:18 }}>
            {filtered.map((shop,i)=>(
              <Tilt3D key={shop.id}>
                <StoreCard store={shop} distKm={null}/>
              </Tilt3D>
            ))}
          </div>
        )}

        {/* List view */}
        {view==="list" && (
          <div style={{ display:"flex",flexDirection:"column",gap:12 }}>
            {filtered.map((shop,i)=>(
              <div key={shop.id} className="reveal card3d" style={{ background:"#FFFFFF",borderRadius:14,border:`1px solid ${T.border}`,padding:"14px",display:"flex",gap:14,alignItems:"flex-start",animationDelay:`${i*.04}s` }}>
                <div style={{ position:"relative",width:100,height:110,borderRadius:10,overflow:"hidden",flexShrink:0 }}>
                  <img src={shop.img} alt={shop.name} style={{ width:"100%",height:"100%",objectFit:"cover",transition:"transform .4s" }} onMouseEnter={e=>e.currentTarget.style.transform="scale(1.08)"} onMouseLeave={e=>e.currentTarget.style.transform="scale(1)"}/>
                  {shop.verified && <div style={{ position:"absolute",bottom:4,left:4,background:"rgba(46,125,50,.9)",color:"#FFF",fontSize:9,fontWeight:700,padding:"2px 6px",borderRadius:8 }}>✅ Verified</div>}
                </div>
                <div style={{ flex:1,minWidth:0 }}>
                  <div style={{ display:"flex",gap:7,marginBottom:5,flexWrap:"wrap" }}>
                    <span style={{ fontSize:11,fontWeight:600,color:T.lav,background:T.lavPale,borderRadius:8,padding:"2px 8px" }}>{shop.stateCode} — {shop.state}</span>
                    <span style={{ fontSize:11,background:T.offWhite,color:T.muted,borderRadius:8,padding:"2px 8px" }}>{shop.type==="mall"?"🏬":shop.type==="boutique"?"✂️":"🏪"} {shop.type}</span>
                    {shop.verified && <span style={{ fontSize:11,background:"#E8F5E9",color:T.ok,borderRadius:8,padding:"2px 8px",fontWeight:600 }}>✅ Verified</span>}
                  </div>
                  <h3 style={{ fontSize:15.5,fontWeight:700,marginBottom:3,lineHeight:1.2 }}>{shop.name}</h3>
                  <p style={{ fontSize:12.5,color:T.muted,marginBottom:6 }}>📍 {shop.area}, {shop.city}</p>
                  <div style={{ display:"flex",alignItems:"center",gap:8,marginBottom:6 }}>
                    <Stars rating={shop.rating} count={shop.reviews} size={12}/>
                  </div>
                  <p style={{ fontSize:13,color:T.charcoal,lineHeight:1.5,marginBottom:8 }}>{shop.snippet}</p>
                  <div style={{ display:"flex",gap:5,flexWrap:"wrap" }}>
                    {shop.tags.slice(0,4).map(t=><span key={t} className="tag">{t}</span>)}
                  </div>
                </div>
                <div style={{ display:"flex",flexDirection:"column",gap:8,flexShrink:0 }}>
                  <button className="btn-primary" style={{ padding:"9px 16px",fontSize:12 }}>🛍️ View Shop</button>
                  <button className="btn-outline" style={{ padding:"8px 16px",fontSize:12 }}>💬 Chat</button>
                  {shop.phone && <a href={`tel:${shop.phone}`} style={{ textDecoration:"none" }}><button style={{ width:"100%",padding:"8px 16px",borderRadius:9,border:`1px solid #A5D6A7`,background:"#E8F5E9",color:T.ok,cursor:"pointer",fontSize:12,fontFamily:"'DM Sans',sans-serif",fontWeight:600 }}>📞 Call</button></a>}
                </div>
              </div>
            ))}
          </div>
        )}

        {filtered.length===0 && (
          <div style={{ textAlign:"center",padding:"64px 20px" }}>
            <div style={{ fontSize:56,marginBottom:16,animation:"float 2s ease-in-out infinite" }}>🔍</div>
            <h3 style={{ fontSize:20,fontWeight:600,marginBottom:8 }}>No shops found</h3>
            <p style={{ color:T.muted,fontSize:14,marginBottom:20 }}>Try different filters or search terms.</p>
            <button className="btn-primary" onClick={()=>{setStateF("ALL");setTypeF("all");setFeatured(false);setSearch("");}}>Show All Shops</button>
          </div>
        )}
      </div>
    </div>
  );
};

// ══════════════════════════════════════════════════════════════
//  CHAT PAGE
// ══════════════════════════════════════════════════════════════
const QUICK_REPLIES = ["What's available?","Current offers?","Open today?","Can I customise?","Send photos please","Do you do home delivery?","What's the price range?"];
const AUTO_REPLIES  = [
  "Sure! Let me check 😊",
  "Yes, we have that! Want details?",
  "Absolutely! What would you like changed?",
  "Sending photos now 📸✨",
  "Yes, we do home delivery — charges apply for distances over 10 km.",
  "Our price range starts from ₹299. What's your budget?",
  "We're open today — do come in or order online!",
  "Great question! I'll get back to you shortly 🙏",
];

const ChatPage = ({ user }) => {
  const [activeShop, setActiveShop]   = useState(STORES_GEO[0]);
  const [allMsgs, setAllMsgs]         = useState({
    [STORES_GEO[0].id]: [{ id:1, from:"shop", text:`Welcome to ${STORES_GEO[0].name}! 👋 How can I help you today?`, time: new Date().toLocaleTimeString("en-IN",{hour:"2-digit",minute:"2-digit"}) }]
  });
  const [input, setInput]             = useState("");
  const [typing, setTyping]           = useState(false);
  const [shopSearch, setShopSearch]   = useState("");
  const endRef = useRef(null);

  useEffect(()=>{ endRef.current?.scrollIntoView({ behavior:"smooth" }); },[allMsgs, activeShop, typing]);

  const msgs = allMsgs[activeShop.id] || [];
  const unreadCounts = Object.fromEntries(STORES_GEO.map(s=>[ s.id, Math.floor(Math.random()*3) ]));

  const switchShop = (shop) => {
    setActiveShop(shop);
    if(!allMsgs[shop.id]) {
      setAllMsgs(p=>({...p, [shop.id]:[{ id:Date.now(), from:"shop", text:`Welcome to ${shop.name}! 👋 Browse our collection or ask me anything.`, time: new Date().toLocaleTimeString("en-IN",{hour:"2-digit",minute:"2-digit"}) }]}));
    }
  };

  const send = (text) => {
    const txt = text || input.trim();
    if(!txt) return;
    const now = new Date().toLocaleTimeString("en-IN",{hour:"2-digit",minute:"2-digit"});
    setAllMsgs(p=>({...p, [activeShop.id]:[...(p[activeShop.id]||[]), { id:Date.now(), from:"user", text:txt, time:now }]}));
    setInput("");
    setTyping(true);
    setTimeout(()=>{
      setTyping(false);
      const reply = AUTO_REPLIES[Math.floor(Math.random()*AUTO_REPLIES.length)];
      const replyTime = new Date().toLocaleTimeString("en-IN",{hour:"2-digit",minute:"2-digit"});
      setAllMsgs(p=>({...p, [activeShop.id]:[...(p[activeShop.id]||[]), { id:Date.now()+1, from:"shop", text:reply, time:replyTime }]}));
    }, 1200+Math.random()*800);
  };

  const filteredShops = STORES_GEO.filter(s=> !shopSearch || s.name.toLowerCase().includes(shopSearch.toLowerCase()) || s.city.toLowerCase().includes(shopSearch.toLowerCase()));

  return (
    <div style={{ maxWidth:1100,margin:"0 auto",padding:"20px 24px",height:"calc(100vh - 75px)",display:"flex",gap:14 }}>

      {/* ── Shop Sidebar ── */}
      <div style={{ width:280,flexShrink:0,background:"#FFFFFF",borderRadius:16,border:`1px solid ${T.border}`,display:"flex",flexDirection:"column",overflow:"hidden",boxShadow:`0 4px 20px #D3D3FF15` }}>
        {/* Sidebar header */}
        <div style={{ padding:"14px 14px 10px",borderBottom:`1px solid ${T.border}`,background:T.lavPale }}>
          <div style={{ display:"flex",alignItems:"center",gap:8,marginBottom:10 }}>
            <L4ULogo size={32} noTagline/>
            <div>
              <div style={{ fontWeight:700,fontSize:13.5,color:T.ink }}>Shop Chats</div>
              <div style={{ fontSize:11,color:T.muted }}>South India</div>
            </div>
          </div>
          <div style={{ position:"relative" }}>
            <span style={{ position:"absolute",left:9,top:"50%",transform:"translateY(-50%)",color:T.muted,fontSize:12 }}>🔍</span>
            <input value={shopSearch} onChange={e=>setShopSearch(e.target.value)}
              placeholder="Search shops…"
              style={{ width:"100%",padding:"7px 9px 7px 26px",border:`1px solid ${T.border}`,borderRadius:8,fontSize:12.5,fontFamily:"'DM Sans',sans-serif",outline:"none" }}/>
          </div>
        </div>

        {/* Shop list */}
        <div style={{ flex:1,overflowY:"auto" }}>
          {filteredShops.map(shop=>{
            const sm = allMsgs[shop.id]||[];
            const last = sm[sm.length-1];
            const isActive = activeShop.id===shop.id;
            return (
              <div key={shop.id} onClick={()=>switchShop(shop)}
                style={{ padding:"11px 13px",borderBottom:`1px solid ${T.borderSoft}`,cursor:"pointer",background:isActive?T.lavPale:"transparent",borderLeft:`3px solid ${isActive?T.lav:"transparent"}`,transition:"all .15s" }}
                onMouseEnter={e=>{if(!isActive)e.currentTarget.style.background=T.offWhite;}}
                onMouseLeave={e=>{if(!isActive)e.currentTarget.style.background="transparent";}}>
                <div style={{ display:"flex",gap:9,alignItems:"center" }}>
                  <div style={{ position:"relative",flexShrink:0 }}>
                    <img src={shop.img} alt="" style={{ width:38,height:38,borderRadius:"50%",objectFit:"cover",border:`2px solid ${isActive?T.lav:T.border}` }}/>
                    <div style={{ position:"absolute",bottom:0,right:0,width:9,height:9,borderRadius:"50%",background:T.ok,border:"1.5px solid white" }}/>
                  </div>
                  <div style={{ flex:1,minWidth:0 }}>
                    <div style={{ fontSize:13,fontWeight:isActive?700:600,color:T.ink,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap" }}>{shop.name}</div>
                    <div style={{ fontSize:11,color:T.muted,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap" }}>
                      {last ? last.text.slice(0,30)+(last.text.length>30?"…":"") : `${shop.city} · ${shop.type}`}
                    </div>
                  </div>
                  <div style={{ display:"flex",flexDirection:"column",alignItems:"flex-end",gap:3,flexShrink:0 }}>
                    {last&&<div style={{ fontSize:9.5,color:T.muted }}>{last.time}</div>}
                    {unreadCounts[shop.id]>0&&!isActive&&(
                      <div style={{ background:T.lav,color:"#FFF",borderRadius:"50%",width:16,height:16,fontSize:9,display:"flex",alignItems:"center",justifyContent:"center",fontWeight:700 }}>{unreadCounts[shop.id]}</div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ── Chat Window ── */}
      <div style={{ flex:1,background:"#FFFFFF",borderRadius:16,border:`1px solid ${T.border}`,display:"flex",flexDirection:"column",overflow:"hidden",boxShadow:`0 4px 20px #D3D3FF15` }}>
        {/* Chat header */}
        <div style={{ padding:"12px 18px",borderBottom:`1px solid ${T.border}`,background:"linear-gradient(135deg,#2D2060,#1A1A3E)",display:"flex",alignItems:"center",gap:12 }}>
          <div style={{ position:"relative" }}>
            <img src={activeShop.img} alt="" style={{ width:42,height:42,borderRadius:"50%",objectFit:"cover",border:`2px solid rgba(211,211,255,.4)` }}/>
            <div style={{ position:"absolute",bottom:0,right:0,width:10,height:10,borderRadius:"50%",background:T.ok,border:"2px solid white",boxShadow:`0 0 6px ${T.ok}80` }}/>
          </div>
          <div style={{ flex:1 }}>
            <div style={{ fontWeight:700,fontSize:14.5,color:"#FFFFFF" }}>{activeShop.name}</div>
            <div style={{ fontSize:11.5,color:"#9090C0",display:"flex",alignItems:"center",gap:6 }}>
              <span style={{ width:6,height:6,borderRadius:"50%",background:T.ok,animation:"pulse 1.5s infinite",display:"inline-block" }}/>
              Online · {activeShop.city}, {activeShop.state}
            </div>
          </div>
          <div style={{ display:"flex",gap:10,alignItems:"center" }}>
            <Stars rating={activeShop.rating} count={activeShop.reviews} size={11}/>
            {activeShop.phone&&(
              <a href={`tel:${activeShop.phone}`} style={{ background:"rgba(46,125,50,.2)",border:"1px solid rgba(46,125,50,.4)",color:"#81C784",borderRadius:8,padding:"5px 11px",fontSize:11.5,textDecoration:"none",fontFamily:"'DM Sans',sans-serif",fontWeight:600 }}>�� Call</a>
            )}
          </div>
        </div>

        {/* Info strip */}
        <div style={{ padding:"6px 16px",background:T.lavPale,borderBottom:`1px solid ${T.borderSoft}`,display:"flex",gap:12,overflowX:"auto" }}>
          <span style={{ fontSize:11.5,color:T.muted,whiteSpace:"nowrap" }}>🕐 {activeShop.hours}</span>
          <span style={{ color:T.border }}>·</span>
          <span style={{ fontSize:11.5,color:T.muted,whiteSpace:"nowrap" }}>📍 {activeShop.area}</span>
          {activeShop.tags.slice(0,3).map(t=><span key={t} className="tag" style={{ flexShrink:0 }}>{t}</span>)}
        </div>

        {/* Messages */}
        <div style={{ flex:1,overflowY:"auto",padding:"16px",display:"flex",flexDirection:"column",gap:10,background:T.softWhite }}>
          {/* Date header */}
          <div style={{ textAlign:"center",marginBottom:4 }}>
            <span style={{ fontSize:11,color:T.muted,background:T.white,border:`1px solid ${T.border}`,padding:"3px 12px",borderRadius:20 }}>Today</span>
          </div>

          {msgs.map((m,i)=>(
            <div key={m.id} style={{ display:"flex",flexDirection:"column",alignItems:m.from==="user"?"flex-end":"flex-start",animation:"fadeUp .3s ease both",animationDelay:`${Math.min(i*.03,.2)}s` }}>
              {m.from==="shop" ? (
                <div style={{ display:"flex",gap:7,alignItems:"flex-end",maxWidth:"72%" }}>
                  <img src={activeShop.img} alt="" style={{ width:24,height:24,borderRadius:"50%",objectFit:"cover",flexShrink:0,marginBottom:2 }}/>
                  <div>
                    <div className="chat-shop">{m.text}</div>
                    <div style={{ fontSize:9.5,color:T.muted,marginTop:3,paddingLeft:4 }}>{m.time}</div>
                  </div>
                </div>
              ) : (
                <div style={{ maxWidth:"72%" }}>
                  <div className="chat-user">{m.text}</div>
                  <div style={{ fontSize:9.5,color:T.muted,marginTop:3,paddingRight:4,textAlign:"right" }}>✓✓ {m.time}</div>
                </div>
              )}
            </div>
          ))}

          {/* Typing indicator */}
          {typing && (
            <div style={{ display:"flex",gap:7,alignItems:"flex-end" }}>
              <img src={activeShop.img} alt="" style={{ width:24,height:24,borderRadius:"50%",objectFit:"cover" }}/>
              <div className="chat-shop" style={{ display:"flex",gap:5,padding:"10px 14px",alignItems:"center" }}>
                {[0,.18,.36].map((d,i)=>(
                  <div key={i} style={{ width:7,height:7,borderRadius:"50%",background:T.lav,animation:`pulse .9s ${d}s infinite` }}/>
                ))}
              </div>
            </div>
          )}
          <div ref={endRef}/>
        </div>

        {/* Quick replies */}
        <div style={{ padding:"6px 14px",display:"flex",gap:6,overflowX:"auto",borderTop:`1px solid ${T.borderSoft}`,background:"#FFFFFF" }}>
          {QUICK_REPLIES.map(q=>(
            <button key={q} onClick={()=>send(q)}
              style={{ padding:"5px 11px",borderRadius:16,border:`1px solid ${T.border}`,background:"#FFFFFF",color:T.charcoal,fontSize:11.5,cursor:"pointer",whiteSpace:"nowrap",fontFamily:"'DM Sans',sans-serif",transition:"all .15s",flexShrink:0 }}
              onMouseEnter={e=>{e.currentTarget.style.borderColor=T.lav;e.currentTarget.style.color=T.lav;e.currentTarget.style.background=T.lavPale;}}
              onMouseLeave={e=>{e.currentTarget.style.borderColor=T.border;e.currentTarget.style.color=T.charcoal;e.currentTarget.style.background="#FFFFFF";}}>
              {q}
            </button>
          ))}
        </div>

        {/* Input row */}
        <div style={{ padding:"10px 14px",borderTop:`1px solid ${T.border}`,display:"flex",gap:8,background:"#FFFFFF",alignItems:"flex-end" }}>
          {/* Emoji / attachment (placeholder) */}
          <button style={{ width:36,height:36,borderRadius:"50%",background:T.offWhite,border:`1px solid ${T.border}`,cursor:"pointer",fontSize:16,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0 }}>😊</button>
          <textarea
            value={input}
            onChange={e=>setInput(e.target.value)}
            onKeyDown={e=>{if(e.key==="Enter"&&!e.shiftKey){e.preventDefault();send();}}}
            placeholder={`Message ${activeShop.name.split(" ")[0]}… (Enter to send)`}
            rows={1}
            style={{ flex:1,padding:"9px 13px",border:`1.5px solid ${T.border}`,borderRadius:18,fontSize:13,fontFamily:"'DM Sans',sans-serif",outline:"none",resize:"none",lineHeight:1.45,maxHeight:80,overflowY:"auto",transition:"border-color .2s" }}
            onFocus={e=>e.target.style.borderColor=T.lav}
            onBlur={e=>e.target.style.borderColor=T.border}
          />
          <button onClick={()=>send()}
            style={{ width:40,height:40,borderRadius:"50%",background:`linear-gradient(135deg,${T.lav},${T.lavDark})`,border:"none",color:"#FFF",fontSize:16,display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer",flexShrink:0,transition:"all .2s",boxShadow:input?`0 4px 14px ${T.lav}50`:"none",transform:input?"scale(1.05)":"scale(1)" }}>
            ➤
          </button>
        </div>
      </div>
    </div>
  );
};

// ══════════════════════════════════════════════════════════════
//  MAIN APP COMPONENT
// ══════════════════════════════════════════════════════════════
export default function App() {
  const [user,       setUser]       = useState(null);
  const [page,       setPage]       = useState("home");
  const [editProduct,setEditProduct]= useState(null);

  // Location state — shared across session
  const [userLoc,    setUserLoc]    = useState(null);   // { lat, lng, label }
  const [locStatus,  setLocStatus]  = useState("idle"); // idle | asking | granted | denied
  const [showLocModal, setShowLocModal] = useState(false);

  const handleAuth = (u) => {
    setUser(u);
    if(u.type==="customer") {
      // Prompt for location after login
      setShowLocModal(true);
    } else {
      setPage("store-home");
    }
  };

  const handleLogout = () => { setUser(null); setPage("home"); setUserLoc(null); setLocStatus("idle"); setShowLocModal(false); };
  const handleEditProduct = (p) => { setEditProduct(p); setPage("store-add"); };

  // Request geolocation — keep modal open in loading state while waiting
  const requestLocation = () => {
    setLocStatus("asking");
    // Don't close modal yet — show spinner inside it
    if(!navigator.geolocation) {
      setLocStatus("denied");
      setShowLocModal(false);
      setPage("home");
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude: lat, longitude: lng } = pos.coords;
        const label = `${lat.toFixed(3)}°N, ${lng.toFixed(3)}°E`;
        setUserLoc({ lat, lng, label });
        setLocStatus("granted");
        setShowLocModal(false);
        setPage("home");
      },
      () => {
        setLocStatus("denied");
        setShowLocModal(false);
        setPage("home");
      },
      { timeout: 10000, maximumAge: 60000, enableHighAccuracy: true }
    );
  };

  const denyLocation = () => {
    setLocStatus("denied");
    setShowLocModal(false);
    setPage("home");
  };

  // Re-request location from within the home page
  const onRequestLocation = () => setShowLocModal(true);

  if(!user) return <><Styles/><CustomCursor/><ScrollBar/><AuthPage onAuth={handleAuth}/></>;

  const isStore = user.type==="store";

  const renderPage = () => {
    if(page==="store-home")      return <StoreDashboard user={user} setPage={setPage}/>;
    if(page==="store-inventory") return <StoreInventory user={user} setPage={setPage} onEditProduct={handleEditProduct}/>;
    if(page==="store-add")       return <AddProductForm user={user} editProduct={editProduct} onSave={()=>{ setEditProduct(null); setPage("store-inventory"); }} onCancel={()=>{ setEditProduct(null); setPage(isStore?"store-inventory":"home"); }}/>;
    if(page==="profile")         return <CustomerProfile user={user} setPage={setPage} onLogout={handleLogout}/>;
    if(page==="explore")         return <ExploreStates setPage={setPage}/>;
    if(page==="discover")        return <DiscoverShops setPage={setPage}/>;
    if(page==="chat")            return <ChatPage user={user}/>;
    if(page==="home")            return isStore
      ? <StoreDashboard user={user} setPage={setPage}/>
      : <CustomerHome user={user} userLoc={userLoc} onRequestLocation={onRequestLocation}/>;
    // fallback
    return <CustomerHome user={user} userLoc={userLoc} onRequestLocation={onRequestLocation}/>;
  };

  return (
    <>
      <Styles/>
      <CustomCursor/>
      <ScrollBar/>

      {/* Location permission modal */}
      {showLocModal && (
        <LocationModal
          onAllow={requestLocation}
          onDeny={denyLocation}
          loading={locStatus==="asking"}
        />
      )}

      <div style={{ minHeight:"100vh",display:"flex",flexDirection:"column",filter:showLocModal?"blur(3px)":"none",transition:"filter .3s",pointerEvents:showLocModal?"none":"all" }}>
        <TopBar user={user} page={page} setPage={setPage} onLogout={handleLogout} userLoc={userLoc} locStatus={locStatus} onRequestLocation={onRequestLocation}/>
        <main style={{ flex:1 }} key={page}>
          <div className="fade-in">{renderPage()}</div>
        </main>
        <footer style={{ background:"#1A1A3E",color:"#B0B0D8",padding:"36px 24px 24px",marginTop:48,textAlign:"center" }}>
          <div style={{ display:"flex",justifyContent:"center",marginBottom:6 }}>
            <L4ULogo size={72} dark/>
          </div>
          <p style={{ fontSize:12,color:"#6060A0",marginTop:4 }}>South India's Local Fashion Platform · 5 States · {STORES_GEO.length} Shops · {DB.inventory.filter(p=>p.active).length} Live Products</p>
        </footer>
      </div>
    </>
  );
}
